import React, { createContext, useState, useContext, ReactNode, useMemo } from 'react';
import { useAuth } from '~/state/auth';
import { useOrganization } from '~/providers/GraphqlProvider';
import { buildQualifyChecks } from '~/utils/qualifyChecks';
import {
  PredictPatient,
  PredictContextType,
  WorkflowStage,
  LocationType,
  PredictProgram,
  ChangeType,
  DisqualificationReason,
  DecisionFactor,
  WorkflowTransition,
  EligibilityChecklist,
  EligibilityChecklistItem,
  PROGRAM_ELIGIBILITY_CHECKLISTS,
  PredictUser,
  UserOrganization,
  ClientOrganization,
  UserRole,
  StageReadinessDetails,
  ReadinessRequirement,
  DemoMode,
  VISIBLE_PROGRAMS,
  HAH_PLUS_PROGRAMS,
  CapacityStatus,
  CapacityCalendar,
} from '~/types/predict';

// Create and export the context
export const PredictContext = createContext<PredictContextType | undefined>(undefined);

// Helper function to generate user initials from email
const getInitials = (email: string): string => {
  const parts = email.split('@')[0].split('.');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return email.substring(0, 2).toUpperCase();
};

// Helper function to calculate checklist completion percentage
const calculateCompletionPercentage = (checklist: EligibilityChecklist): number => {
  const totalItems = checklist.sections.reduce(
    (sum, section) => sum + section.items.length,
    0
  );

  if (totalItems === 0) return 0;

  const answeredItems = checklist.sections.reduce(
    (sum, section) =>
      sum + section.items.filter(item => item.answer !== undefined && item.answer !== null).length,
    0
  );

  return Math.round((answeredItems / totalItems) * 100);
};

// ==================== TWO-LAYER ORGANIZATION SETUP ====================

// Layer 1: User Organization (who the user works for)
const CAREDNA_ORG: UserOrganization = {
  id: 'org-caredna',
  name: 'CareDNA Discharge Services',
  type: 'DISCHARGE_MANAGEMENT'
};

// Layer 2: Client Organizations (hospitals that are clients)
const CLIENT_METRO_GENERAL: ClientOrganization = {
  id: 'client-metro',
  name: 'Metro General Hospital',
  type: 'HOSPITAL'
};

const CLIENT_CITY_MEDICAL: ClientOrganization = {
  id: 'client-city',
  name: 'City Medical Center',
  type: 'HOSPITAL'
};

// Demo user profiles with different client access levels
export const DEMO_USERS: PredictUser[] = [
  {
    id: 'user-admin',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@caredna.com',
    role: UserRole.ADMINISTRATOR,
    userOrganization: CAREDNA_ORG,
    clientOrganizations: [CLIENT_METRO_GENERAL, CLIENT_CITY_MEDICAL] // Access to both hospitals
  },
  {
    id: 'user-metro-coordinator',
    name: 'Michael Chen',
    email: 'michael.chen@caredna.com',
    role: UserRole.CLINICAL_LIAISON,
    userOrganization: CAREDNA_ORG,
    clientOrganizations: [CLIENT_METRO_GENERAL] // Access to Metro General only
  },
  {
    id: 'user-city-coordinator',
    name: 'Jennifer Martinez',
    email: 'jennifer.martinez@caredna.com',
    role: UserRole.CLINICAL_LIAISON,
    userOrganization: CAREDNA_ORG,
    clientOrganizations: [CLIENT_CITY_MEDICAL] // Access to City Medical only
  },
  {
    id: 'user-regional-manager',
    name: 'David Thompson',
    email: 'david.thompson@caredna.com',
    role: UserRole.RN_CASE_MANAGER,
    userOrganization: CAREDNA_ORG,
    clientOrganizations: [CLIENT_METRO_GENERAL, CLIENT_CITY_MEDICAL] // Access to both hospitals
  }
];

// ==================== END ORGANIZATION SETUP ====================

// Mock provider list (from EHR system)
export const MOCK_PROVIDERS = [
  'Dr. Sarah Chen',
  'Dr. Michael Rodriguez',
  'Dr. James Wilson',
  'Dr. Lisa Anderson',
  'Dr. Robert Kim',
  'Dr. Emily White',
  'Dr. David Martinez',
  'Dr. Jennifer Lopez',
  'Dr. Christopher Moore',
  'Dr. Anna Garcia',
  'Dr. Mark Johnson',
  'Dr. Patricia Lee',
  'Dr. Steven Clark',
  'Dr. Michelle Adams',
  'Dr. Kevin Martinez',
  'Dr. Thomas Brown',
  'Dr. Elizabeth Taylor',
  'Dr. Daniel Harris',
  'Dr. Maria Santos',
  'Dr. Richard Thompson',
  'Dr. Jessica Miller',
  'Dr. Andrew Davis',
  'Dr. Nancy Wilson',
  'Dr. Paul Anderson',
  'Dr. Rebecca Martinez',
].sort();

// Mock data
const MOCK_PATIENTS: PredictPatient[] = [
  {
    id: '1',
    firstName: 'Linda',
    lastName: 'Martinez',
    mrn: 'MRN001234',
    dateOfBirth: '1958-03-15T00:00:00.000Z',
    age: 66,
    gender: 'Female',
    address: '1234 Pike St, Seattle, WA 98101',
    facility: 'Metro General Hospital',
    department: 'Cardiology',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Sarah Chen',
    primaryDiagnosis: 'Congestive Heart Failure',
    insuranceType: 'Medicare',
    expectedLOS: 5,
    currentLOS: 3,
    programScores: {
      [PredictProgram.HOME_HEALTH]: 45,
      [PredictProgram.SNF_AT_HOME]: 38,
      [PredictProgram.HOSPITAL_AT_HOME]: 32
    },
    programDecisionFactors: {
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df1_hh_initial',
          label: 'Oxygen Requirements',
          importance: 85,
          value: 'Requiring supplemental O2 at 4L',
          dataSource: 'Respiratory Therapy',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'High oxygen requirements indicate respiratory instability limiting home health',
          contribution: 'negative'
        },
        {
          id: 'df2_hh_initial',
          label: 'Caregiver Support',
          importance: 78,
          value: 'No documented caregiver',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Lack of caregiver support creates safety concerns for home-based care',
          contribution: 'negative'
        },
        {
          id: 'df3_hh_initial',
          label: 'Medicare Eligibility',
          importance: 65,
          value: 'Age 65+, Medicare eligible',
          dataSource: 'Epic Demographics',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          explanation: 'Medicare covers home health services, making patient eligible for program',
          contribution: 'positive'
        },
        {
          id: 'df4_hh_initial',
          label: 'Clinical Complexity',
          importance: 72,
          value: 'Declining cardiac function with recurrent admissions',
          dataSource: 'Cardiology Notes',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          explanation: 'Complex cardiac history and instability reduce home health appropriateness',
          contribution: 'negative'
        }
      ],
      [PredictProgram.SNF_AT_HOME]: [
        {
          id: 'df1_snf_initial',
          label: 'Clinical Instability',
          importance: 92,
          value: 'High O2 requirements (4L) and declining cardiac function',
          dataSource: 'Nursing Assessment',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Medical instability precludes safe delivery of intensive home-based rehab services',
          contribution: 'negative'
        },
        {
          id: 'df2_snf_initial',
          label: 'Caregiver Availability',
          importance: 88,
          value: 'No caregiver available - lives alone',
          dataSource: 'Social Work Assessment',
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home requires 24/7 caregiver presence for intensive services - critical barrier',
          contribution: 'negative'
        },
        {
          id: 'df3_snf_initial',
          label: 'Rehabilitation Need Assessment',
          importance: 75,
          value: 'PT/OT evaluation pending - unclear rehab intensity needs',
          dataSource: 'Therapy Consult',
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Cannot confirm need for intensive rehab services without completed therapy evaluation',
          contribution: 'negative'
        }
      ],
      [PredictProgram.HOSPITAL_AT_HOME]: [
        {
          id: 'df1_hah_initial',
          label: 'Critical Clinical Instability',
          importance: 92,
          value: 'Active CHF exacerbation with O2 dependency and declining EF',
          dataSource: 'Cardiology Assessment',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Acute decompensation requires hospital-level monitoring beyond Hospital at Home capability',
          contribution: 'negative'
        },
        {
          id: 'df2_hah_initial',
          label: 'Caregiver Support',
          importance: 88,
          value: 'No caregiver available - lives alone',
          dataSource: 'Social Work Assessment',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Hospital at Home requires continuous caregiver presence for safety - critical barrier',
          contribution: 'negative'
        },
        {
          id: 'df3_hah_initial',
          label: 'Medical Complexity',
          importance: 82,
          value: 'Progressive CHF with declining EF (30%) and recurrent admissions',
          dataSource: 'Cardiology Notes',
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          explanation: 'Disease trajectory and complexity exceed Hospital at Home management capabilities',
          contribution: 'negative'
        }
      ]
    },
    recommendedProgram: PredictProgram.HOME_HEALTH,
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '1',
      userId: 'user1',
      userName: 'Sarah Miller',
      userInitials: 'SM',
      assignedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh1',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified for discharge planning - awaiting clinical updates'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mrs. Martinez is a 66-year-old female with a complex cardiac history presenting with acute exacerbation of congestive heart failure (CHF). Over the past 90 days, she has experienced two hospital admissions and one ER visit[[1]], all related to CHF management and fluid overload.

Her first admission (65 days ago) was a 7-day stay for diuresis and medication adjustment. She was discharged on an optimized cardiac regimen including Lasix 40mg BID, Carvedilol 12.5mg BID, and Lisinopril 20mg daily[[2]].

Following discharge, she was readmitted 28 days ago after presenting to the ER with worsening dyspnea and peripheral edema. This admission lasted 5 days with aggressive diuresis and addition of Spironolactone 25mg daily. Home health services were initiated for daily weights and vitals monitoring.

Current admission (day 3) was prompted by 15-pound weight gain over 4 days despite home health monitoring. Echo shows EF 30% (down from 35% two months ago)[[3]], indicating disease progression. BNP elevated at 1,850 (baseline 800-900)[[4]].

Recent PT evaluation shows deconditioning but good rehab potential. Patient lives alone but has daughter nearby who visits 3x weekly. Insurance is Medicare with supplemental coverage. Patient is motivated but concerned about managing complex medication regimen and dietary restrictions at home.`,
      references: [
        { id: 1, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '2 inpatient admissions, 1 ED visit for CHF' },
        { id: 2, source: 'Discharge Medication Reconciliation', sourceType: 'medication', detail: 'Dr. R. Okafor, Cardiology · 65 days ago', snippet: 'Lasix 40mg BID, Carvedilol 12.5mg BID, Lisinopril 20mg' },
        { id: 3, source: 'Echocardiogram Report', sourceType: 'imaging', detail: 'Cardiology · on admission', snippet: 'LVEF 30%, down from 35%; progressive systolic dysfunction' },
        { id: 4, source: 'BNP / Cardiac Labs', sourceType: 'lab', detail: 'Lab · day 3', snippet: 'BNP 1,850 pg/mL (baseline 800-900)' },
      ],
      recentAdmissions: 3,
      erVisits: 1,
      totalInpatientDays: 15,
      currentLOS: 3,
      trend: 'declining',
      trendDescription: 'Recurrent CHF exacerbations despite optimal medical management. Worsening ejection fraction from 35% to 30% over 2 months. Three admissions in 90 days with progressively shorter intervals.',
      keyFindings: [
        'Declining EF from 35% to 30% over 2 months indicates progressive heart failure',
        'Recurrent admissions despite home health intervention suggest need for higher level of care',
        'Good rehab potential per PT but significant deconditioning present',
        'Lives alone with limited caregiver support - daughter available 3x weekly',
        'Medication non-adherence suspected as contributing factor to readmissions'
      ],
      events: [
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for CHF exacerbation with 15-lb weight gain, dyspnea at rest',
          location: 'Metro General Hospital - Cardiology'
        },
        {
          date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'pt_eval',
          description: 'Physical therapy evaluation: Deconditioning noted, good rehab potential',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'discharge',
          description: 'Discharged home with home health services for daily vitals/weights',
          destination: 'Home + Home Health',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 33 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted via ER for worsening dyspnea and peripheral edema',
          location: 'Metro General Hospital - ER/Cardiology'
        },
        {
          date: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'Spironolactone 25mg daily added to cardiac regimen',
          location: 'Cardiology Clinic'
        },
        {
          date: new Date(Date.now() - 65 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'discharge',
          description: 'Discharged after 7-day stay with optimized diuretic regimen',
          destination: 'Home',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 72 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for CHF exacerbation, required IV diuresis',
          location: 'Metro General Hospital - Cardiology'
        }
      ],
      lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() // 2 hours ago
    }
  },
  {
    id: '2',
    firstName: 'Angela',
    lastName: 'Martinez',
    mrn: 'MRN001235',
    dateOfBirth: '1968-03-15T00:00:00.000Z',
    age: 56,
    gender: 'Female',
    address: '5678 Pine St, Seattle, WA 98101',
    facility: 'Metro General Hospital',
    department: 'Emergency Department',
    locationType: LocationType.ED,
    attendingPhysician: 'Dr. Michael Rodriguez',
    primaryDiagnosis: 'Cellulitis requiring IV antibiotics',
    insuranceType: 'Blue Cross Blue Shield',
    expectedLOS: 3,
    currentLOS: 0.5,
    programScores: {
      [PredictProgram.HOME_HEALTH]: 85,
      [PredictProgram.INFUSION_AT_HOME]: 76,
      [PredictProgram.SNF_AT_HOME]: 68
    },
    programDecisionFactors: {
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df_hh_1',
          label: 'IV Therapy Requirements',
          importance: 90,
          value: 'PIV line for IV antibiotics',
          dataSource: 'Pharmacy Orders',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health can manage PIV line and IV antibiotic administration',
          contribution: 'positive'
        },
        {
          id: 'df_hh_2',
          label: 'Wound Care',
          importance: 88,
          value: 'Requires daily wound assessment',
          dataSource: 'Nursing Assessment',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health nursing can monitor cellulitis healing and wound care',
          contribution: 'positive'
        },
        {
          id: 'df_hh_3',
          label: 'Caregiver Support',
          importance: 82,
          value: 'Daughter available for assistance',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Family support available to assist with care at home',
          contribution: 'positive'
        }
      ],
      [PredictProgram.INFUSION_AT_HOME]: [
        {
          id: 'df_inf_1',
          label: 'IV Antibiotic Administration',
          importance: 88,
          value: 'Daily IV antibiotics via PIV line',
          dataSource: 'Pharmacy Orders',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Infusion at Home specializes in IV medication delivery and line management',
          contribution: 'positive'
        },
        {
          id: 'df_inf_2',
          label: 'Simple Protocol Appropriate',
          importance: 75,
          value: 'Straightforward once-daily antibiotic regimen',
          dataSource: 'Pharmacy Review',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Uncomplicated IV protocol well-suited for infusion services',
          contribution: 'positive'
        },
        {
          id: 'df_inf_3',
          label: 'Comprehensive Wound Care',
          importance: 70,
          value: 'Cellulitis requires daily wound assessment and monitoring',
          dataSource: 'Nursing Assessment',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health provides more comprehensive wound management alongside IV therapy',
          contribution: 'negative'
        }
      ],
      [PredictProgram.SNF_AT_HOME]: [
        {
          id: 'df_snf_1',
          label: 'Skilled Nursing Capability',
          importance: 78,
          value: 'SNF at Home provides intensive daily nursing care',
          dataSource: 'Clinical Assessment',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home level of care can manage IV therapy and wound care effectively',
          contribution: 'positive'
        },
        {
          id: 'df_snf_2',
          label: 'Rehabilitation Needs',
          importance: 75,
          value: 'No significant rehabilitation requirements',
          dataSource: 'PT Evaluation',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Patient independently mobile, does not need intensive SNF-level therapy services',
          contribution: 'negative'
        },
        {
          id: 'df_snf_3',
          label: 'Care Appropriateness',
          importance: 72,
          value: 'Uncomplicated cellulitis case',
          dataSource: 'Case Management',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Straightforward IV and wound care can be managed by standard home health',
          contribution: 'negative'
        }
      ]
    },
    recommendedProgram: PredictProgram.HOME_HEALTH,
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '2',
      userId: 'user2',
      userName: 'James Thompson',
      userInitials: 'JT',
      assignedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh2',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago (matches currentLOS: 0.5)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified as Home Health candidate - cellulitis requiring IV antibiotics and wound care'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Ms. Martinez is a 56-year-old female presenting with acute cellulitis of the right lower extremity requiring IV antibiotic therapy. Over the past 90 days, she has had one previous ED visit for similar concerns[[1]].

Her clinical course has been characterized by recurrent soft tissue infections, likely related to venous insufficiency and minor skin breakdown. The current episode represents a more severe presentation requiring IV antibiotics via PICC line for home infusion therapy.

Recent imaging confirmed no abscess formation or deep tissue involvement[[2]]. Vascular studies showed chronic venous insufficiency but no acute DVT[[3]]. Her overall medical complexity is moderate, with well-controlled diabetes and hypertension as comorbidities[[4]].`,
      references: [
        { id: 1, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '1 prior ED visit for lower-extremity cellulitis' },
        { id: 2, source: 'Soft Tissue Ultrasound Report', sourceType: 'imaging', detail: 'Radiology · on admission', snippet: 'No drainable abscess or deep tissue involvement' },
        { id: 3, source: 'Vascular Duplex Study', sourceType: 'imaging', detail: 'Vascular lab · on admission', snippet: 'Chronic venous insufficiency, no acute DVT' },
        { id: 4, source: 'Problem List / Chart Review', sourceType: 'ehr', detail: 'Chart review · current', snippet: 'DM2 and HTN, both well-controlled' },
      ],
      recentAdmissions: 1,
      erVisits: 1,
      totalInpatientDays: 0.5,
      currentLOS: 0.5,
      trend: 'stable',
      trendDescription: 'Recurrent cellulitis episodes related to chronic venous insufficiency. Current episode responding well to IV antibiotics. Overall stable with good support at home.',
      keyFindings: [
        'Recurrent cellulitis related to chronic venous insufficiency',
        'PICC line placed for 14-day home IV antibiotic course',
        'Daughter trained and competent in PICC line care',
        'Diabetes and hypertension well-controlled',
        'Lives with daughter who provides excellent caregiver support'
      ],
      events: [
        {
          date: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted from ED with cellulitis requiring IV antibiotics',
          location: 'Metro General Hospital - Emergency Department'
        },
        {
          date: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          type: 'procedure',
          description: 'PICC line placement for home IV therapy',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Daughter trained on PICC line care and medication administration',
          location: 'Metro General Hospital - Nursing Education'
        },
        {
          date: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'ED visit for mild cellulitis, treated with oral antibiotics',
          location: 'Metro General Hospital - ED'
        }
      ],
      lastUpdated: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString() // 1 hour ago
    }
  },
  {
    id: '3',
    firstName: 'Maria',
    lastName: 'Garcia',
    mrn: 'MRN001236',
    dateOfBirth: '1945-11-30T00:00:00.000Z',
    age: 79,
    gender: 'Female',
    address: '789 Madison St, Seattle, WA 98104',
    facility: 'City Medical Center',
    department: 'Oncology',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. James Wilson',
    primaryDiagnosis: 'Palliative Care',
    insuranceType: 'Medicare',
    expectedLOS: 5,
    currentLOS: 3,
    programScores: {
      [PredictProgram.PALLIATIVE]: 85,
      [PredictProgram.HOSPICE]: 68,
      [PredictProgram.HOME_HEALTH]: 55
    },
    programDecisionFactors: {
      [PredictProgram.PALLIATIVE]: [
        {
          id: 'df_pal_1',
          label: 'Goals of Care Discussion',
          importance: 95,
          value: 'Comfort focused care',
          dataSource: 'Palliative Care Notes',
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          explanation: 'Family agrees with home-based palliative care plan',
          contribution: 'positive'
        },
        {
          id: 'df_pal_2',
          label: 'Symptom Management',
          importance: 88,
          value: 'Pain well controlled on current medication plan',
          dataSource: 'Epic Flowsheet',
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Symptoms manageable in home setting with palliative care support',
          contribution: 'positive'
        },
        {
          id: 'df_pal_3',
          label: 'Caregiver Support',
          importance: 82,
          value: 'Daughter lives in home as primary caregiver',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          explanation: 'Strong caregiver presence enables home-based palliative care',
          contribution: 'positive'
        }
      ],
      [PredictProgram.HOSPICE]: [
        {
          id: 'df_hos_1',
          label: 'Prognosis',
          importance: 85,
          value: 'Life expectancy weeks to months',
          dataSource: 'Oncology Notes',
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          explanation: 'Disease trajectory suggests hospice may be appropriate soon',
          contribution: 'positive'
        },
        {
          id: 'df_hos_2',
          label: 'Patient/Family Preference',
          importance: 78,
          value: 'Patient choosing palliative over hospice at this time',
          dataSource: 'Family Meeting Notes',
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          explanation: 'Family not ready for hospice transition - prefers palliative care currently',
          contribution: 'negative'
        }
      ],
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df_hh_pal_1',
          label: 'Care Complexity',
          importance: 85,
          value: 'Advanced cancer requiring specialized symptom management',
          dataSource: 'Oncology Notes',
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          explanation: 'Standard home health insufficient for complex palliative needs',
          contribution: 'negative'
        },
        {
          id: 'df_hh_pal_2',
          label: 'Pain Management Complexity',
          importance: 82,
          value: 'Complex pain regimen requiring frequent adjustments',
          dataSource: 'Palliative Care Notes',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          explanation: 'Specialized palliative care services better suited than basic home health',
          contribution: 'negative'
        }
      ]
    },
    recommendedProgram: PredictProgram.PALLIATIVE,
    selectedProgram: PredictProgram.PALLIATIVE,
    currentStage: WorkflowStage.ACCEPTANCE,
    assignment: {
      patientId: '3',
      userId: 'user1',
      userName: 'Sarah Miller',
      userInitials: 'SM',
      assignedAt: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh4',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified for palliative home care'
      },
      {
        id: 'wh5',
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.ELIGIBILITY,
        timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
        userId: 'user1',
        userName: 'Sarah Miller',
        note: 'Insurance and eligibility confirmed'
      },
      {
        id: 'wh6',
        fromStage: WorkflowStage.ELIGIBILITY,
        toStage: WorkflowStage.ACCEPTANCE,
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        userId: 'user1',
        userName: 'Sarah Miller',
        note: 'Patient and family education completed. Palliative agency contacted.'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mrs. Garcia is a 79-year-old female with stage IV metastatic ovarian cancer[[1]] who has transitioned to comfort-focused care. Over the past 90 days, her clinical trajectory has shown progressive decline with increasing symptom burden.

She was admitted to oncology 85 days ago following her decision to discontinue chemotherapy after discussing goals of care with her oncologist and family. That admission lasted 4 days for pain management optimization and initiation of palliative care services.

Following discharge, she remained at home with weekly palliative care nurse visits. However, she returned to the ER 52 days ago with severe abdominal pain and was admitted for 3 days[[2]]. Pain management was escalated from oral opioids to a fentanyl patch with breakthrough oral morphine[[3]]. A palliative care consultation was completed during this stay.

Most recently, she was admitted 5 days ago from her oncologist's office due to worsening fatigue, decreased oral intake, and confusion. Hospice appropriateness was discussed with the family, but the patient and family elected to continue with palliative home care at this time.

Current functional status shows significant decline - patient is mostly bed/chair bound with assistance needed for ADLs. Pain is well-controlled on current regimen (Fentanyl 50mcg patch + morphine 15mg PO q4h PRN). Appetite remains poor despite anti-nausea medications.

Family support is strong - daughter is primary caregiver and lives in the home. Patient has advance directives in place with DNR/DNI status confirmed[[4]]. The family understands prognosis (estimated weeks to months) and is prepared for home-based care with 24/7 availability of palliative care team.`,
      references: [
        { id: 1, source: 'Oncology Consult Note', sourceType: 'note', detail: 'Dr. S. Patel, Oncology · 85 days ago', snippet: 'Stage IV metastatic ovarian cancer, goals-of-care transition' },
        { id: 2, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: 'ED visit 52 days ago, 3-day admission for pain' },
        { id: 3, source: 'Palliative Care Medication Orders', sourceType: 'medication', detail: 'Palliative care · 52 days ago', snippet: 'Fentanyl patch with breakthrough oral morphine' },
        { id: 4, source: 'Advance Directive / Goals of Care Note', sourceType: 'note', detail: 'Case management · current', snippet: 'DNR/DNI confirmed with patient and family' },
      ],
      recentAdmissions: 3,
      erVisits: 1,
      totalInpatientDays: 12,
      currentLOS: 5,
      trend: 'declining',
      trendDescription: 'Progressive decline with advanced stage IV metastatic ovarian cancer. Functional status deteriorated from ambulatory to bed/chair bound over 90 days. Three admissions in past 90 days with escalating symptom burden.',
      keyFindings: [
        'Progressive functional decline - now primarily bed/chair bound',
        'Pain well-controlled on optimized regimen (fentanyl patch + breakthrough morphine)',
        'Strong family support system with daughter as primary caregiver in home',
        'Patient/family choosing palliative care over hospice at this time despite appropriate for both',
        'DNR/DNI status confirmed, advance directives in place',
        'Estimated prognosis: weeks to months per oncology and palliative care teams'
      ],
      events: [
        {
          date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for worsening fatigue, decreased PO intake, and confusion',
          location: 'City Medical Center - Oncology'
        },
        {
          date: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Hospice discussion with family - family elects to continue palliative care',
          location: 'City Medical Center'
        },
        {
          date: new Date(Date.now() - 52 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'discharge',
          description: 'Discharged home with palliative care, pain regimen optimized',
          destination: 'Home + Palliative',
          location: 'City Medical Center'
        },
        {
          date: new Date(Date.now() - 55 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'ER visit for severe abdominal pain, admitted for pain management',
          location: 'City Medical Center - ER'
        },
        {
          date: new Date(Date.now() - 57 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'Pain regimen escalated: Fentanyl patch added, morphine for breakthrough',
          location: 'City Medical Center - Palliative Care'
        },
        {
          date: new Date(Date.now() - 85 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'discharge',
          description: 'Discharged after goals of care discussion and pain management initiation',
          destination: 'Home',
          location: 'City Medical Center'
        },
        {
          date: new Date(Date.now() - 89 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted after deciding to discontinue chemotherapy, focus on comfort',
          location: 'City Medical Center - Oncology'
        }
      ],
      lastUpdated: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString() // 3 hours ago
    }
  },
  {
    id: '4',
    firstName: 'William',
    lastName: 'Johnson',
    mrn: 'MRN001237',
    dateOfBirth: '1968-05-10T00:00:00.000Z',
    age: 56,
    gender: 'Male',
    address: '321 Union St, Seattle, WA 98101',
    facility: 'Metro General Hospital',
    department: 'Orthopedics',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Lisa Anderson',
    primaryDiagnosis: 'Hip Replacement',
    insuranceType: 'United Healthcare',
    expectedLOS: 6,
    currentLOS: 4,
    programScores: {
      [PredictProgram.HOME_HEALTH]: 42,
      [PredictProgram.SNF_AT_HOME]: 38
    },
    programDecisionFactors: {
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df_hh_wj_1',
          label: 'Caregiver Availability',
          importance: 92,
          value: 'No caregiver during daytime work hours',
          dataSource: 'Social Work Assessment',
          timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          explanation: 'Unsafe for home health without daytime supervision post-surgery',
          contribution: 'negative'
        },
        {
          id: 'df_hh_wj_2',
          label: 'Fall Risk',
          importance: 88,
          value: 'High fall risk post-hip replacement without supervision',
          dataSource: 'PT Evaluation',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          explanation: 'Mobility limitations and fall risk require constant supervision unavailable at home',
          contribution: 'negative'
        }
      ],
      [PredictProgram.SNF_AT_HOME]: [
        {
          id: 'df_snf_wj_1',
          label: 'Caregiver Support',
          importance: 85,
          value: 'No caregiver for SNF-level care supervision',
          dataSource: 'Social Work Assessment',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home requires 24/7 caregiver presence which is not available',
          contribution: 'negative'
        }
      ]
    },
    recommendedProgram: PredictProgram.HOME_HEALTH,
    selectedProgram: PredictProgram.HOME_HEALTH,
    currentStage: WorkflowStage.DISQUALIFIED,
    assignment: {
      patientId: '4',
      userId: 'user-regional-manager',
      userName: 'David Thompson',
      userInitials: 'DT',
      assignedAt: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
    disqualificationReason: DisqualificationReason.NO_CAREGIVER,
    disqualificationNote: 'Patient lives alone, wife works full-time. No caregiver available during daytime hours.',
    workflowHistory: [
      {
        id: 'wh7',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), // 4 days ago (matches currentLOS: 4)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified for home health'
      },
      {
        id: 'wh8',
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.DISQUALIFIED,
        timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
        userId: 'user2',
        userName: 'James Thompson',
        note: 'No caregiver available. Patient needs facility-based care.'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mr. Johnson is a 56-year-old male who underwent elective total hip replacement for severe osteoarthritis. Over the past 90 days, his medical history has been relatively stable with the exception of progressively worsening hip pain limiting his mobility and quality of life.

He was admitted 4 days ago for elective total hip arthroplasty. His surgical procedure was uncomplicated[[1]], and his post-operative course has been progressing as expected. He is currently working with physical therapy on mobility, transfers, and early ambulation. Pain is well-controlled with multimodal analgesia including oxycodone and acetaminophen[[2]].

The primary challenge for discharge planning is the lack of caregiver support at home. He lives with his wife who works full-time, leaving him without supervision during daytime hours (8am-5pm)[[4]]. This presents a significant safety concern given his mobility limitations, walker dependence, and fall risk during the early recovery period[[3]]. Despite good surgical outcome, he cannot be safely discharged home without adequate caregiver coverage and has been disqualified from home-based programs.`,
      references: [
        { id: 1, source: 'Operative Report', sourceType: 'note', detail: 'Dr. K. Bauer, Orthopedics · 4 days ago', snippet: 'Uncomplicated total hip arthroplasty' },
        { id: 2, source: 'Medication Administration Record', sourceType: 'medication', detail: 'Nursing · current', snippet: 'Multimodal analgesia: oxycodone and acetaminophen' },
        { id: 3, source: 'Physical Therapy Evaluation', sourceType: 'assessment', detail: 'PT · 2 days ago', snippet: 'Walker-dependent, elevated fall risk in early recovery' },
        { id: 4, source: 'Case Management / Social Work Note', sourceType: 'note', detail: 'Case management · current', snippet: 'No daytime caregiver, unsupervised 8am-5pm' },
      ],
      recentAdmissions: 1,
      erVisits: 0,
      totalInpatientDays: 4,
      currentLOS: 4,
      trend: 'stable',
      trendDescription: 'Stable post-operative recovery with appropriate healing. No complications from surgery. Limited by social factors (lack of caregiver) rather than medical factors.',
      keyFindings: [
        'Uncomplicated total hip replacement surgery with good surgical outcome',
        'Post-operative pain well-controlled on oral medications',
        'Progressing appropriately with physical therapy, using walker for mobility',
        'No caregiver available during daytime hours (wife works full-time)',
        'Safety concern due to fall risk and mobility limitations without supervision',
        'Disqualified from home discharge due to inadequate caregiver support'
      ],
      events: [
        {
          date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for elective total hip arthroplasty',
          location: 'Metro General Hospital - Orthopedics'
        },
        {
          date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'procedure',
          description: 'Total hip replacement surgery completed successfully',
          location: 'Metro General Hospital - Operating Room'
        },
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Physical therapy evaluation - using walker, requires assistance with transfers',
          location: 'Metro General Hospital - Orthopedics'
        },
        {
          date: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Social work assessment identified lack of daytime caregiver - disqualified from home programs',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Pre-operative visit - surgical clearance obtained',
          location: 'Orthopedic Surgery Clinic'
        }
      ],
      lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString() // 4 hours ago
    }
  },
  {
    id: '5',
    firstName: 'Jennifer',
    lastName: 'Lee',
    mrn: 'MRN001238',
    dateOfBirth: '1985-09-18T00:00:00.000Z',
    age: 39,
    gender: 'Female',
    address: '456 Bellevue Way NE, Bellevue, WA 98004',
    facility: 'City Medical Center',
    department: 'Emergency Department',
    locationType: LocationType.ED,
    attendingPhysician: 'Dr. Robert Kim',
    primaryDiagnosis: 'Cellulitis',
    insuranceType: 'Aetna',
    expectedLOS: 5,
    currentLOS: 3,
    programScores: {
      [PredictProgram.INFUSION_AT_HOME]: 85,
      [PredictProgram.HOME_HEALTH]: 72
    },
    programDecisionFactors: {
      [PredictProgram.INFUSION_AT_HOME]: [
        {
          id: 'df_inf_1',
          label: 'IV Antibiotic Therapy',
          importance: 90,
          value: 'Daily IV antibiotics needed',
          dataSource: 'Pharmacy Orders',
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Infusion at home can administer IV therapy',
          contribution: 'positive'
        },
        {
          id: 'df_inf_2',
          label: 'Clinical Response',
          importance: 85,
          value: 'Improving on treatment',
          dataSource: 'Physician Notes',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Patient responding well to antibiotics',
          contribution: 'positive'
        },
        {
          id: 'df_inf_3',
          label: 'Clinical Simplicity',
          importance: 78,
          value: 'Uncomplicated cellulitis case',
          dataSource: 'ED Assessment',
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Straightforward case well-suited for infusion services',
          contribution: 'positive'
        }
      ],
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df_hh_jl_1',
          label: 'Skilled Nursing Capability',
          importance: 82,
          value: 'Wound care and IV management',
          dataSource: 'Nursing Assessment',
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health can manage wound care and IV therapy',
          contribution: 'positive'
        },
        {
          id: 'df_hh_jl_2',
          label: 'Caregiver Support',
          importance: 75,
          value: 'Lives independently, capable of self-care',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Independent patient can manage home health visits',
          contribution: 'positive'
        },
        {
          id: 'df_hh_jl_3',
          label: 'Service Specialization',
          importance: 70,
          value: 'Infusion services specialize in IV therapy',
          dataSource: 'Care Coordination',
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Dedicated infusion services may provide more specialized IV care',
          contribution: 'negative'
        }
      ]
    },
    recommendedProgram: PredictProgram.INFUSION_AT_HOME,
    selectedProgram: PredictProgram.INFUSION_AT_HOME,
    currentStage: WorkflowStage.TRANSITION,
    assignment: {
      patientId: '5',
      userId: 'user2',
      userName: 'James Thompson',
      userInitials: 'JT',
      assignedAt: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString()
    },
    changeType: ChangeType.CLINICAL_CHANGE,
    lastUpdated: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh9',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified for home health with IV therapy'
      },
      {
        id: 'wh10',
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.ELIGIBILITY,
        timestamp: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(),
        userId: 'user2',
        userName: 'James Thompson',
        note: 'Insurance covers home health IV therapy'
      },
      {
        id: 'wh11',
        fromStage: WorkflowStage.ELIGIBILITY,
        toStage: WorkflowStage.ACCEPTANCE,
        timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
        userId: 'user2',
        userName: 'James Thompson',
        note: 'Patient educated. Home health agency confirmed availability.'
      },
      {
        id: 'wh12',
        fromStage: WorkflowStage.ACCEPTANCE,
        toStage: WorkflowStage.TRANSITION,
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        userId: 'user2',
        userName: 'James Thompson',
        note: 'Discharge orders signed. First home visit scheduled for tomorrow.'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Ms. Lee is a 39-year-old female presenting with severe cellulitis of the right lower extremity requiring IV antibiotic therapy. Over the past 90 days, she has been generally healthy with no significant hospital encounters or medical issues prior to this acute infection[[1]].

She initially presented to the ED 3 days ago with a painful, red, swollen right leg that had progressively worsened over 48 hours. She was started on IV ceftriaxone after blood cultures were drawn[[2]]. The cellulitis has shown excellent clinical response with decreasing erythema, reduced swelling, and resolution of fever. She has remained afebrile for 24 hours[[3]] and her pain is well-controlled.

A PICC line was placed for home IV antibiotic therapy completion[[4]]. She is being discharged with Infusion at Home services to complete a 7-day course of IV ceftriaxone. She lives independently and is capable of managing her care. Her overall prognosis is excellent with full recovery expected after completing the antibiotic course.`,
      references: [
        { id: 1, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: 'No prior admissions or ED visits' },
        { id: 2, source: 'Blood Culture / Microbiology', sourceType: 'lab', detail: 'Lab · 3 days ago', snippet: 'Cultures drawn prior to IV ceftriaxone start' },
        { id: 3, source: 'Nursing Vitals Flowsheet', sourceType: 'vitals', detail: 'Nursing · current', snippet: 'Afebrile >24 hours, stable vitals' },
        { id: 4, source: 'Procedure Note (PICC placement)', sourceType: 'note', detail: 'IV team · 1 day ago', snippet: 'PICC placed for home IV antibiotic completion' },
      ],
      recentAdmissions: 1,
      erVisits: 1,
      totalInpatientDays: 3,
      currentLOS: 3,
      trend: 'improving',
      trendDescription: 'Rapid clinical improvement on IV antibiotic therapy. Cellulitis resolving with decreasing erythema and swelling. Afebrile x24 hours. Ready for home IV therapy completion.',
      keyFindings: [
        'Excellent response to IV antibiotics - cellulitis improving significantly',
        'Afebrile for 24 hours with stable vital signs',
        'PICC line placed successfully for home IV antibiotic completion',
        'Lives independently, capable of managing home infusion therapy',
        'No underlying chronic conditions or immunosuppression',
        'Expected full recovery with completion of 7-day IV antibiotic course'
      ],
      events: [
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'ED visit for severe right leg cellulitis with fever',
          location: 'City Medical Center - Emergency Department'
        },
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for IV antibiotic therapy for cellulitis',
          location: 'City Medical Center - Emergency Department'
        },
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'Started IV ceftriaxone 2g daily, blood cultures obtained',
          location: 'City Medical Center - ED'
        },
        {
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'procedure',
          description: 'PICC line placement for home IV therapy',
          location: 'City Medical Center - Interventional Radiology'
        },
        {
          date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Afebrile x24 hours, cellulitis significantly improved',
          location: 'City Medical Center - Emergency Department'
        },
        {
          date: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Home infusion company coordinated - first home visit scheduled',
          location: 'City Medical Center'
        }
      ],
      lastUpdated: new Date(Date.now() - 30 * 60 * 1000).toISOString() // 30 minutes ago
    }
  },
  {
    id: '6',
    firstName: 'Thomas',
    lastName: 'Anderson',
    mrn: 'MRN001239',
    dateOfBirth: '1962-12-03T00:00:00.000Z',
    age: 62,
    gender: 'Male',
    address: '890 East Olive Way, Seattle, WA 98102',
    facility: 'Metro General Hospital',
    department: 'Intensive Care Unit',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Emily White',
    primaryDiagnosis: 'COPD Exacerbation',
    insuranceType: 'Medicare',
    expectedLOS: 5,
    currentLOS: 3,
    recommendedProgram: PredictProgram.HOME_HEALTH,
    selectedProgram: PredictProgram.HOME_HEALTH,
    programScores: {
      [PredictProgram.HOME_HEALTH]: 35,
      [PredictProgram.SNF_AT_HOME]: 28
    },
    programDecisionFactors: {
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df_ta_icu_1',
          label: 'Respiratory Status',
          importance: 95,
          value: 'Intubated on mechanical ventilation',
          dataSource: 'ICU Flowsheet',
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          explanation: 'COPD exacerbation progressed to respiratory failure requiring intubation',
          contribution: 'negative'
        },
        {
          id: 'df_ta_icu_2',
          label: 'Acuity Level',
          importance: 92,
          value: 'ICU-level care required',
          dataSource: 'ICU Team',
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          explanation: 'Requires continuous monitoring, unable to protect airway',
          contribution: 'negative'
        },
        {
          id: 'df_ta_icu_3',
          label: 'Hemodynamics',
          importance: 88,
          value: 'Requiring vasopressor support',
          dataSource: 'ICU Nursing',
          timestamp: new Date(Date.now() - 46 * 60 * 60 * 1000).toISOString(),
          explanation: 'Blood pressure support needed, hemodynamically unstable',
          contribution: 'negative'
        },
        {
          id: 'df_ta_icu_4',
          label: 'Prognosis',
          importance: 75,
          value: 'Expected prolonged ICU course',
          dataSource: 'Physician Notes',
          timestamp: new Date(Date.now() - 44 * 60 * 60 * 1000).toISOString(),
          explanation: 'Will likely require extended rehabilitation after ICU discharge',
          contribution: 'negative'
        }
      ],
      [PredictProgram.SNF_AT_HOME]: [
        {
          id: 'df_ta_snf_1',
          label: 'Critical Care Status',
          importance: 95,
          value: 'Currently in ICU with mechanical ventilation',
          dataSource: 'ICU Team',
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          explanation: 'ICU-level care requirements far exceed SNF at Home capabilities',
          contribution: 'negative'
        },
        {
          id: 'df_ta_snf_2',
          label: 'Rehabilitation Feasibility',
          importance: 85,
          value: 'Unable to participate in therapy at this time',
          dataSource: 'PT Consult',
          timestamp: new Date(Date.now() - 46 * 60 * 60 * 1000).toISOString(),
          explanation: 'Patient too critically ill for any home-based rehabilitation program',
          contribution: 'negative'
        }
      ]
    },
    currentStage: WorkflowStage.DISQUALIFIED,
    disqualificationReason: DisqualificationReason.ACUITY_TOO_HIGH,
    disqualificationNote: 'Patient in ICU requiring mechanical ventilation and vasopressor support. ICU-level care required. Not appropriate for home discharge at this time.',
    assignment: {
      patientId: '6',
      userId: 'user1',
      userName: 'Sarah Miller',
      userInitials: 'SM',
      assignedAt: new Date(Date.now() - 50 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh_ta_initial',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified for home health - COPD exacerbation'
      },
      {
        id: 'wh_ta_icu',
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.DISQUALIFIED,
        timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
        userId: 'system',
        userName: 'System',
        note: 'Patient transferred to ICU due to respiratory failure. Intubated and mechanically ventilated. Too acute for home discharge planning at this time.'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mr. Anderson is a 62-year-old male with severe COPD (GOLD Stage 4)[[1]] presenting with acute exacerbation and respiratory failure. Over the past 90 days, he has had multiple hospitalizations related to his underlying lung disease, reflecting a pattern of progressive decline in pulmonary function and poor disease control.

He was admitted 3 days ago from the ED with severe shortness of breath and hypoxemia. Initial treatment with nebulizers, IV steroids, and antibiotics was insufficient. He developed worsening respiratory failure within 12 hours of admission, necessitating emergency intubation and transfer to the ICU. He subsequently became hemodynamically unstable requiring vasopressor support (norepinephrine)[[2]].

His 90-day history shows a concerning pattern: hospitalization 28 days ago for COPD exacerbation (5-day stay), ED visit 45 days ago for dyspnea (treated and released), and another hospitalization 80 days ago for pneumonia with COPD exacerbation (7-day stay requiring home oxygen initiation)[[3]]. This frequent-flyer pattern demonstrates inadequate outpatient management, likely medication non-adherence, and continued smoking cessation challenges. He has limited pulmonary reserve with baseline oxygen requirement of 4L continuous[[4]].

Currently remains intubated on mechanical ventilation with high oxygen requirements. On multiple vasopressor medications for blood pressure support. Far too acute for any home-based discharge planning - requires ongoing ICU-level care with uncertain timeline for extubation. His wife is overwhelmed as his primary caregiver.`,
      references: [
        { id: 1, source: 'Pulmonology Note', sourceType: 'note', detail: 'Dr. H. Okonkwo, Pulmonology · prior visit', snippet: 'Severe COPD, GOLD Stage 4' },
        { id: 2, source: 'ICU Admission Note / Critical Care', sourceType: 'note', detail: 'Critical care · day 1', snippet: 'Intubated, hemodynamically unstable, norepinephrine started' },
        { id: 3, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '2 admissions and 1 ED visit for COPD in 90 days' },
        { id: 4, source: 'Home Oxygen / Respiratory Flowsheet', sourceType: 'vitals', detail: 'Respiratory therapy · baseline', snippet: 'Baseline 4L continuous supplemental oxygen' },
      ],
      recentAdmissions: 3,
      erVisits: 1,
      totalInpatientDays: 15,
      currentLOS: 3,
      trend: 'declining',
      trendDescription: 'Severe decline - most critical exacerbation to date requiring ICU admission, mechanical ventilation, and vasopressor support. Pattern of frequent admissions over 90 days demonstrates progressive disease with poor control.',
      keyFindings: [
        'ICU admission requiring mechanical ventilation and vasopressor support',
        'Most severe COPD exacerbation to date with respiratory failure',
        'Three hospitalizations in past 90 days - frequent-flyer pattern',
        'Baseline severe COPD (GOLD Stage 4) with home oxygen 4L continuous',
        'History of medication non-adherence contributing to frequent exacerbations',
        'Wife overwhelmed as primary caregiver, inadequate social support',
        'Disqualified from home discharge - requires extended ICU-level care'
      ],
      events: [
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'ED visit for severe dyspnea and hypoxemia',
          location: 'Metro General Hospital - Emergency Department'
        },
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for COPD exacerbation, started on IV steroids and antibiotics',
          location: 'Metro General Hospital - Medical Floor'
        },
        {
          date: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Transferred to ICU for worsening respiratory failure',
          location: 'Metro General Hospital - ICU'
        },
        {
          date: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          type: 'procedure',
          description: 'Emergency intubation and mechanical ventilation initiated',
          location: 'Metro General Hospital - ICU'
        },
        {
          date: new Date(Date.now() - 46 * 60 * 60 * 1000).toISOString(),
          type: 'procedure',
          description: 'Central venous line placed for vasopressor administration',
          location: 'Metro General Hospital - ICU'
        },
        {
          date: new Date(Date.now() - 46 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'Norepinephrine drip started for hemodynamic instability',
          location: 'Metro General Hospital - ICU'
        },
        {
          date: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'discharge',
          description: 'Discharged home after 5-day admission for COPD exacerbation',
          destination: 'Home',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 33 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Prior admission for COPD exacerbation treated with steroids and antibiotics',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'ED visit for shortness of breath - treated with nebulizers and released',
          location: 'Metro General Hospital - Emergency Department'
        },
        {
          date: new Date(Date.now() - 73 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'discharge',
          description: 'Discharged home with new home oxygen requirement (4L continuous)',
          destination: 'Home + Home Health',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 80 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for pneumonia and COPD exacerbation - 7-day hospitalization',
          location: 'Metro General Hospital'
        }
      ],
      lastUpdated: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString() // 5 hours ago
    }
  },
  {
    id: '7',
    firstName: 'Patricia',
    lastName: 'Brown',
    mrn: 'MRN001240',
    dateOfBirth: '1950-04-25T00:00:00.000Z',
    age: 74,
    gender: 'Female',
    address: '234 35th Ave NE, Seattle, WA 98105',
    facility: 'City Medical Center',
    department: 'Geriatrics',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. David Martinez',
    primaryDiagnosis: 'Stroke - Mild',
    insuranceType: 'Medicare Advantage',
    expectedLOS: 7,
    currentLOS: 5,
    recommendedProgram: PredictProgram.SNF_AT_HOME,
    selectedProgram: PredictProgram.SNF_AT_HOME,
    programScores: {
      [PredictProgram.SNF_AT_HOME]: 91,
      [PredictProgram.HOSPITAL_AT_HOME]: 84,
      [PredictProgram.HOME_HEALTH]: 78
    },
    programDecisionFactors: {
      [PredictProgram.SNF_AT_HOME]: [
        {
          id: 'df15_snf',
          label: 'Stroke Severity',
          importance: 88,
          value: 'NIHSS score 3 (mild)',
          dataSource: 'Neurology Assessment',
          timestamp: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString(),
          explanation: 'Mild stroke appropriate for home-based rehabilitation',
          contribution: 'positive'
        },
        {
          id: 'df16_snf',
          label: 'Rehabilitation Needs',
          importance: 94,
          value: 'PT/OT/Speech therapy needed',
          dataSource: 'Rehab Evaluation',
          timestamp: new Date(Date.now() - 14 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home can provide intensive multi-disciplinary therapy at home',
          contribution: 'positive'
        },
        {
          id: 'df17_snf',
          label: 'Family Support',
          importance: 85,
          value: 'Daughter available as full-time caregiver',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Strong family support system enables intensive home-based rehab',
          contribution: 'positive'
        },
        {
          id: 'df18_snf',
          label: 'Clinical Response to tPA',
          importance: 82,
          value: 'Excellent response - NIHSS improved from 8 to 3',
          dataSource: 'Neurology Notes',
          timestamp: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString(),
          explanation: 'Good prognosis supports intensive home rehabilitation',
          contribution: 'positive'
        }
      ],
      [PredictProgram.HOSPITAL_AT_HOME]: [
        {
          id: 'df15_hah',
          label: 'Medical Stability',
          importance: 88,
          value: 'Stable post-stroke vitals, new afib controlled',
          dataSource: 'Neurology Assessment',
          timestamp: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString(),
          explanation: 'Medically stable, could be managed at Hospital at Home level',
          contribution: 'positive'
        },
        {
          id: 'df16_hah',
          label: 'Caregiver Support',
          importance: 80,
          value: 'Daughter available as full-time caregiver',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Caregiver presence supports Hospital at Home services',
          contribution: 'positive'
        },
        {
          id: 'df17_hah',
          label: 'Rehabilitation Focus',
          importance: 75,
          value: 'Primary need is intensive therapy, not acute medical care',
          dataSource: 'Rehab Evaluation',
          timestamp: new Date(Date.now() - 14 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home better suited for rehabilitation-focused care than Hospital at Home',
          contribution: 'negative'
        }
      ],
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df15_hh',
          label: 'Stroke Recovery',
          importance: 75,
          value: 'Mild stroke with good prognosis',
          dataSource: 'Neurology Assessment',
          timestamp: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health can support stroke recovery and therapy',
          contribution: 'positive'
        },
        {
          id: 'df16_hh',
          label: 'Caregiver Support',
          importance: 72,
          value: 'Daughter available as caregiver',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Family support enables home health services',
          contribution: 'positive'
        },
        {
          id: 'df17_hh',
          label: 'Therapy Intensity Requirements',
          importance: 82,
          value: 'Needs intensive multi-disciplinary therapy (PT/OT/Speech)',
          dataSource: 'Rehab Evaluation',
          timestamp: new Date(Date.now() - 14 * 60 * 60 * 1000).toISOString(),
          explanation: 'Standard home health visits insufficient for intensive rehab needs',
          contribution: 'negative'
        }
      ]
    },
    currentStage: WorkflowStage.ELIGIBILITY,
    assignment: {
      patientId: '7',
      userId: 'user1',
      userName: 'Sarah Miller',
      userInitials: 'SM',
      assignedAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh14',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago (matches currentLOS: 5)
        userId: 'system',
        userName: 'System',
        note: 'High-priority candidate for SNF at Home'
      },
      {
        id: 'wh15',
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.ELIGIBILITY,
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        userId: 'user1',
        userName: 'Sarah Miller',
        note: 'Excellent candidate. Proceeding with eligibility verification.'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mrs. Brown is a 74-year-old female who suffered a mild ischemic stroke affecting her left hemisphere with resultant right-sided weakness and mild aphasia. Over the past 90 days, her medical history has been notable for increasingly difficult-to-control hypertension and newly diagnosed atrial fibrillation.

She presented to the ED 5 days ago with sudden onset right-sided weakness, facial droop, and speech difficulties (NIHSS score 8 on arrival)[[1]]. CT and CT angiography confirmed an acute ischemic left MCA territory stroke[[2]]. She received IV tPA within 90 minutes of symptom onset with excellent response. Her NIHSS score improved to 3 within 24 hours, indicating mild residual deficits.

Her stroke workup revealed previously undiagnosed atrial fibrillation as the likely cardioembolic source[[3]]. Anticoagulation with apixaban was initiated[[4]]. Cardiac monitoring showed intermittent atrial fibrillation with rate control achieved on metoprolol. Blood pressure medications were optimized for secondary stroke prevention.

She has made remarkable progress with early mobility and rehabilitation. She currently has mild right arm weakness, slight right leg weakness, and mild word-finding difficulties. She requires minimal assistance with ADLs. Her daughter lives with her and is committed to being a full-time caregiver. She is an excellent candidate for intensive home-based SNF-level rehabilitation (PT/OT/Speech therapy) rather than facility-based SNF. Expected excellent functional recovery with intensive rehab.`,
      references: [
        { id: 1, source: 'NIH Stroke Scale (NIHSS)', sourceType: 'assessment', detail: 'Neurology · on arrival', snippet: 'NIHSS 8 on arrival, right-sided deficits' },
        { id: 2, source: 'CT / CT Angiography Report', sourceType: 'imaging', detail: 'Radiology · on admission', snippet: 'Acute ischemic left MCA territory stroke' },
        { id: 3, source: 'Cardiac Telemetry Monitoring', sourceType: 'vitals', detail: 'Telemetry · during admission', snippet: 'Intermittent atrial fibrillation, cardioembolic source' },
        { id: 4, source: 'Medication Reconciliation', sourceType: 'medication', detail: 'Pharmacy · during admission', snippet: 'Apixaban initiated for secondary stroke prevention' },
      ],
      recentAdmissions: 1,
      erVisits: 1,
      totalInpatientDays: 5,
      currentLOS: 5,
      trend: 'improving',
      trendDescription: 'Significant improvement post-stroke with excellent response to tPA. NIHSS improved from 8 to 3. Making steady gains with early rehabilitation. Appropriate candidate for intensive home-based SNF-level rehab.',
      keyFindings: [
        'Mild ischemic stroke (NIHSS 3) - excellent response to IV tPA treatment',
        'Newly diagnosed atrial fibrillation identified as stroke etiology',
        'Started on anticoagulation (apixaban) for secondary stroke prevention',
        'Requires intensive multi-disciplinary rehab: PT, OT, and Speech therapy',
        'Strong family support - daughter available as full-time caregiver at home',
        'Excellent candidate for home-based SNF-level rehabilitation services',
        'Expected good functional recovery with appropriate intensive therapy'
      ],
      events: [
        {
          date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'ED presentation with acute stroke symptoms - right-sided weakness, facial droop, aphasia (NIHSS 8)',
          location: 'City Medical Center - Emergency Department'
        },
        {
          date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted to stroke unit for acute ischemic stroke management',
          location: 'City Medical Center - Stroke Unit'
        },
        {
          date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'procedure',
          description: 'IV tPA administered within 90 minutes of symptom onset',
          location: 'City Medical Center - ED'
        },
        {
          date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'CT angiography completed - left MCA territory stroke confirmed',
          location: 'City Medical Center - Radiology'
        },
        {
          date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'NIHSS improved to 3 - excellent response to tPA',
          location: 'City Medical Center - Stroke Unit'
        },
        {
          date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Atrial fibrillation identified on cardiac monitoring',
          location: 'City Medical Center - Stroke Unit'
        },
        {
          date: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'Started anticoagulation with apixaban, metoprolol for rate control',
          location: 'City Medical Center - Stroke Unit'
        },
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'PT/OT/Speech therapy evaluations completed - needs intensive multi-disciplinary rehab',
          location: 'City Medical Center - Rehabilitation'
        },
        {
          date: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'SNF at Home program initiated - daughter confirmed as full-time caregiver',
          location: 'City Medical Center - Discharge Planning'
        },
        {
          date: new Date(Date.now() - 38 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Prior cardiology visit - palpitations evaluation, echo and monitoring ordered',
          location: 'Cardiology Clinic'
        },
        {
          date: new Date(Date.now() - 52 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Prior primary care visit - hypertension poorly controlled, medications adjusted',
          location: 'Primary Care Clinic'
        }
      ],
      lastUpdated: new Date(Date.now() - 90 * 60 * 1000).toISOString() // 90 minutes ago
    }
  },
  {
    id: '8',
    firstName: 'Michael',
    lastName: 'Davis',
    mrn: 'MRN001241',
    dateOfBirth: '1978-08-14T00:00:00.000Z',
    age: 46,
    gender: 'Male',
    address: '567 4th Ave N, Seattle, WA 98109',
    facility: 'Metro General Hospital',
    department: 'Emergency Department',
    locationType: LocationType.ED,
    attendingPhysician: 'Dr. Jennifer Lopez',
    primaryDiagnosis: 'Dehydration',
    insuranceType: 'Cigna',
    expectedLOS: 4,
    currentLOS: 3,
    recommendedProgram: PredictProgram.HOME_HEALTH,
    selectedProgram: PredictProgram.HOME_HEALTH,
    programScores: {
      [PredictProgram.HOME_HEALTH]: 35
    },
    programDecisionFactors: {
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df18_hh',
          label: 'Patient Preference',
          importance: 95,
          value: 'Patient declined home health services',
          dataSource: 'Discharge Planning',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Patient refusal makes home health inappropriate',
          contribution: 'negative'
        },
        {
          id: 'df19_hh',
          label: 'Service Need',
          importance: 70,
          value: 'Minimal skilled nursing needs',
          dataSource: 'ED Assessment',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Simple dehydration case does not require home health intervention',
          contribution: 'negative'
        }
      ]
    },
    currentStage: WorkflowStage.DISQUALIFIED,
    assignment: {
      patientId: '8',
      userId: 'user2',
      userName: 'James Thompson',
      userInitials: 'JT',
      assignedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    disqualificationReason: DisqualificationReason.PATIENT_DECLINED,
    disqualificationNote: 'Patient prefers to self-manage at home without home health services.',
    workflowHistory: [
      {
        id: 'wh16',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified for home health follow-up'
      },
      {
        id: 'wh17',
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.DISQUALIFIED,
        timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
        userId: 'user2',
        userName: 'James Thompson',
        note: 'Patient declined home health services. Provided education materials.'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mr. Davis is a 46-year-old male presenting with dehydration secondary to acute gastroenteritis. Over the past 90 days, he has been completely healthy with no prior medical encounters, hospitalizations, or chronic health conditions[[1]]. This is an isolated acute illness episode.

He presented to the ED 3 days ago with a 4-day history of nausea, vomiting, and diarrhea. He was unable to maintain adequate oral hydration, leading to moderate dehydration with orthostatic vital signs and electrolyte abnormalities[[2]]. He was treated with IV fluid resuscitation (3L normal saline), IV ondansetron for nausea, and electrolyte replacement with excellent response[[3]].

By hospital day 2, his symptoms had completely resolved. He is now tolerating regular diet without nausea, vomiting, or diarrhea. Vital signs are stable without orthostasis. Electrolytes normalized[[4]]. He is medically stable and ready for discharge.

Home health follow-up was offered for hydration monitoring and symptom management but was declined by the patient. He is fully functional, lives with his wife and two children, and prefers to manage his recovery independently at home with family support. Given his young age, lack of comorbidities, and full recovery, he is appropriate for standard discharge without home services. Patient was educated on hydration, diet advancement, and return precautions.`,
      references: [
        { id: 1, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: 'No prior encounters or chronic conditions' },
        { id: 2, source: 'Orthostatic Vitals / Nursing Assessment', sourceType: 'vitals', detail: 'Nursing · 3 days ago', snippet: 'Orthostatic vitals positive, moderate dehydration' },
        { id: 3, source: 'Medication Administration Record', sourceType: 'medication', detail: 'Nursing · on admission', snippet: '3L normal saline, IV ondansetron, electrolyte repletion' },
        { id: 4, source: 'Basic Metabolic Panel', sourceType: 'lab', detail: 'Lab · hospital day 2', snippet: 'Electrolytes normalized' },
      ],
      recentAdmissions: 1,
      erVisits: 1,
      totalInpatientDays: 3,
      currentLOS: 3,
      trend: 'improving',
      trendDescription: 'Complete resolution of acute gastroenteritis. Fully recovered from dehydration with normalized electrolytes and stable vital signs. Tolerating oral intake without issues.',
      keyFindings: [
        'Acute gastroenteritis with dehydration - now fully resolved',
        'No chronic medical conditions or comorbidities',
        'Excellent response to IV hydration and antiemetics',
        'Tolerating regular diet, electrolytes normalized, vital signs stable',
        'Young, healthy patient with full functional independence',
        'Patient declined home health services - prefers independent management',
        'Appropriate for standard discharge with return precautions'
      ],
      events: [
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'ED visit for dehydration from 4 days of vomiting and diarrhea',
          location: 'Metro General Hospital - Emergency Department'
        },
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for IV hydration and electrolyte repletion',
          location: 'Metro General Hospital - Emergency Department'
        },
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'Started IV ondansetron for nausea, received 3L IV normal saline',
          location: 'Metro General Hospital - ED'
        },
        {
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Symptoms resolved, tolerating oral intake, electrolytes normalized',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Patient declined home health services - ready for standard discharge',
          location: 'Metro General Hospital - Discharge Planning'
        }
      ],
      lastUpdated: new Date(Date.now() - 45 * 60 * 1000).toISOString() // 45 minutes ago
    }
  },
  {
    id: '9',
    firstName: 'Elizabeth',
    lastName: 'Wilson',
    mrn: 'MRN001242',
    dateOfBirth: '1942-01-20T00:00:00.000Z',
    age: 82,
    gender: 'Female',
    address: '123 NW 85th St, Seattle, WA 98117',
    facility: 'City Medical Center',
    department: 'Oncology',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Christopher Moore',
    primaryDiagnosis: 'End-stage Cancer',
    insuranceType: 'Medicare',
    expectedLOS: 5,
    currentLOS: 3,
    recommendedProgram: PredictProgram.HOSPICE,
    selectedProgram: PredictProgram.HOSPICE,
    programScores: {
      [PredictProgram.HOSPICE]: 92,
      [PredictProgram.PALLIATIVE]: 76,
      [PredictProgram.HOME_HEALTH]: 48
    },
    programDecisionFactors: {
      [PredictProgram.HOSPICE]: [
        {
          id: 'df20_hospice',
          label: 'Prognosis',
          importance: 98,
          value: 'Life expectancy < 6 months (weeks to months)',
          dataSource: 'Oncology Assessment',
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          explanation: 'Meets hospice eligibility criteria with clear prognosis',
          contribution: 'positive'
        },
        {
          id: 'df21_hospice',
          label: 'Goals of Care',
          importance: 96,
          value: 'Comfort measures only - DNR/DNI in place',
          dataSource: 'Family Meeting Notes',
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          explanation: 'Patient and family choosing hospice care at home',
          contribution: 'positive'
        },
        {
          id: 'df22_hospice',
          label: 'Caregiver Support',
          importance: 88,
          value: 'Son and daughter-in-law provide 24/7 care',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          explanation: 'Strong family support enables hospice care at home',
          contribution: 'positive'
        }
      ],
      [PredictProgram.PALLIATIVE]: [
        {
          id: 'df20_palliative',
          label: 'Symptom Management',
          importance: 88,
          value: 'Complex pain management on fentanyl patch',
          dataSource: 'Oncology Assessment',
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          explanation: 'Palliative care can manage complex symptom needs',
          contribution: 'positive'
        },
        {
          id: 'df21_palliative',
          label: 'Hospice Eligibility',
          importance: 85,
          value: 'Meets hospice criteria - hospice preferred',
          dataSource: 'Family Meeting Notes',
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          explanation: 'Patient clearly meets hospice criteria - hospice more appropriate',
          contribution: 'negative'
        }
      ],
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df20_hh',
          label: 'Skilled Care Appropriateness',
          importance: 88,
          value: 'Comfort-focused care, not skilled nursing needs',
          dataSource: 'Nursing Assessment',
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          explanation: 'Hospice/palliative care more appropriate than skilled home health',
          contribution: 'negative'
        },
        {
          id: 'df21_hh',
          label: 'Goals of Care Mismatch',
          importance: 82,
          value: 'End-of-life care focus',
          dataSource: 'Family Meeting Notes',
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          explanation: 'Goals of care do not align with home health model',
          contribution: 'negative'
        }
      ]
    },
    currentStage: WorkflowStage.ACCEPTANCE,
    assignment: {
      patientId: '9',
      userId: 'user1',
      userName: 'Sarah Miller',
      userInitials: 'SM',
      assignedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 90 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh18',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified for hospice evaluation'
      },
      {
        id: 'wh19',
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.ELIGIBILITY,
        timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
        userId: 'user1',
        userName: 'Sarah Miller',
        note: 'Hospice eligibility confirmed with physician certification'
      },
      {
        id: 'wh20',
        fromStage: WorkflowStage.ELIGIBILITY,
        toStage: WorkflowStage.ACCEPTANCE,
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        userId: 'user1',
        userName: 'Sarah Miller',
        note: 'Family meeting completed. Hospice agency contacted and accepted patient.'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mrs. Wilson is an 82-year-old female with end-stage metastatic pancreatic cancer[[1]] who has elected to transition to hospice care. Over the past 90 days, her condition has progressively declined with increasing symptom burden, multiple hospitalizations for pain and symptom management, and deteriorating functional status.

She was initially diagnosed with pancreatic cancer 18 months ago. She completed 6 cycles of FOLFIRINOX chemotherapy with initial partial response, but recent imaging 75 days ago showed widespread disease progression with new liver metastases, peritoneal carcinomatosis, and malignant ascites[[2]]. Her oncologist recommended discontinuing chemotherapy given minimal benefit and declining performance status.

Over the past 90 days, she has had three hospital encounters: (1) admission 66 days ago for malignant bowel obstruction managed conservatively without surgery (7-day stay), (2) admission 25 days ago for uncontrolled abdominal pain requiring escalation to fentanyl patch with oxycodone breakthrough (4-day stay)[[3]], and (3) current admission 3 days ago for worsening pain, decreased oral intake, and family exhaustion[[4]]. She has had progressive functional decline from ambulatory with walker to completely bedbound and dependent for all ADLs.

After extensive discussions with her oncologist, the hospitalist team, and her family, she has made the informed decision to focus on comfort measures and quality of life rather than life-prolonging treatments. Prognosis estimated at weeks to months. A family meeting was held to discuss hospice services. She, her son, and daughter-in-law are all in agreement with this transition to hospice care at home. Hospice agency has accepted her and is ready for transition upon discharge.`,
      references: [
        { id: 1, source: 'Oncology / Palliative Care Note', sourceType: 'note', detail: 'Dr. J. Feld, Oncology · current', snippet: 'End-stage metastatic pancreatic cancer' },
        { id: 2, source: 'CT Abdomen/Pelvis', sourceType: 'imaging', detail: 'Radiology · 75 days ago', snippet: 'New liver mets, peritoneal carcinomatosis, malignant ascites' },
        { id: 3, source: 'Palliative Medication Orders', sourceType: 'medication', detail: 'Palliative care · 25 days ago', snippet: 'Fentanyl patch with oxycodone breakthrough' },
        { id: 4, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '3 hospital admissions in 90 days' },
      ],
      recentAdmissions: 3,
      erVisits: 0,
      totalInpatientDays: 14,
      currentLOS: 3,
      trend: 'declining',
      trendDescription: 'Progressive decline from metastatic pancreatic cancer with three hospitalizations in 90 days. Functional status deteriorated from ambulatory to bedbound. Pain burden increasing despite escalating medications. Transitioned to hospice-focused care.',
      keyFindings: [
        'End-stage metastatic pancreatic cancer with widespread disease progression',
        'Prognosis estimated at weeks to months per oncology team',
        'Progressive functional decline - now completely bedbound and dependent for all ADLs',
        'Three hospitalizations in past 90 days for pain management and complications',
        'Pain escalated to fentanyl patch 75mcg with oxycodone 10mg breakthrough',
        'Goals of care transitioned to comfort measures only - hospice elected',
        'Strong family support - son and daughter-in-law provide 24/7 care in home',
        'Hospice agency coordinated and ready for home discharge'
      ],
      events: [
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for worsening pain, decreased PO intake, and family exhaustion',
          location: 'City Medical Center - Oncology'
        },
        {
          date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Family meeting held - hospice care elected, goals transitioned to comfort only',
          location: 'City Medical Center'
        },
        {
          date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Hospice eligibility confirmed - physician certification completed',
          location: 'City Medical Center - Hospice Coordination'
        },
        {
          date: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'discharge',
          description: 'Discharged home with escalated pain regimen after 4-day admission',
          destination: 'Home',
          location: 'City Medical Center'
        },
        {
          date: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for uncontrolled abdominal pain',
          location: 'City Medical Center - Oncology'
        },
        {
          date: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'Pain regimen escalated to fentanyl patch 75mcg + oxycodone 10mg breakthrough',
          location: 'City Medical Center - Pain Management'
        },
        {
          date: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'procedure',
          description: 'Palliative paracentesis performed for symptomatic ascites (3L removed)',
          location: 'City Medical Center - Interventional Radiology'
        },
        {
          date: new Date(Date.now() - 59 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'discharge',
          description: 'Discharged after 7-day admission for bowel obstruction (managed conservatively)',
          destination: 'Home',
          location: 'City Medical Center'
        },
        {
          date: new Date(Date.now() - 66 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for malignant bowel obstruction - conservative management without surgery',
          location: 'City Medical Center - Oncology'
        },
        {
          date: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Oncology clinic visit - imaging shows disease progression, chemotherapy discontinued',
          location: 'Oncology Clinic'
        }
      ],
      lastUpdated: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString() // 6 hours ago
    }
  },
  {
    id: '10',
    firstName: 'James',
    lastName: 'Taylor',
    mrn: 'MRN001243',
    dateOfBirth: '1955-06-08T00:00:00.000Z',
    age: 69,
    gender: 'Male',
    address: '789 Westlake Ave N, Seattle, WA 98109',
    facility: 'Metro General Hospital',
    department: 'Cardiology',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Anna Garcia',
    primaryDiagnosis: 'Atrial Fibrillation',
    insuranceType: 'Medicare',
    expectedLOS: 5,
    currentLOS: 3,
    recommendedProgram: PredictProgram.HOME_HEALTH,
    programScores: {
      [PredictProgram.HOME_HEALTH]: 58,
      [PredictProgram.HOSPITAL_AT_HOME]: 48
    },
    programDecisionFactors: {
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df22_hh',
          label: 'Anticoagulation Management Complexity',
          importance: 85,
          value: 'New warfarin requiring close INR monitoring',
          dataSource: 'Pharmacy Orders',
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Complex medication titration creates challenges for home health management',
          contribution: 'negative'
        },
        {
          id: 'df23_hh',
          label: 'Safety Concerns',
          importance: 78,
          value: 'Fall risk combined with anticoagulation',
          dataSource: 'PT Assessment',
          timestamp: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString(),
          explanation: 'Increased bleeding risk at home creates safety concerns',
          contribution: 'negative'
        }
      ],
      [PredictProgram.HOSPITAL_AT_HOME]: [
        {
          id: 'df22_hah',
          label: 'Clinical Acuity',
          importance: 88,
          value: 'Stable atrial fibrillation - converted to sinus rhythm',
          dataSource: 'Cardiology Notes',
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Does not meet Hospital at Home acuity requirements - too stable',
          contribution: 'negative'
        },
        {
          id: 'df23_hah',
          label: 'Care Level Appropriateness',
          importance: 75,
          value: 'Chronic anticoagulation management, not acute care',
          dataSource: 'Care Planning',
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Standard home health more appropriate than Hospital at Home intensity',
          contribution: 'negative'
        }
      ]
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '10',
      userId: 'user-metro-coordinator',
      userName: 'Michael Chen',
      userInitials: 'MC',
      assignedAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh21',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified for home health anticoagulation monitoring'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mr. Taylor is a 69-year-old male presenting with new-onset atrial fibrillation requiring anticoagulation and rate control. Over the past 90 days, he has been relatively stable with well-controlled hypertension and hyperlipidemia on his baseline medications. No prior cardiac arrhythmias or hospitalizations.

He presented to the ED 3 days ago with palpitations, lightheadedness, and dyspnea that started suddenly while at home. He was found to be in atrial fibrillation with rapid ventricular response (heart rate 140-160s)[[1]]. He received IV metoprolol in the ED with successful rate control to 80-90s. His rhythm spontaneously converted back to normal sinus rhythm within 24 hours.

Workup included echocardiogram showing normal left ventricular function, mildly dilated left atrium, no significant valvular disease[[2]]. Labs showed normal thyroid function and electrolytes. CHA₂DS₂-VASc score is 3 (age 69, hypertension)[[3]], indicating anticoagulation benefit. He was started on warfarin with target INR 2-3 for stroke prevention[[4]], and metoprolol was added for rate control.

He requires close monitoring of his anticoagulation therapy during the initial warfarin titration period. Home health nursing has been arranged for twice-weekly INR checks, warfarin dose adjustments in coordination with his cardiologist, and education on anticoagulation safety. He lives with his wife who is supportive. He is functionally independent and expected to do well with home health oversight during the anticoagulation initiation phase.`,
      references: [
        { id: 1, source: '12-Lead ECG / Telemetry', sourceType: 'vitals', detail: 'ED · 3 days ago', snippet: 'Atrial fibrillation with RVR, HR 140-160' },
        { id: 2, source: 'Echocardiogram Report', sourceType: 'imaging', detail: 'Cardiology · during admission', snippet: 'Normal LV function, mildly dilated LA, no valvular disease' },
        { id: 3, source: 'CHA₂DS₂-VASc Risk Score', sourceType: 'assessment', detail: 'Cardiology · during admission', snippet: 'Score 3 (age, hypertension); anticoagulation indicated' },
        { id: 4, source: 'Medication Reconciliation', sourceType: 'medication', detail: 'Pharmacy · during admission', snippet: 'Warfarin (target INR 2-3) and metoprolol started' },
      ],
      recentAdmissions: 1,
      erVisits: 1,
      totalInpatientDays: 3,
      currentLOS: 3,
      trend: 'stable',
      trendDescription: 'Stable - new diagnosis of atrial fibrillation with successful rate control and rhythm conversion. Being initiated on anticoagulation with appropriate monitoring plan in place.',
      keyFindings: [
        'New-onset atrial fibrillation with rapid ventricular response - successfully rate-controlled',
        'Spontaneous conversion to normal sinus rhythm within 24 hours',
        'CHA₂DS₂-VASc score 3 - clear indication for anticoagulation',
        'Started on warfarin anticoagulation (target INR 2-3) and metoprolol for rate control',
        'Echocardiogram shows structurally normal heart with mild LA dilation',
        'Requires close INR monitoring during warfarin titration phase',
        'Home health arranged for twice-weekly INR checks and warfarin management',
        'Good functional status, supportive wife at home'
      ],
      events: [
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'ED visit for palpitations, found in atrial fibrillation with RVR (HR 140-160s)',
          location: 'Metro General Hospital - Emergency Department'
        },
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted for new-onset atrial fibrillation management',
          location: 'Metro General Hospital - Cardiology'
        },
        {
          date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'IV metoprolol given in ED, rate controlled successfully',
          location: 'Metro General Hospital - ED'
        },
        {
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Spontaneous conversion to normal sinus rhythm',
          location: 'Metro General Hospital - Cardiology'
        },
        {
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'procedure',
          description: 'Transthoracic echocardiogram - normal LV function, mild LA dilation, no significant valve disease',
          location: 'Metro General Hospital - Cardiology'
        },
        {
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'Started warfarin 5mg daily (target INR 2-3) and metoprolol 50mg BID',
          location: 'Metro General Hospital - Cardiology'
        },
        {
          date: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Home health arranged for INR monitoring twice weekly during warfarin titration',
          location: 'Metro General Hospital - Discharge Planning'
        }
      ],
      lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() // 2 hours ago
    }
  },
  {
    id: '11',
    firstName: 'Susan',
    lastName: 'Miller',
    mrn: 'MRN001244',
    dateOfBirth: '1990-03-12T00:00:00.000Z',
    age: 34,
    gender: 'Female',
    address: '456 21st Ave E, Seattle, WA 98112',
    facility: 'City Medical Center',
    department: 'Emergency Department',
    locationType: LocationType.ED,
    attendingPhysician: 'Dr. Mark Johnson',
    primaryDiagnosis: 'Pyelonephritis',
    insuranceType: 'Blue Shield',
    expectedLOS: 5,
    currentLOS: 2,
    recommendedProgram: PredictProgram.HOME_HEALTH,
    selectedProgram: PredictProgram.HOME_HEALTH,
    programScores: {
      [PredictProgram.HOME_HEALTH]: 73,
      [PredictProgram.INFUSION_AT_HOME]: 75
    },
    programDecisionFactors: {
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df24_hh',
          label: 'IV Antibiotic Management',
          importance: 88,
          value: '7-day IV ceftriaxone completion at home',
          dataSource: 'Pharmacy Orders',
          timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health nursing can safely administer and monitor IV antibiotic therapy',
          contribution: 'positive'
        },
        {
          id: 'df25_hh',
          label: 'Clinical Stability',
          importance: 82,
          value: 'Afebrile x 24 hours, responding to treatment',
          dataSource: 'Vital Signs',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Stable clinical status allows safe transition to home with home health oversight',
          contribution: 'positive'
        },
        {
          id: 'df26_hh',
          label: 'Comprehensive Nursing Care',
          importance: 75,
          value: 'Home health provides holistic assessment and education',
          dataSource: 'Nursing Assessment',
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health RNs provide comprehensive care beyond IV administration',
          contribution: 'positive'
        }
      ],
      [PredictProgram.INFUSION_AT_HOME]: [
        {
          id: 'df24_infusion',
          label: 'Specialized IV Therapy',
          importance: 85,
          value: 'Single daily IV antibiotic protocol',
          dataSource: 'Pharmacy Orders',
          timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
          explanation: 'Dedicated infusion services specialize in IV medication management',
          contribution: 'positive'
        },
        {
          id: 'df25_infusion',
          label: 'Focused Care Model',
          importance: 78,
          value: 'Primary need is IV therapy administration',
          dataSource: 'Nursing Assessment',
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Infusion at Home appropriate for focused IV antibiotic delivery',
          contribution: 'positive'
        },
        {
          id: 'df26_infusion',
          label: 'Clinical Simplicity',
          importance: 72,
          value: 'Uncomplicated pyelonephritis case',
          dataSource: 'ED Assessment',
          timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
          explanation: 'Straightforward case well-suited for infusion services',
          contribution: 'positive'
        }
      ]
    },
    currentStage: WorkflowStage.ELIGIBILITY,
    assignment: {
      patientId: '11',
      userId: 'user2',
      userName: 'James Thompson',
      userInitials: 'JT',
      assignedAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 75 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh22',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago (matches currentLOS: 2)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified for home IV therapy'
      },
      {
        id: 'wh23',
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.ELIGIBILITY,
        timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
        userId: 'user2',
        userName: 'James Thompson',
        note: 'Verifying insurance coverage for home IV therapy'
      }
    ]
  },
  {
    id: '12',
    firstName: 'David',
    lastName: 'Rodriguez',
    mrn: 'MRN001245',
    dateOfBirth: '1948-09-28T00:00:00.000Z',
    age: 76,
    gender: 'Male',
    address: '789 Rainier Ave S, Seattle, WA 98118',
    facility: 'Metro General Hospital',
    department: 'Nephrology',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Patricia Lee',
    primaryDiagnosis: 'Acute Kidney Injury',
    insuranceType: 'Medicare',
    expectedLOS: 5,
    currentLOS: 3,
    recommendedProgram: PredictProgram.SNF_AT_HOME,
    selectedProgram: PredictProgram.SNF_AT_HOME,
    programScores: {
      [PredictProgram.SNF_AT_HOME]: 29,
      [PredictProgram.HOME_HEALTH]: 25
    },
    programDecisionFactors: {
      [PredictProgram.SNF_AT_HOME]: [
        {
          id: 'df26_snf',
          label: 'Worsening Renal Function',
          importance: 95,
          value: 'Creatinine 4.2 and rising, oliguria present',
          dataSource: 'Lab Results',
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Worsening renal function requires hospital-level monitoring beyond SNF at Home capability',
          contribution: 'negative'
        },
        {
          id: 'df27_snf',
          label: 'Dialysis Risk',
          importance: 92,
          value: 'May need emergent dialysis',
          dataSource: 'Nephrology Consult',
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          explanation: 'Patient too unstable for home-based care - requires hospital admission',
          contribution: 'negative'
        }
      ],
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df26_hh',
          label: 'Critical Clinical Instability',
          importance: 95,
          value: 'Acute kidney injury with rising creatinine',
          dataSource: 'Lab Results',
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Too unstable for standard home health - requires hospital-level care',
          contribution: 'negative'
        },
        {
          id: 'df27_hh',
          label: 'Potential Dialysis Need',
          importance: 88,
          value: 'May require emergent hemodialysis',
          dataSource: 'Nephrology Consult',
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health cannot manage potential dialysis needs',
          contribution: 'negative'
        }
      ]
    },
    currentStage: WorkflowStage.DISQUALIFIED,
    assignment: {
      patientId: '12',
      userId: 'user-city-coordinator',
      userName: 'Jennifer Martinez',
      userInitials: 'JM',
      assignedAt: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    disqualificationReason: DisqualificationReason.CLINICALLY_UNSTABLE,
    disqualificationNote: 'Creatinine still rising, may require dialysis. Not stable for home care.',
    workflowHistory: [
      {
        id: 'wh24',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
        userId: 'system',
        userName: 'System',
        note: 'Patient initially identified for SNF at Home'
      },
      {
        id: 'wh25',
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.DISQUALIFIED,
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        userId: 'user1',
        userName: 'Sarah Miller',
        note: 'Clinical condition deteriorating. Patient requires continued hospital care.'
      }
    ]
  },
  {
    id: '13',
    firstName: 'Nancy',
    lastName: 'White',
    mrn: 'MRN001246',
    dateOfBirth: '1965-11-05T00:00:00.000Z',
    age: 59,
    gender: 'Female',
    address: '234 Queen Anne Ave N, Seattle, WA 98109',
    facility: 'City Medical Center',
    department: 'Emergency Department',
    locationType: LocationType.ED,
    attendingPhysician: 'Dr. Steven Clark',
    primaryDiagnosis: 'Asthma Exacerbation',
    insuranceType: 'United Healthcare',
    expectedLOS: 6,
    currentLOS: 4,
    recommendedProgram: PredictProgram.HOME_HEALTH,
    programScores: {
      [PredictProgram.HOME_HEALTH]: 75,
      [PredictProgram.HOSPITAL_AT_HOME]: 52
    },
    programDecisionFactors: {
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df28_hh',
          label: 'Respiratory Improvement',
          importance: 88,
          value: 'Clinically improved after bronchodilators and steroids',
          dataSource: 'ED Assessment',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Patient stable on current medications, safe for home discharge',
          contribution: 'positive'
        },
        {
          id: 'df29_hh',
          label: 'Education Needs',
          importance: 82,
          value: 'Poor inhaler technique requiring education',
          dataSource: 'Respiratory Therapy',
          timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health can provide crucial asthma education and inhaler training',
          contribution: 'positive'
        },
        {
          id: 'df30_hh',
          label: 'Chronic Disease Management',
          importance: 75,
          value: 'Asthma monitoring and medication compliance',
          dataSource: 'Care Planning',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health appropriate for ongoing asthma management',
          contribution: 'positive'
        }
      ],
      [PredictProgram.HOSPITAL_AT_HOME]: [
        {
          id: 'df28_hah',
          label: 'Clinical Acuity',
          importance: 85,
          value: 'Stable asthma exacerbation, not acute enough for Hospital at Home',
          dataSource: 'ED Assessment',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Does not meet Hospital at Home acuity requirements - too stable',
          contribution: 'negative'
        },
        {
          id: 'df29_hah',
          label: 'Care Level Appropriateness',
          importance: 72,
          value: 'Chronic disease management, not acute care needs',
          dataSource: 'Care Planning',
          timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
          explanation: 'Standard home health more appropriate than Hospital at Home intensity',
          contribution: 'negative'
        }
      ]
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '13',
      userId: 'user1',
      userName: 'Sarah Miller',
      userInitials: 'SM',
      assignedAt: new Date(Date.now() - 90 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh26',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), // 4 days ago (matches currentLOS: 4)
        userId: 'system',
        userName: 'System',
        note: 'New patient identified for home health asthma management'
      }
    ]
  },
  {
    id: '14',
    firstName: 'Richard',
    lastName: 'Thomas',
    mrn: 'MRN001247',
    dateOfBirth: '1937-02-17T00:00:00.000Z',
    age: 87,
    gender: 'Male',
    address: '567 5th Ave S, Seattle, WA 98104',
    facility: 'Metro General Hospital',
    department: 'Geriatrics',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Michelle Adams',
    primaryDiagnosis: 'Multiple Chronic Conditions',
    insuranceType: 'Medicare',
    expectedLOS: 5,
    currentLOS: 3,
    recommendedProgram: PredictProgram.SNF_AT_HOME,
    programScores: {
      [PredictProgram.SNF_AT_HOME]: 80,
      [PredictProgram.HOME_HEALTH]: 70,
      [PredictProgram.HOSPITAL_AT_HOME]: 58
    },
    programDecisionFactors: {
      [PredictProgram.SNF_AT_HOME]: [
        {
          id: 'df30_snf',
          label: 'Complex Medication Management',
          importance: 88,
          value: '15 medications requiring intensive oversight',
          dataSource: 'Medication Reconciliation',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home skilled nursing can manage complex polypharmacy regimen',
          contribution: 'positive'
        },
        {
          id: 'df31_snf',
          label: 'Geriatric Frailty',
          importance: 90,
          value: 'Moderate frailty with multiple comorbidities',
          dataSource: 'Geriatric Assessment',
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home level of care appropriate for complex geriatric medical needs',
          contribution: 'positive'
        },
        {
          id: 'df32_snf',
          label: 'Caregiver Support',
          importance: 82,
          value: 'Son lives with patient as primary caregiver',
          dataSource: 'Social Work',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Strong caregiver support enables intensive SNF at Home delivery',
          contribution: 'positive'
        },
        {
          id: 'df33_snf',
          label: 'Daily Monitoring Needs',
          importance: 78,
          value: 'Multiple chronic conditions require daily assessment',
          dataSource: 'Clinical Assessment',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home provides intensive daily nursing oversight',
          contribution: 'positive'
        }
      ],
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df30_hh',
          label: 'Medication Coordination',
          importance: 82,
          value: 'Complex 15-medication regimen',
          dataSource: 'Medication Reconciliation',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health nursing can coordinate complex medication management',
          contribution: 'positive'
        },
        {
          id: 'df31_hh',
          label: 'Chronic Disease Monitoring',
          importance: 75,
          value: 'Multiple chronic conditions requiring oversight',
          dataSource: 'Geriatric Assessment',
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health appropriate for chronic disease management',
          contribution: 'positive'
        },
        {
          id: 'df32_hh',
          label: 'Care Intensity Needs',
          importance: 80,
          value: 'Complex geriatric needs may exceed standard home health',
          dataSource: 'Care Planning',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Frailty and polypharmacy may require more intensive oversight than HH provides',
          contribution: 'negative'
        }
      ],
      [PredictProgram.HOSPITAL_AT_HOME]: [
        {
          id: 'df30_hah',
          label: 'Clinical Acuity',
          importance: 88,
          value: 'Stable chronic conditions without acute illness',
          dataSource: 'Clinical Assessment',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Does not meet Hospital at Home acuity criteria - chronic management needs only',
          contribution: 'negative'
        },
        {
          id: 'df31_hah',
          label: 'Care Model Mismatch',
          importance: 75,
          value: 'Needs chronic disease management, not acute hospital-level care',
          dataSource: 'Care Planning',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home or home health more appropriate for chronic care needs',
          contribution: 'negative'
        }
      ]
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '14',
      userId: 'user-regional-manager',
      userName: 'David Thompson',
      userInitials: 'DT',
      assignedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh27',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
        userId: 'system',
        userName: 'System',
        note: 'Patient identified for SNF at Home due to complex medical needs'
      }
    ]
  },
  {
    id: '15',
    firstName: 'Barbara',
    lastName: 'Moore',
    mrn: 'MRN001248',
    dateOfBirth: '1981-07-30T00:00:00.000Z',
    age: 43,
    gender: 'Female',
    address: '890 California Ave SW, Seattle, WA 98116',
    facility: 'City Medical Center',
    department: 'Surgery',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Kevin Martinez',
    primaryDiagnosis: 'Post-op Wound Care',
    insuranceType: 'Aetna',
    expectedLOS: 5,
    currentLOS: 3,
    recommendedProgram: PredictProgram.HOME_HEALTH,
    selectedProgram: PredictProgram.HOME_HEALTH,
    programScores: {
      [PredictProgram.HOME_HEALTH]: 80,
      [PredictProgram.SNF_AT_HOME]: 68
    },
    programDecisionFactors: {
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df33_hh',
          label: 'Post-Surgical Wound Care',
          importance: 90,
          value: 'Healing well, requires skilled dressing changes',
          dataSource: 'Surgical Notes',
          timestamp: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health nursing ideal for post-operative wound management and monitoring',
          contribution: 'positive'
        },
        {
          id: 'df34_hh',
          label: 'Pain Management',
          importance: 78,
          value: 'Pain well-controlled on oral medications',
          dataSource: 'Pain Assessment',
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Stable pain control allows safe home discharge with home health support',
          contribution: 'positive'
        },
        {
          id: 'df35_hh',
          label: 'Functional Independence',
          importance: 72,
          value: 'Independently mobile, no rehab needs',
          dataSource: 'PT Assessment',
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Good functional status appropriate for home health level of care',
          contribution: 'positive'
        }
      ],
      [PredictProgram.SNF_AT_HOME]: [
        {
          id: 'df33_snf',
          label: 'Skilled Wound Care Capability',
          importance: 78,
          value: 'SNF at Home can provide intensive wound care',
          dataSource: 'Surgical Notes',
          timestamp: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home level can manage complex post-surgical wound care',
          contribution: 'positive'
        },
        {
          id: 'df34_snf',
          label: 'Rehabilitation Needs',
          importance: 75,
          value: 'No significant rehab requirements',
          dataSource: 'PT Assessment',
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Patient independently mobile - does not need intensive SNF-level therapy',
          contribution: 'negative'
        },
        {
          id: 'df35_snf',
          label: 'Care Appropriateness',
          importance: 70,
          value: 'Standard home health sufficient for wound care',
          dataSource: 'Case Management',
          timestamp: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString(),
          explanation: 'Straightforward post-op wound care can be managed by home health',
          contribution: 'negative'
        }
      ]
    },
    currentStage: WorkflowStage.ACCEPTANCE,
    assignment: {
      patientId: '15',
      userId: 'user2',
      userName: 'James Thompson',
      userInitials: 'JT',
      assignedAt: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString()
    },
    lastUpdated: new Date(Date.now() - 40 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh28',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
        userId: 'system',
        userName: 'System',
        note: 'Post-surgical patient identified for home health wound care'
      },
      {
        id: 'wh29',
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.ELIGIBILITY,
        timestamp: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString(),
        userId: 'user2',
        userName: 'James Thompson',
        note: 'Insurance pre-authorization obtained for home health'
      },
      {
        id: 'wh30',
        fromStage: WorkflowStage.ELIGIBILITY,
        toStage: WorkflowStage.ACCEPTANCE,
        timestamp: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString(),
        userId: 'user2',
        userName: 'James Thompson',
        note: 'Patient educated on wound care. Home health agency confirmed.'
      }
    ]
  }
];

// Hospital at Home-only patients: only visible when demoMode === 'hah-plus'
const HAH_ONLY_PATIENTS: PredictPatient[] = [
  {
    id: '16',
    firstName: 'Walter',
    lastName: 'Nakamura',
    mrn: 'MRN001258',
    dateOfBirth: '1957-03-15T00:00:00.000Z',
    age: 67,
    gender: 'Male',
    address: '142 NW 56th St, Seattle, WA 98107',
    facility: 'Metro General Hospital',
    department: 'Neurology',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Sophia Park',
    primaryDiagnosis: 'Atrial fibrillation, unspecified',
    insuranceType: 'Medicare',
    expectedLOS: 4,
    currentLOS: 2,
    recommendedProgram: PredictProgram.HOSPITAL_AT_HOME,
    programScores: {
      [PredictProgram.HOSPITAL_AT_HOME]: 88,
      [PredictProgram.HOME_HEALTH]: 65,
      [PredictProgram.SNF_AT_HOME]: 48,
    },
    programDecisionFactors: {
      [PredictProgram.HOSPITAL_AT_HOME]: [
        { id: 'df_hah_16_stability', label: 'Clinical Stability', importance: 92,
          value: 'Stable vitals and neuro exam; NIHSS 4 with no progression',
          dataSource: 'Neurology Assessment', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Stable neurologic status and vitals support daily monitoring under the Hospital at Home acute care model.',
          contribution: 'positive' },
        { id: 'df_hah_16_response', label: 'Treatment Response', importance: 88,
          value: 'Managed medically on antiplatelet plus high-intensity statin; deficits unchanged',
          dataSource: 'Neurology Notes', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'No thrombolysis or thrombectomy required, so the remaining regimen can be delivered and monitored at home.',
          contribution: 'positive' },
        { id: 'df_hah_16_caregiver', label: 'Caregiver Support', importance: 80,
          value: 'Daughter available as full-time caregiver',
          dataSource: 'Social Work Notes', timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Full-time caregiver is critical for Hospital at Home safety protocols.',
          contribution: 'positive' },
        { id: 'df_hah_16_home', label: 'Home Environment', importance: 76,
          value: 'Single-story home, WiFi available for remote monitoring',
          dataSource: 'Home Assessment', timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home environment supports Hospital at Home technology and care delivery.',
          contribution: 'positive' },
        { id: 'df_hah_16_insurance', label: 'Insurance Coverage', importance: 72,
          value: 'Traditional Medicare; hospital Acute Hospital Care at Home waiver covers the episode',
          dataSource: 'Case Management', timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          explanation: 'Waiver coverage is already in place, so no separate payer authorization is required.',
          contribution: 'positive' },
        { id: 'df_hah_16_readmission', label: 'Readmission Risk', importance: 70,
          value: 'Probable TIA 45 days ago; anticoagulation decision pending echo and telemetry',
          dataSource: 'Neurology Notes', timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          explanation: 'A recent cerebrovascular event and an unresolved anticoagulation plan raise near-term readmission risk that daily Hospital at Home oversight helps manage.',
          contribution: 'negative' },
      ],
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df_hah_mc_hh1',
          label: 'Clinical Complexity',
          importance: 85,
          value: 'Mild stroke requires physician-level monitoring beyond standard home health',
          dataSource: 'Neurology Assessment',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Hospital at Home offers daily physician oversight vs standard home health RN visits',
          contribution: 'negative'
        },
      ],
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '16',
      userId: 'user-metro-coordinator',
      userName: 'Michael Chen',
      userInitials: 'MC',
      assignedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    },
    lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      {
        id: 'wh_mc1',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        userId: 'system',
        userName: 'System',
        note: 'Patient identified as Hospital at Home candidate following mild ischemic stroke.'
      }
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mr. Nakamura is a 67-year-old male with a history of poorly controlled hypertension and newly documented atrial fibrillation, now admitted with a mild ischemic stroke. Over the past 90 days his cerebrovascular risk has become increasingly apparent, culminating in the current admission.

Approximately 70 days ago he was seen by his PCP for a routine visit, where blood pressure was poorly controlled (160s/90s) and an irregular pulse was noted[[1]]; he was referred to cardiology and started on an antihypertensive, though follow-up was incomplete.

About 45 days ago he presented to the ED with transient slurred speech and dizziness lasting roughly 20 minutes that resolved spontaneously. The episode was worked up as a probable TIA; imaging was unremarkable[[2]] and he was discharged with outpatient neurology follow-up.

He presented to the ED two days ago with acute right-sided weakness and slurred speech (NIHSS 4)[[3]]. MRI confirmed a small left MCA territory ischemic stroke with no large-vessel occlusion[[4]]; he was outside the thrombolysis window with only mild deficits, so he was managed medically with aspirin and high-intensity statin[[5]], with anticoagulation deferred pending echo and telemetry. He has remained neurologically stable since admission. PT/OT/SLP evaluation shows mild right arm weakness and mild dysarthria with good rehabilitation potential. His daughter is available as a full-time caregiver and the home environment supports home-based acute care.`,
      references: [
        { id: 1, source: 'PCP Visit Vitals', sourceType: 'vitals', detail: 'Primary care · 70 days ago', snippet: 'BP 160s/90s, irregular pulse noted' },
        { id: 2, source: 'CT Head (TIA workup)', sourceType: 'imaging', detail: 'Radiology · 45 days ago', snippet: 'Unremarkable; probable TIA' },
        { id: 3, source: 'NIH Stroke Scale (NIHSS)', sourceType: 'assessment', detail: 'Neurology · on admission', snippet: 'NIHSS 4; right-sided weakness, dysarthria' },
        { id: 4, source: 'MRI Brain Report', sourceType: 'imaging', detail: 'Radiology · 2 days ago', snippet: 'Small left MCA infarct, no large-vessel occlusion' },
        { id: 5, source: 'Stroke Medication Orders', sourceType: 'medication', detail: 'Neurology · 2 days ago', snippet: 'Aspirin and high-intensity statin; anticoagulation deferred' },
      ],
      recentAdmissions: 1,
      erVisits: 1,
      totalInpatientDays: 2,
      currentLOS: 2,
      trend: 'stable',
      trendDescription: 'First stroke admission following a probable TIA six weeks earlier, on a background of poorly controlled hypertension and new atrial fibrillation. Deficits are mild and neurologically stable since admission, with good rehab potential.',
      keyFindings: [
        'Small left MCA ischemic stroke with mild right-sided weakness and dysarthria (NIHSS 4)',
        'Probable preceding TIA ~6 weeks ago; underlying poorly controlled hypertension and newly documented atrial fibrillation',
        'Not a candidate for thrombolysis or thrombectomy; managed medically with antiplatelet plus high-intensity statin',
        'Anticoagulation decision pending echocardiogram and telemetry for AFib',
        'Full-time caregiver (daughter) and suitable home environment support Hospital at Home and home-based rehab',
      ],
      events: [
        {
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted via ED for acute right-sided weakness and slurred speech; NIHSS 4. MRI confirmed small left MCA ischemic stroke.',
          location: 'Metro General Hospital - Neurology'
        },
        {
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'Started aspirin 81mg and atorvastatin 80mg; anticoagulation deferred pending echo and telemetry.',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'pt_eval',
          description: 'PT/OT/SLP evaluation: mild right arm weakness and mild dysarthria, good rehab potential, safe for home-based therapy with caregiver support.',
          location: 'Metro General Hospital'
        },
        {
          date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'ED visit for transient slurred speech and dizziness (~20 min, resolved); worked up as probable TIA, discharged with outpatient neurology follow-up.',
          location: 'Metro General Hospital - ER'
        },
        {
          date: new Date(Date.now() - 70 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'PCP visit: poorly controlled hypertension (160s/90s) and irregular pulse noted; referred to cardiology and started on antihypertensive.',
          location: 'Greenwood Primary Care'
        },
      ],
      lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    },
  },
  {
    id: '17',
    firstName: 'David',
    lastName: 'Park',
    mrn: 'MRN001259',
    dateOfBirth: '1949-11-22T00:00:00.000Z',
    age: 75,
    gender: 'Male',
    address: '355 Greenwood Ave N, Seattle, WA 98103',
    facility: 'Metro General Hospital',
    department: 'Emergency Department',
    locationType: LocationType.ED,
    attendingPhysician: 'Dr. Angela Torres',
    primaryDiagnosis: 'Chronic obstructive pulmonary disease',
    insuranceType: 'Medicare Advantage',
    expectedLOS: 5,
    currentLOS: 0,
    recommendedProgram: PredictProgram.HOSPITAL_AT_HOME,
    programScores: {
      [PredictProgram.HOSPITAL_AT_HOME]: 48,
      [PredictProgram.HOME_HEALTH]: 35,
      [PredictProgram.SNF_AT_HOME]: 22,
    },
    programDecisionFactors: {
      [PredictProgram.HOSPITAL_AT_HOME]: [
        { id: 'df_hah_17_stability', label: 'Clinical Stability', importance: 92,
          value: 'On 3L O2 nasal cannula on arrival; SpO2 89% on room air, RR 22, HR 96',
          dataSource: 'ED Nursing Assessment / Triage', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Hospital at Home threshold is 2L O2 or less at rest with SpO2 at least 92%. Patient arrived above that threshold, though there is no BiPAP, intubation or other ICU indication. Response to initial treatment will determine if Hospital at Home is viable.',
          contribution: 'negative' },
        { id: 'df_hah_17_response', label: 'Treatment Response', importance: 88,
          value: 'Nebulizer therapy and steroids just started; response not yet assessed',
          dataSource: 'ED Physician Assessment', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Moderate COPD exacerbation is one of the best-evidenced Hospital at Home diagnoses, but candidacy depends on response to initial ED treatment.',
          contribution: 'negative' },
        { id: 'df_hah_17_caregiver', label: 'Caregiver Support', importance: 80,
          value: 'Son resides with patient but works full time; coverage unconfirmed',
          dataSource: 'Pending Social Work / Hospital at Home Screening', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Hospital at Home clinical candidacy is based on chart data only. Caregiver coverage has not yet been confirmed.',
          contribution: 'negative' },
        { id: 'df_hah_17_home', label: 'Home Environment', importance: 76,
          value: 'Not yet assessed — home environment and geographic eligibility unconfirmed',
          dataSource: 'Pending Social Work / Hospital at Home Screening', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Environmental eligibility screening has not yet been conducted, so the score reflects clinical factors alone.',
          contribution: 'negative' },
        { id: 'df_hah_17_insurance', label: 'Insurance Coverage', importance: 72,
          value: 'Medicare Advantage; Hospital at Home acute benefit confirmed, prior auth pending',
          dataSource: 'Case Management', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Benefit is confirmed, leaving prior authorization as the only remaining payer step.',
          contribution: 'positive' },
        { id: 'df_hah_17_readmission', label: 'Readmission Risk', importance: 70,
          value: 'Two COPD exacerbations in 45 days; escalating frequency on GOLD III disease',
          dataSource: 'Pulmonology Notes', timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Escalating exacerbation frequency raises readmission risk; Hospital at Home monitoring may interrupt the pattern.',
          contribution: 'negative' },
      ],
      [PredictProgram.HOME_HEALTH]: [
        {
          id: 'df_hah_dp_hh1',
          label: 'Not Yet in Post-Acute Phase',
          importance: 95,
          value: 'Patient presenting with active acute exacerbation — not currently a candidate for post-acute home health',
          dataSource: 'ED Physician Assessment',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home Health is a post-acute program for patients who have been treated and stabilized. This patient requires acute-level care first. Home Health may be appropriate as a step-down after a successful Hospital at Home episode.',
          contribution: 'negative'
        },
        {
          id: 'df_hah_dp_hh2',
          label: 'Future Step-Down Potential',
          importance: 60,
          value: 'Post-exacerbation COPD management (inhaler technique, O2 titration, symptom monitoring) is well-suited to home health once acute phase resolves',
          dataSource: 'Pulmonology / Respiratory Therapy',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home Health is a reasonable step-down option following a successful Hospital at Home episode if continued skilled nursing support is needed.',
          contribution: 'positive'
        },
      ],
      [PredictProgram.SNF_AT_HOME]: [
        {
          id: 'df_hah_dp_snf1',
          label: 'Not Applicable — Acute Phase',
          importance: 90,
          value: 'SNF at Home is a post-acute program requiring a qualifying 3-day inpatient hospital stay; patient has not yet been admitted',
          dataSource: 'Case Management',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home requires Medicare Part A benefit trigger (3-night inpatient stay) and is designed for patients needing daily skilled nursing or therapy. Neither criterion applies at this ED presentation.',
          contribution: 'negative'
        },
      ],
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '17',
      userId: 'user-city-coordinator',
      userName: 'Jennifer Martinez',
      userInitials: 'JM',
      assignedAt: new Date(Date.now() - 90 * 60 * 1000).toISOString(),
    },
    lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    ninetyDaySummary: {
      narrativeSummary: 'Mr. David Park is a 75-year-old male with a history of moderate-to-severe COPD (GOLD III)[[1]] presenting to the ED with his second acute exacerbation in the past 45 days[[2]]. Approximately 45 days ago he was admitted for a similar presentation and completed a 3-day inpatient stay, discharged on as-needed supplemental oxygen and an updated inhaler regimen. He returned to the ED 20 days ago with worsening dyspnea and was managed with nebulized bronchodilators and a short course of oral prednisone without requiring admission. A pulmonology follow-up 30 days ago confirmed GOLD III severity and resulted in the addition of umeclidinium/vilanterol (Anoro Ellipta) to his maintenance regimen[[3]], though this optimization has not yet translated into sustained exacerbation prevention.\n\nThe escalating frequency of exacerbations over the past 90 days — one inpatient admission, two ED presentations[[4]] — is consistent with a poorly controlled GOLD III trajectory and places him at elevated risk for near-term readmission. He is currently independent with activities of daily living and manages his nebulizer and inhalers at home. His son lives with him but works full-time. He has not previously been enrolled in a Hospital at Home program. His Medicare Advantage plan includes a Hospital at Home acute benefit, and this presentation is clinically appropriate for Hospital at Home consideration pending social and environmental screening.',
      references: [
        { id: 1, source: 'Pulmonology Note', sourceType: 'note', detail: 'Dr. A. Sandoval, Pulmonology · 30 days ago', snippet: 'GOLD III severity confirmed' },
        { id: 2, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 45 days', snippet: 'Second acute COPD exacerbation in 45 days' },
        { id: 3, source: 'Medication Reconciliation', sourceType: 'medication', detail: 'Pulmonology · 30 days ago', snippet: 'Added umeclidinium/vilanterol (Anoro Ellipta)' },
        { id: 4, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '1 inpatient admission, 2 ED presentations' },
      ],
      recentAdmissions: 1,
      erVisits: 2,
      totalInpatientDays: 3,
      currentLOS: 0,
      trend: 'declining',
      trendDescription: 'Two ED presentations and one inpatient admission in 45 days indicates escalating COPD exacerbation frequency. Pulmonology-directed medication optimization has not yet achieved stable control. Pattern is consistent with high near-term readmission risk without a structured acute care intervention.',
      keyFindings: [
        'GOLD III COPD — two exacerbations in past 45 days, escalating frequency',
        'Prior 3-day inpatient admission (45 days ago) for moderate COPD exacerbation',
        'Unscheduled ED return 20 days ago — managed without admission on nebulizers and oral steroids',
        'Pulmonology follow-up 30 days ago — umeclidinium/vilanterol added; GOLD III severity confirmed',
        'Currently independent with ADLs; self-managing nebulizer and inhaler regimen at home',
        'Son resides with patient but works full-time — caregiver availability unconfirmed for Hospital at Home',
        'Medicare Advantage plan — Hospital at Home acute benefit confirmed; prior auth pending',
        'No prior Hospital at Home enrollment; strong clinical candidate if social screening criteria met',
      ],
      events: [
        {
          date: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'Current ED presentation — moderate COPD exacerbation. SpO2 89% on room air, on 3L O2 nasal cannula on arrival. HR 96, RR 22. Responding to initial nebulizer treatment. Hospital at Home candidacy flagged.',
          location: 'Metro General Hospital — Emergency Department',
        },
        {
          date: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'er_visit',
          description: 'ED visit for worsening dyspnea and increased sputum production. Treated with albuterol/ipratropium nebulizers and oral prednisone 40mg x 5 days. SpO2 92% on 2L at discharge. Not admitted — discharged after 4-hour ED observation.',
          location: 'Metro General Hospital — Emergency Department',
        },
        {
          date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'other',
          description: 'Pulmonology outpatient follow-up. GOLD III severity confirmed (FEV1 38% predicted). Umeclidinium/vilanterol (Anoro Ellipta) added to maintenance regimen. Azithromycin 250mg 3x/week prophylaxis initiated for exacerbation prevention. COPD action plan reviewed with patient.',
          location: 'UW Medicine Pulmonology Clinic',
        },
        {
          date: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'medication_change',
          description: 'Post-discharge medication reconciliation: rescue inhaler frequency increased to PRN q4h. Home O2 concentrator ordered for use during activity (SpO2 <90%). PCP notified of discharge summary and updated regimen.',
          location: 'Metro General Hospital — Discharge Planning',
        },
        {
          date: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'discharge',
          description: 'Discharged after 3-day inpatient stay for moderate COPD exacerbation. Treated with IV methylprednisolone (transitioned to oral prednisone), nebulized bronchodilators, and supplemental O2. Discharged on 2L O2 PRN with home concentrator and updated inhaler regimen. Pulmonology follow-up scheduled.',
          location: 'Metro General Hospital',
          destination: 'Home + Home Health',
        },
        {
          date: new Date(Date.now() - 48 * 24 * 60 * 60 * 1000).toISOString(),
          type: 'admission',
          description: 'Admitted via ED for moderate COPD exacerbation — SpO2 86% on room air, on 4L O2, RR 26 on presentation. No ICU escalation required. Responded to systemic steroids and aggressive bronchodilator therapy over 48 hours.',
          location: 'Metro General Hospital',
        },
      ],
      lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    },
    workflowHistory: [
      {
        id: 'wh_dp1',
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        userId: 'system',
        userName: 'System',
        note: 'ED presentation: moderate COPD exacerbation. Clinical Hospital at Home candidacy flagged — O2 requirement on arrival above Hospital at Home threshold. Recommend Hospital at Home screening once response to initial treatment is assessed.'
      }
    ],
  },
];

// Additional Hospital at Home sample patients to give the Hospital at Home org demo a fuller dataset.
// Distribution: mostly 60–70, with some higher and some lower.
const HAH_EXTRA_PATIENTS: PredictPatient[] = [
  // ---------- Patient 18: Helen Nguyen — CAP, score 68 ----------
  {
    id: '18',
    firstName: 'Helen',
    lastName: 'Nguyen',
    mrn: 'MRN001260',
    dateOfBirth: '1952-06-04T00:00:00.000Z',
    age: 72,
    gender: 'Female',
    address: '88 Belmont Ave E, Seattle, WA 98102',
    facility: 'Metro General Hospital',
    department: 'Internal Medicine',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Sarah Chen',
    primaryDiagnosis: 'Pneumonia, unspecified organism',
    insuranceType: 'Medicare',
    expectedLOS: 4,
    currentLOS: 1,
    recommendedProgram: PredictProgram.HOSPITAL_AT_HOME,
    programScores: {
      [PredictProgram.HOSPITAL_AT_HOME]: 82,
      [PredictProgram.HOME_HEALTH]: 52,
      [PredictProgram.SNF_AT_HOME]: 30,
    },
    programDecisionFactors: {
      [PredictProgram.HOSPITAL_AT_HOME]: [
        { id: 'df_hah_18_stability', label: 'Clinical Stability', importance: 92,
          value: '2L NC, SpO2 94%, afebrile — at Hospital at Home threshold',
          dataSource: 'Nursing Flowsheet', timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Oxygen requirement and vitals sit at the Hospital at Home threshold and are trending in the right direction.',
          contribution: 'positive' },
        { id: 'df_hah_18_response', label: 'Treatment Response', importance: 88,
          value: 'Afebrile within 12h on IV ceftriaxone and azithromycin; O2 down from 4L to 2L in 24h',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Uncomplicated CAP responding this quickly can complete its IV course under Hospital at Home oversight.',
          contribution: 'positive' },
        { id: 'df_hah_18_caregiver', label: 'Caregiver Support', importance: 80,
          value: 'Adult son lives in home; works hybrid schedule',
          dataSource: 'Social Work Assessment', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'In-home caregiver with partial daytime presence meets the Hospital at Home coverage standard for a stable patient.',
          contribution: 'positive' },
        { id: 'df_hah_18_home', label: 'Home Environment', importance: 76,
          value: 'Stable residence with reliable connectivity for remote monitoring',
          dataSource: 'Home Assessment', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home environment supports Hospital at Home remote monitoring and daily visits.',
          contribution: 'positive' },
        { id: 'df_hah_18_insurance', label: 'Insurance Coverage', importance: 72,
          value: 'Traditional Medicare; hospital Acute Hospital Care at Home waiver covers the episode',
          dataSource: 'Case Management', timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          explanation: 'Waiver coverage is already in place, so no separate payer authorization is required.',
          contribution: 'positive' },
        { id: 'df_hah_18_readmission', label: 'Readmission Risk', importance: 70,
          value: 'No admissions or ED visits in 90 days; type 2 diabetes and HTN well controlled',
          dataSource: 'Problem List', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'A clean utilization history and well-controlled comorbidities point to low readmission risk.',
          contribution: 'positive' },
      ],
      [PredictProgram.HOME_HEALTH]: [
        { id: 'df_hh_18_1', label: 'Phase of Care Mismatch', importance: 88,
          value: 'Still in acute phase — needs daily MD oversight',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Standard home health is post-acute; this patient still requires acute-level monitoring.',
          contribution: 'negative' },
        { id: 'df_hh_18_2', label: 'Step-Down Potential', importance: 60,
          value: 'Good candidate for HH after acute phase resolves',
          dataSource: 'Discharge Planning', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Reasonable HH transition once IV antibiotics complete and oxygen weaned.',
          contribution: 'positive' },
      ],
      [PredictProgram.SNF_AT_HOME]: [
        { id: 'df_snf_18_1', label: 'No Qualifying Inpatient Stay', importance: 90,
          value: 'Day 1 of admission — no 3-day Medicare benefit trigger',
          dataSource: 'Case Management', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home requires 3-night inpatient stay and skilled rehab need; neither applies here.',
          contribution: 'negative' },
      ],
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '18',
      userId: 'user2',
      userName: 'James Thompson',
      userInitials: 'JT',
      assignedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    },
    lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      { id: 'wh_18_1', fromStage: null, toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        userId: 'system', userName: 'System',
        note: 'Patient flagged as Hospital at Home candidate following CAP admission.' },
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mrs. Nguyen is a 72-year-old female with well-controlled type 2 diabetes and hypertension admitted yesterday for community-acquired pneumonia. Over the past 90 days she has had no inpatient admissions and one PCP visit 35 days ago for a routine diabetes check[[1]].

She presented to the ED with 3 days of productive cough, low-grade fever, and dyspnea on exertion. CXR confirmed right lower lobe infiltrate[[2]]. She was started on IV ceftriaxone and azithromycin[[3]] with rapid clinical response — afebrile within 12 hours and SpO2 improved from 88% on room air to 94% on 2L NC[[4]].

She is otherwise functionally independent, lives with her adult son who provides hybrid-schedule caregiver coverage, and her home environment is well-suited to remote monitoring. Clinical trajectory and social profile support Hospital at Home candidacy pending oxygen wean.`,
      references: [
        { id: 1, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: 'No admissions; 1 routine PCP visit 35 days ago' },
        { id: 2, source: 'Chest X-Ray Report', sourceType: 'imaging', detail: 'Radiology · on admission', snippet: 'Right lower lobe infiltrate' },
        { id: 3, source: 'Medication Administration Record', sourceType: 'medication', detail: 'Nursing · yesterday', snippet: 'IV ceftriaxone and azithromycin started' },
        { id: 4, source: 'Pulse Oximetry / Vitals Flowsheet', sourceType: 'vitals', detail: 'Nursing · current', snippet: 'SpO2 88% RA to 94% on 2L NC; afebrile' },
      ],
      recentAdmissions: 0,
      erVisits: 0,
      totalInpatientDays: 1,
      currentLOS: 1,
      trend: 'improving',
      trendDescription: 'New acute illness without prior admissions in 90 days. Rapid clinical response to first-line therapy.',
      keyFindings: [
        'No prior admissions or ED visits in 90 days',
        'RLL pneumonia responding rapidly to IV ceftriaxone + azithromycin',
        'Oxygen requirement trending down (4L → 2L within 24h)',
        'Functionally independent at baseline; adult son in home',
        'Well-controlled comorbidities (T2DM, HTN)',
      ],
      events: [
        { date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), type: 'admission',
          description: 'Admitted from ED for CAP; started IV ceftriaxone + azithromycin',
          location: 'Metro General Hospital — Internal Medicine' },
        { date: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(), type: 'er_visit',
          description: 'ED presentation for cough, fever, dyspnea; RLL infiltrate on CXR',
          location: 'Metro General Hospital — Emergency Department' },
        { date: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString(), type: 'other',
          description: 'Routine PCP visit for diabetes management; A1C 6.8',
          location: 'Capitol Hill Primary Care' },
      ],
      lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    },
  },
  // ---------- Patient 19: Robert Hayes — mild CHF, score 66 ----------
  {
    id: '19',
    firstName: 'Robert',
    lastName: 'Hayes',
    mrn: 'MRN001261',
    dateOfBirth: '1955-02-19T00:00:00.000Z',
    age: 70,
    gender: 'Male',
    address: '410 12th Ave E, Seattle, WA 98102',
    facility: 'Metro General Hospital',
    department: 'Cardiology',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Michael Rodriguez',
    primaryDiagnosis: 'Acute on chronic congestive heart failure',
    insuranceType: 'Medicare Advantage',
    expectedLOS: 4,
    currentLOS: 2,
    recommendedProgram: PredictProgram.HOSPITAL_AT_HOME,
    programScores: {
      [PredictProgram.HOSPITAL_AT_HOME]: 81,
      [PredictProgram.HOME_HEALTH]: 54,
      [PredictProgram.SNF_AT_HOME]: 32,
    },
    programDecisionFactors: {
      [PredictProgram.HOSPITAL_AT_HOME]: [
        { id: 'df_hah_19_stability', label: 'Clinical Stability', importance: 92,
          value: 'BP 128/76, HR 82, no vasopressor need',
          dataSource: 'Cardiology Flowsheet', timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Hemodynamically stable on oral regimen — appropriate for Hospital at Home telemetry.',
          contribution: 'positive' },
        { id: 'df_hah_19_response', label: 'Treatment Response', importance: 88,
          value: 'Net negative 2L over 36 hours; weight down 4 lbs',
          dataSource: 'I/O Records', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Strong diuretic response indicates IV therapy can transition to PO at home with monitoring.',
          contribution: 'positive' },
        { id: 'df_hah_19_caregiver', label: 'Caregiver Support', importance: 80,
          value: 'Wife is full-time caregiver at home',
          dataSource: 'Social Work Notes', timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Reliable full-time caregiver supports Hospital at Home safety protocols.',
          contribution: 'positive' },
        { id: 'df_hah_19_home', label: 'Home Environment', importance: 76,
          value: 'Single-story home; daily weights and BP checks already part of the household routine',
          dataSource: 'Home Assessment', timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'An established home monitoring routine transfers directly to the Hospital at Home care plan.',
          contribution: 'positive' },
        { id: 'df_hah_19_insurance', label: 'Insurance Coverage', importance: 72,
          value: 'Medicare Advantage; Hospital at Home benefit confirmed',
          dataSource: 'Case Management', timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          explanation: 'Confirmed benefit removes the most common Hospital at Home delay.',
          contribution: 'positive' },
        { id: 'df_hah_19_readmission', label: 'Readmission Risk', importance: 70,
          value: 'Second CHF admission in 6 months',
          dataSource: 'Cardiology Notes', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Recent recurrence raises modest readmission risk; Hospital at Home monitoring may help prevent the next episode.',
          contribution: 'negative' },
      ],
      [PredictProgram.HOME_HEALTH]: [
        { id: 'df_hh_19_1', label: 'Phase of Care', importance: 82,
          value: 'Still requires daily IV diuresis and labs',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'HH cannot provide daily IV titration; Hospital at Home is the correct acute setting.',
          contribution: 'negative' },
        { id: 'df_hh_19_2', label: 'Step-Down Plan', importance: 65,
          value: 'CHF maintenance program well-suited to HH after acute phase',
          dataSource: 'Discharge Planning', timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'HH appropriate as follow-on care for CHF symptom monitoring and weights.',
          contribution: 'positive' },
      ],
      [PredictProgram.SNF_AT_HOME]: [
        { id: 'df_snf_19_1', label: 'No Skilled Rehab Need', importance: 85,
          value: 'Independent with ADLs and ambulation',
          dataSource: 'PT Eval', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home is designed for skilled rehab needs which are not present.',
          contribution: 'negative' },
      ],
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '19',
      userId: 'user2',
      userName: 'James Thompson',
      userInitials: 'JT',
      assignedAt: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
    },
    lastUpdated: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      { id: 'wh_19_1', fromStage: null, toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        userId: 'system', userName: 'System',
        note: 'Mild CHF exacerbation — Hospital at Home candidacy flagged after diuretic response.' },
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mr. Hayes is a 70-year-old male with chronic systolic heart failure (EF 40%)[[1]] admitted 2 days ago with mild CHF exacerbation. He had one prior admission 75 days ago (4-day stay) for similar exacerbation[[2]], and no ED visits between.

Current presentation was 4-day weight gain (8 lbs) and increasing dyspnea on exertion. He was started on IV furosemide with strong response (net -2L, weight down 4 lbs in 36h) and remains up 4 lbs from baseline[[3]]. BNP improved from 1450 to 720[[4]]. He remains hemodynamically stable on his usual oral regimen plus IV diuresis.

His wife is full-time caregiver and is familiar with his medication regimen and weight-monitoring routine. Echo from prior admission unchanged at EF 40%. Cardiology recommends continued diuresis with transition to oral and intensive monitoring — well-aligned with Hospital at Home acute model.`,
      references: [
        { id: 1, source: 'Echocardiogram Report', sourceType: 'imaging', detail: 'Cardiology · 75 days ago', snippet: 'LVEF 40%, chronic systolic dysfunction' },
        { id: 2, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '1 prior admission 75 days ago, 4-day stay' },
        { id: 3, source: 'Medication Administration Record', sourceType: 'medication', detail: 'Nursing · current', snippet: 'IV furosemide, net -2L, weight down 4 lbs in 36h' },
        { id: 4, source: 'BNP / Cardiac Labs', sourceType: 'lab', detail: 'Lab · current admission', snippet: 'BNP improved 1450 to 720 pg/mL' },
      ],
      recentAdmissions: 1,
      erVisits: 0,
      totalInpatientDays: 6,
      currentLOS: 2,
      trend: 'stable',
      trendDescription: 'Stable chronic HF with one recurrence. Excellent response to standard therapy. Spouse provides reliable caregiver support.',
      keyFindings: [
        'EF 40% (unchanged from prior); chronic systolic HF',
        'Strong diuretic response: net -2L, weight -4 lbs in 36h',
        'BNP improved from 1450 to 720',
        'Hemodynamically stable on oral regimen',
        'Wife provides full-time caregiver support; familiar with regimen',
      ],
      events: [
        { date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), type: 'admission',
          description: 'Admitted for mild CHF exacerbation; IV furosemide initiated',
          location: 'Metro General Hospital — Cardiology' },
        { date: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(), type: 'medication_change',
          description: 'IV furosemide 40mg BID; strong diuretic response',
          location: 'Metro General Hospital — Cardiology' },
        { date: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000).toISOString(), type: 'discharge',
          description: 'Discharged after 4-day stay for CHF exacerbation; HH initiated',
          location: 'Metro General Hospital', destination: 'Home + Home Health' },
        { date: new Date(Date.now() - 79 * 24 * 60 * 60 * 1000).toISOString(), type: 'admission',
          description: 'Admitted for CHF exacerbation; required IV diuresis',
          location: 'Metro General Hospital — Cardiology' },
      ],
      lastUpdated: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    },
  },
  // ---------- Patient 20: Patricia Brooks — UTI w/ bacteremia, score 64 ----------
  {
    id: '20',
    firstName: 'Patricia',
    lastName: 'Brooks',
    mrn: 'MRN001262',
    dateOfBirth: '1948-09-12T00:00:00.000Z',
    age: 76,
    gender: 'Female',
    address: '203 N 36th St, Seattle, WA 98103',
    facility: 'Metro General Hospital',
    department: 'Internal Medicine',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Lisa Anderson',
    primaryDiagnosis: 'Bacteremia',
    insuranceType: 'Medicare',
    expectedLOS: 5,
    currentLOS: 2,
    recommendedProgram: PredictProgram.HOSPITAL_AT_HOME,
    programScores: {
      [PredictProgram.HOSPITAL_AT_HOME]: 64,
      [PredictProgram.HOME_HEALTH]: 50,
      [PredictProgram.SNF_AT_HOME]: 28,
    },
    programDecisionFactors: {
      [PredictProgram.HOSPITAL_AT_HOME]: [
        { id: 'df_hah_20_stability', label: 'Clinical Stability', importance: 92,
          value: 'Afebrile x24h, BP 122/74, blood cultures clearing',
          dataSource: 'Nursing Assessment', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Sepsis source controlled and clinical course stabilized — appropriate for Hospital at Home transfer.',
          contribution: 'positive' },
        { id: 'df_hah_20_response', label: 'Treatment Response', importance: 88,
          value: 'Rapid defervescence on daily IV ceftriaxone; repeat cultures clearing',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Once-daily IV antibiotic regimens are routinely administered under Hospital at Home protocols.',
          contribution: 'positive' },
        { id: 'df_hah_20_caregiver', label: 'Caregiver Support', importance: 80,
          value: 'Niece provides daytime check-ins; lives alone overnight',
          dataSource: 'Social Work Notes', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Partial caregiver coverage is below the ideal Hospital at Home standard but workable with remote monitoring.',
          contribution: 'negative' },
        { id: 'df_hah_20_home', label: 'Home Environment', importance: 76,
          value: 'Single-story home; independent with ambulation and ADLs',
          dataSource: 'Home Assessment', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'An accessible home and functional independence support safe home-based care without a rehab requirement.',
          contribution: 'positive' },
        { id: 'df_hah_20_insurance', label: 'Insurance Coverage', importance: 72,
          value: 'Traditional Medicare; hospital Acute Hospital Care at Home waiver covers the episode',
          dataSource: 'Case Management', timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          explanation: 'Waiver coverage is already in place, so no separate payer authorization is required.',
          contribution: 'positive' },
        { id: 'df_hah_20_readmission', label: 'Readmission Risk', importance: 70,
          value: 'One ED visit for simple UTI 55 days ago; no admissions in 90 days',
          dataSource: 'Encounter History', timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
          explanation: 'A single low-acuity ED visit and no admissions indicate limited readmission risk.',
          contribution: 'positive' },
      ],
      [PredictProgram.HOME_HEALTH]: [
        { id: 'df_hh_20_1', label: 'Acute Phase Active', importance: 85,
          value: 'IV antibiotics with daily MD oversight required',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'HH cannot replace Hospital at Home-level acute monitoring during bacteremia treatment.',
          contribution: 'negative' },
        { id: 'df_hh_20_2', label: 'Future Step-Down', importance: 60,
          value: 'PO antibiotics + symptom monitoring suitable for HH later',
          dataSource: 'Discharge Planning', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'HH appropriate transition after IV course completes.',
          contribution: 'positive' },
      ],
      [PredictProgram.SNF_AT_HOME]: [
        { id: 'df_snf_20_1', label: 'No Rehab Need', importance: 88,
          value: 'Ambulatory at baseline; no skilled therapy need',
          dataSource: 'PT Eval', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home eligibility requires skilled therapy needs which are not present.',
          contribution: 'negative' },
      ],
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '20',
      userId: 'user1',
      userName: 'Sarah Miller',
      userInitials: 'SM',
      assignedAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    },
    lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      { id: 'wh_20_1', fromStage: null, toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        userId: 'system', userName: 'System',
        note: 'UTI with bacteremia, clinically stabilizing — Hospital at Home candidate for IV antibiotic completion.' },
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mrs. Brooks is a 76-year-old female admitted 2 days ago with E. coli urinary tract infection complicated by bacteremia[[1]]. She has had one prior ED visit 55 days ago for simple UTI managed with oral antibiotics[[2]], no admissions in 90 days otherwise.

On admission she was febrile (39.1°C) with positive blood cultures growing pan-sensitive E. coli[[3]]. She was started on IV ceftriaxone with rapid defervescence — afebrile for 24 hours[[4]]. Repeat blood cultures are clearing. Source control achieved with antibiotic therapy alone.

She lives alone in a single-story home with a niece nearby providing daytime check-ins. She is independent with ADLs and ambulation. Plan is to complete the IV course (estimated 4 more days) under Hospital at Home supervision rather than continuing inpatient stay.`,
      references: [
        { id: 1, source: 'Urinalysis / Microbiology', sourceType: 'lab', detail: 'Lab · on admission', snippet: 'E. coli UTI with bacteremia' },
        { id: 2, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '1 prior ED visit 55 days ago for simple UTI' },
        { id: 3, source: 'Blood Culture / Microbiology', sourceType: 'lab', detail: 'Lab · on admission', snippet: 'Pan-sensitive E. coli; temp 39.1°C' },
        { id: 4, source: 'Medication Administration Record', sourceType: 'medication', detail: 'Nursing · current', snippet: 'IV ceftriaxone; afebrile 24 hours' },
      ],
      recentAdmissions: 0,
      erVisits: 1,
      totalInpatientDays: 2,
      currentLOS: 2,
      trend: 'improving',
      trendDescription: 'Acute infectious illness with strong response to first-line therapy. No chronic decline pattern.',
      keyFindings: [
        'E. coli UTI with bacteremia — pan-sensitive organism',
        'Afebrile for 24 hours on IV ceftriaxone',
        'Repeat blood cultures clearing',
        'Lives alone but functionally independent',
        'Niece provides daytime caregiver support',
      ],
      events: [
        { date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), type: 'admission',
          description: 'Admitted for UTI with bacteremia; IV ceftriaxone initiated',
          location: 'Metro General Hospital — Internal Medicine' },
        { date: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(), type: 'other',
          description: 'Blood cultures growing pan-sensitive E. coli',
          location: 'Metro General Hospital — Microbiology' },
        { date: new Date(Date.now() - 55 * 24 * 60 * 60 * 1000).toISOString(), type: 'er_visit',
          description: 'ED visit for simple UTI; treated with oral antibiotics, discharged',
          location: 'Metro General Hospital — Emergency Department' },
      ],
      lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    },
  },
  // ---------- Patient 21: James O'Connor — cellulitis, score 70 ----------
  {
    id: '21',
    firstName: 'James',
    lastName: 'O’Connor',
    mrn: 'MRN001263',
    dateOfBirth: '1960-12-30T00:00:00.000Z',
    age: 64,
    gender: 'Male',
    address: '1750 Westlake Ave, Seattle, WA 98109',
    facility: 'Metro General Hospital',
    department: 'Internal Medicine',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Emily White',
    primaryDiagnosis: 'Cellulitis of lower limb',
    insuranceType: 'Blue Cross Blue Shield',
    expectedLOS: 4,
    currentLOS: 1,
    recommendedProgram: PredictProgram.HOSPITAL_AT_HOME,
    programScores: {
      [PredictProgram.HOSPITAL_AT_HOME]: 84,
      [PredictProgram.HOME_HEALTH]: 58,
      [PredictProgram.SNF_AT_HOME]: 32,
    },
    programDecisionFactors: {
      [PredictProgram.HOSPITAL_AT_HOME]: [
        { id: 'df_hah_21_stability', label: 'Clinical Stability', importance: 92,
          value: 'Afebrile, stable vitals; cellulitis margins regressing',
          dataSource: 'Nursing Assessment', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Stable vitals with a visibly improving infection support Hospital at Home transfer.',
          contribution: 'positive' },
        { id: 'df_hah_21_response', label: 'Treatment Response', importance: 88,
          value: 'Margins measurably regressed within 24h of IV vancomycin; PICC placed for 10-day course',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'A clear early response means the remaining IV course can be completed at home.',
          contribution: 'positive' },
        { id: 'df_hah_21_caregiver', label: 'Caregiver Support', importance: 80,
          value: 'Spouse home full-time; familiar with PICC care',
          dataSource: 'Social Work Assessment', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Full-time caregiver with prior PICC experience exceeds the Hospital at Home coverage standard.',
          contribution: 'positive' },
        { id: 'df_hah_21_home', label: 'Home Environment', importance: 76,
          value: 'Single-story home, reliable WiFi for remote monitoring',
          dataSource: 'Home Assessment', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Physical environment is well suited to Hospital at Home delivery including remote monitoring.',
          contribution: 'positive' },
        { id: 'df_hah_21_insurance', label: 'Insurance Coverage', importance: 72,
          value: 'BCBS Hospital at Home benefit confirmed; prior auth pending',
          dataSource: 'Case Management', timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Commercial benefit is confirmed, leaving prior authorization as the only remaining payer step.',
          contribution: 'positive' },
        { id: 'df_hah_21_readmission', label: 'Readmission Risk', importance: 70,
          value: 'No admissions or ED visits in 90 days; otherwise healthy with controlled HTN',
          dataSource: 'Encounter History', timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
          explanation: 'A clean utilization history and low comorbidity burden point to low readmission risk.',
          contribution: 'positive' },
      ],
      [PredictProgram.HOME_HEALTH]: [
        { id: 'df_hh_21_1', label: 'Phase of Care', importance: 80,
          value: 'Still requires daily IV vancomycin and trough labs',
          dataSource: 'Pharmacy Notes', timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          explanation: 'Vancomycin monitoring requires acute-level oversight beyond HH scope.',
          contribution: 'negative' },
        { id: 'df_hh_21_2', label: 'Step-Down Suitability', importance: 65,
          value: 'Wound care + PO transition is good HH fit',
          dataSource: 'Discharge Planning', timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          explanation: 'HH is reasonable transition once IV course completes.',
          contribution: 'positive' },
      ],
      [PredictProgram.SNF_AT_HOME]: [
        { id: 'df_snf_21_1', label: 'No Rehab Indication', importance: 85,
          value: 'Independent ambulation, no functional deficit',
          dataSource: 'PT Eval', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home eligibility requires skilled rehab needs which are not present.',
          contribution: 'negative' },
      ],
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '21',
      userId: 'user-metro-coordinator',
      userName: 'Michael Chen',
      userInitials: 'MC',
      assignedAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    },
    lastUpdated: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      { id: 'wh_21_1', fromStage: null, toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        userId: 'system', userName: 'System',
        note: 'Cellulitis responding to IV vancomycin — strong Hospital at Home candidate.' },
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mr. O'Connor is a 64-year-old male admitted yesterday with right lower extremity cellulitis requiring IV antibiotics. He has had no prior hospitalizations or ED visits in 90 days[[1]]. He is otherwise healthy with well-controlled hypertension.

Cellulitis margins were marked on admission and have regressed measurably in the first 24 hours of IV vancomycin[[2]]. He is afebrile with stable vitals[[3]]. A PICC line was placed for the planned 10-day IV course[[4]].

Home situation is strong: spouse is full-time caregiver with prior PICC line experience from a family member, the home is single-story with reliable WiFi, and his BCBS plan includes a Hospital at Home benefit (prior auth pending). Clinical and social factors align well for Hospital at Home transfer.`,
      references: [
        { id: 1, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: 'No prior admissions or ED visits' },
        { id: 2, source: 'Medication Administration Record', sourceType: 'medication', detail: 'Nursing · current', snippet: 'IV vancomycin; margins regressed in 24 hours' },
        { id: 3, source: 'Nursing Vitals Flowsheet', sourceType: 'vitals', detail: 'Nursing · current', snippet: 'Afebrile, stable vital signs' },
        { id: 4, source: 'Procedure Note (PICC placement)', sourceType: 'note', detail: 'IV team · yesterday', snippet: 'PICC placed for planned 10-day IV course' },
      ],
      recentAdmissions: 0,
      erVisits: 0,
      totalInpatientDays: 1,
      currentLOS: 1,
      trend: 'improving',
      trendDescription: 'Acute infectious illness with rapid response to therapy. No chronic disease burden.',
      keyFindings: [
        'No prior admissions or ED visits in 90 days',
        'Cellulitis margins regressing within 24h of IV vancomycin',
        'PICC line placed for 10-day IV course',
        'Spouse full-time caregiver with prior PICC experience',
        'BCBS Hospital at Home benefit confirmed; prior auth in progress',
      ],
      events: [
        { date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), type: 'admission',
          description: 'Admitted for RLE cellulitis; IV vancomycin initiated',
          location: 'Metro General Hospital — Internal Medicine' },
        { date: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(), type: 'procedure',
          description: 'PICC line placement for home IV antibiotic therapy',
          location: 'Metro General Hospital — Interventional Radiology' },
      ],
      lastUpdated: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    },
  },
  // ---------- Patient 22: Margaret Foster — pyelonephritis, score 62 ----------
  {
    id: '22',
    firstName: 'Margaret',
    lastName: 'Foster',
    mrn: 'MRN001264',
    dateOfBirth: '1946-04-08T00:00:00.000Z',
    age: 78,
    gender: 'Female',
    address: '610 Pine St, Seattle, WA 98101',
    facility: 'Metro General Hospital',
    department: 'Internal Medicine',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Robert Kim',
    primaryDiagnosis: 'Urinary tract infection, site not specified',
    insuranceType: 'Medicare',
    expectedLOS: 4,
    currentLOS: 1,
    recommendedProgram: PredictProgram.HOSPITAL_AT_HOME,
    programScores: {
      [PredictProgram.HOSPITAL_AT_HOME]: 62,
      [PredictProgram.HOME_HEALTH]: 48,
      [PredictProgram.SNF_AT_HOME]: 28,
    },
    programDecisionFactors: {
      [PredictProgram.HOSPITAL_AT_HOME]: [
        { id: 'df_hah_22_stability', label: 'Clinical Stability', importance: 92,
          value: 'Afebrile within 24h, tolerating PO',
          dataSource: 'Nursing Flowsheet', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Rapid defervescence with PO tolerance supports acute care in the home.',
          contribution: 'positive' },
        { id: 'df_hah_22_response', label: 'Treatment Response', importance: 88,
          value: 'Rapid response to IV ceftriaxone; 5 more days of the IV course planned',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Once-daily IV antibiotics for an uncomplicated pyelonephritis are routinely delivered under Hospital at Home.',
          contribution: 'positive' },
        { id: 'df_hah_22_caregiver', label: 'Caregiver Support', importance: 80,
          value: 'Lives alone; daughter visits twice daily',
          dataSource: 'Social Work Notes', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Twice-daily caregiver presence is below the ideal Hospital at Home standard and needs a remote monitoring plan overnight.',
          contribution: 'negative' },
        { id: 'df_hah_22_home', label: 'Home Environment', importance: 76,
          value: 'Two-story home but sleeps downstairs; within Hospital at Home service area at 5 miles',
          dataSource: 'Hospital at Home Screening', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Single-level living and geographic eligibility both clear, though a home safety assessment is indicated for gait instability.',
          contribution: 'positive' },
        { id: 'df_hah_22_insurance', label: 'Insurance Coverage', importance: 72,
          value: 'Traditional Medicare; hospital Acute Hospital Care at Home waiver covers the episode',
          dataSource: 'Case Management', timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          explanation: 'Waiver coverage is already in place, so no separate payer authorization is required.',
          contribution: 'positive' },
        { id: 'df_hah_22_readmission', label: 'Readmission Risk', importance: 70,
          value: 'Age 78 with mild gait instability; one ED visit for UTI 40 days ago',
          dataSource: 'PT Screen', timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
          explanation: 'Frailty and a recent low-acuity ED visit raise modest readmission risk that Hospital at Home monitoring helps offset.',
          contribution: 'negative' },
      ],
      [PredictProgram.HOME_HEALTH]: [
        { id: 'df_hh_22_1', label: 'Acute Phase', importance: 82,
          value: 'IV antibiotics with daily monitoring needed',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'HH does not provide acute-level daily IV antibiotic management.',
          contribution: 'negative' },
      ],
      [PredictProgram.SNF_AT_HOME]: [
        { id: 'df_snf_22_1', label: 'No Skilled Rehab', importance: 80,
          value: 'No PT/OT requirements',
          dataSource: 'PT Screen', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home eligibility requires skilled rehab needs which are not present.',
          contribution: 'negative' },
      ],
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '22',
      userId: 'user-metro-coordinator',
      userName: 'Michael Chen',
      userInitials: 'MC',
      assignedAt: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
    },
    lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      { id: 'wh_22_1', fromStage: null, toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        userId: 'system', userName: 'System',
        note: 'Pyelonephritis with rapid response — Hospital at Home candidacy flagged for IV antibiotic completion.' },
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mrs. Foster is a 78-year-old female admitted yesterday for acute pyelonephritis. She had one previous ED visit 40 days ago for a simple UTI managed with oral antibiotics[[1]], and no inpatient stays in 90 days.

Admission was prompted by 2 days of fever, flank pain, and dysuria. CT showed left pyelonephritis without abscess[[2]]. IV ceftriaxone initiated with rapid defervescence within 24 hours[[3]]. She is tolerating PO well.

She lives alone in a two-story home (sleeps downstairs since hip pain began 6 months ago). Daughter visits twice daily and manages medications and shopping. Mild gait instability noted on PT screen but no falls in 90 days[[4]]. Plan is to complete the IV course (5 more days) under Hospital at Home with home safety assessment included.`,
      references: [
        { id: 1, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '1 prior ED visit 40 days ago for simple UTI' },
        { id: 2, source: 'CT Abdomen/Pelvis', sourceType: 'imaging', detail: 'Radiology · on admission', snippet: 'Left pyelonephritis without abscess' },
        { id: 3, source: 'Medication Administration Record', sourceType: 'medication', detail: 'Nursing · current', snippet: 'IV ceftriaxone; defervescence within 24 hours' },
        { id: 4, source: 'Physical Therapy Screen / Fall Risk', sourceType: 'assessment', detail: 'PT · yesterday', snippet: 'Mild gait instability, no falls in 90 days' },
      ],
      recentAdmissions: 0,
      erVisits: 1,
      totalInpatientDays: 1,
      currentLOS: 1,
      trend: 'improving',
      trendDescription: 'Acute infection responding well to first-line therapy. Underlying mild frailty but no acute decline.',
      keyFindings: [
        'Pyelonephritis with rapid response to IV ceftriaxone',
        'No abscess on CT; uncomplicated course expected',
        'Lives alone; daughter visits twice daily',
        'Mild gait instability — Hospital at Home home safety assessment indicated',
        'Geographic eligibility for Hospital at Home confirmed (5 miles)',
      ],
      events: [
        { date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), type: 'admission',
          description: 'Admitted for acute pyelonephritis; IV ceftriaxone initiated',
          location: 'Metro General Hospital — Internal Medicine' },
        { date: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(), type: 'er_visit',
          description: 'ED presentation for flank pain, fever, dysuria',
          location: 'Metro General Hospital — Emergency Department' },
        { date: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(), type: 'er_visit',
          description: 'ED visit for simple UTI; treated with oral antibiotics, discharged',
          location: 'Metro General Hospital — Emergency Department' },
      ],
      lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    },
  },
  // ---------- Patient 23: Eleanor Whitfield — COPD w/ comorbidities, score 55 ----------
  {
    id: '23',
    firstName: 'Eleanor',
    lastName: 'Whitfield',
    mrn: 'MRN001265',
    dateOfBirth: '1940-07-22T00:00:00.000Z',
    age: 84,
    gender: 'Female',
    address: '2200 Elliott Ave, Seattle, WA 98121',
    facility: 'Metro General Hospital',
    department: 'Internal Medicine',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. James Wilson',
    primaryDiagnosis: 'Respiratory failure, unspecified',
    insuranceType: 'Medicare',
    expectedLOS: 6,
    currentLOS: 3,
    recommendedProgram: PredictProgram.HOSPITAL_AT_HOME,
    programScores: {
      [PredictProgram.HOSPITAL_AT_HOME]: 55,
      [PredictProgram.HOME_HEALTH]: 45,
      [PredictProgram.SNF_AT_HOME]: 38,
    },
    programDecisionFactors: {
      [PredictProgram.HOSPITAL_AT_HOME]: [
        { id: 'df_hah_23_stability', label: 'Clinical Stability', importance: 92,
          value: 'No ICU indicators; stable hemodynamics on 3L NC against a 2L home baseline',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
          explanation: 'No ICU-level requirements, and the oxygen gap from baseline is small enough to manage at home.',
          contribution: 'positive' },
        { id: 'df_hah_23_response', label: 'Treatment Response', importance: 88,
          value: 'Responding gradually — still on 3L O2 on day 3 of steroids and nebulizers',
          dataSource: 'Respiratory Therapy', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'A slow response means the oxygen requirement may not reach the Hospital at Home threshold before discharge.',
          contribution: 'negative' },
        { id: 'df_hah_23_caregiver', label: 'Caregiver Support', importance: 80,
          value: 'Daughter available evenings only; alone during the day',
          dataSource: 'Social Work Assessment', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Hospital at Home requires reliable daytime caregiver presence for a patient at this acuity — a significant gap.',
          contribution: 'negative' },
        { id: 'df_hah_23_home', label: 'Home Environment', importance: 76,
          value: 'Senior apartment with elevator access; no stairs',
          dataSource: 'Home Assessment', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Accessible single-level housing supports Hospital at Home equipment delivery and daily visits.',
          contribution: 'positive' },
        { id: 'df_hah_23_insurance', label: 'Insurance Coverage', importance: 72,
          value: 'Traditional Medicare; hospital Acute Hospital Care at Home waiver covers the episode',
          dataSource: 'Case Management', timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          explanation: 'Waiver coverage is already in place, so no separate payer authorization is required.',
          contribution: 'positive' },
        { id: 'df_hah_23_readmission', label: 'Readmission Risk', importance: 70,
          value: 'Two admissions in 25 days; CKD stage 3, atrial fibrillation on anticoagulation, and frailty',
          dataSource: 'Problem List', timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
          explanation: 'Recurrent admissions layered on a heavy comorbidity burden place this patient at high readmission risk.',
          contribution: 'negative' },
      ],
      [PredictProgram.HOME_HEALTH]: [
        { id: 'df_hh_23_1', label: 'Phase Mismatch', importance: 80,
          value: 'Still in acute exacerbation phase',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'HH inappropriate during active exacerbation; better fit post-acute.',
          contribution: 'negative' },
      ],
      [PredictProgram.SNF_AT_HOME]: [
        { id: 'df_snf_23_1', label: 'Possible Future Fit', importance: 70,
          value: 'Deconditioning likely; may benefit from skilled rehab',
          dataSource: 'PT Screen', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'After acute phase, SNF at Home may be viable if 3-day Medicare benefit triggers and skilled need is documented.',
          contribution: 'positive' },
        { id: 'df_snf_23_2', label: 'Caregiver Coverage', importance: 80,
          value: 'SNF at Home also requires substantial caregiver presence',
          dataSource: 'Social Work Notes', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'Same caregiver coverage gap limits SNF at Home eligibility.',
          contribution: 'negative' },
      ],
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '23',
      userId: 'user-regional-manager',
      userName: 'David Thompson',
      userInitials: 'DT',
      assignedAt: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
    },
    lastUpdated: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      { id: 'wh_23_1', fromStage: null, toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        userId: 'system', userName: 'System',
        note: 'COPD exacerbation in complex patient — Hospital at Home flagged but caregiver gap is primary risk.' },
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mrs. Whitfield is an 84-year-old female with severe COPD (GOLD III), chronic kidney disease stage 3, atrial fibrillation on warfarin, and mild frailty[[1]]. She has had two admissions in 90 days: 25 days ago (4-day stay for AFib with RVR) and current admission for COPD exacerbation[[2]].

Current admission was for increased dyspnea and productive cough over 4 days. She required 3L O2 nasal cannula (baseline 2L)[[3]] and was started on IV methylprednisolone, nebulized bronchodilators, and azithromycin[[4]]. She is responding gradually — still on 3L O2 day 3.

Daughter is sole caregiver, available evenings and weekends but works full-time during the day. Mrs. Whitfield lives in a senior apartment with elevator access. She is at high risk of further readmission given disease severity and limited daytime caregiver coverage. Hospital at Home could provide structured monitoring but caregiver gap is the central concern.`,
      references: [
        { id: 1, source: 'Problem List / Chart Review', sourceType: 'ehr', detail: 'Chart review · current', snippet: 'Severe COPD, CKD stage 3, AFib on warfarin, frailty' },
        { id: 2, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '2 admissions in 90 days (AFib RVR, COPD)' },
        { id: 3, source: 'Respiratory / SpO2 Flowsheet', sourceType: 'vitals', detail: 'Respiratory therapy · day 3', snippet: '3L O2 NC vs baseline 2L' },
        { id: 4, source: 'Medication Administration Record', sourceType: 'medication', detail: 'Nursing · current', snippet: 'IV methylprednisolone, nebs, azithromycin' },
      ],
      recentAdmissions: 1,
      erVisits: 0,
      totalInpatientDays: 7,
      currentLOS: 3,
      trend: 'declining',
      trendDescription: 'Two admissions in 25 days indicates accelerating frequency. Multiple comorbidities and frailty raise risk for next exacerbation.',
      keyFindings: [
        'GOLD III COPD; two admissions in 25 days',
        'Comorbidities: CKD3, AFib on warfarin, frailty',
        'Baseline home O2 2L; currently 3L during exacerbation',
        'Daughter is sole caregiver; daytime gap during work hours',
        'Senior apartment with elevator access; no stairs',
      ],
      events: [
        { date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), type: 'admission',
          description: 'Admitted for COPD exacerbation; IV methylprednisolone + bronchodilators',
          location: 'Metro General Hospital — Internal Medicine' },
        { date: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(), type: 'discharge',
          description: 'Discharged after 4-day stay for AFib RVR; rate-control regimen optimized',
          location: 'Metro General Hospital', destination: 'Home' },
        { date: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(), type: 'admission',
          description: 'Admitted for AFib with rapid ventricular response',
          location: 'Metro General Hospital — Cardiology' },
        { date: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(), type: 'other',
          description: 'Pulmonology follow-up; GOLD III confirmed, inhaler regimen adjusted',
          location: 'UW Medicine Pulmonology Clinic' },
      ],
      lastUpdated: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    },
  },
  // ---------- Patient 24: Daniel Reyes — diabetic foot infection, score 85 ----------
  {
    id: '24',
    firstName: 'Daniel',
    lastName: 'Reyes',
    mrn: 'MRN001266',
    dateOfBirth: '1958-01-17T00:00:00.000Z',
    age: 67,
    gender: 'Male',
    address: '975 8th Ave, Seattle, WA 98104',
    facility: 'Metro General Hospital',
    department: 'Internal Medicine',
    locationType: LocationType.INPATIENT,
    attendingPhysician: 'Dr. Anna Garcia',
    primaryDiagnosis: 'Non-pressure chronic ulcer of lower limb',
    insuranceType: 'Medicare Advantage',
    expectedLOS: 5,
    currentLOS: 2,
    recommendedProgram: PredictProgram.HOSPITAL_AT_HOME,
    programScores: {
      [PredictProgram.HOSPITAL_AT_HOME]: 85,
      [PredictProgram.HOME_HEALTH]: 65,
      [PredictProgram.SNF_AT_HOME]: 40,
    },
    programDecisionFactors: {
      [PredictProgram.HOSPITAL_AT_HOME]: [
        { id: 'df_hah_24_stability', label: 'Clinical Stability', importance: 92,
          value: 'Afebrile, BP/HR stable, glucose 110–160 range',
          dataSource: 'Nursing Flowsheet', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Stable infection course with good glycemic control supports safe Hospital at Home transfer.',
          contribution: 'positive' },
        { id: 'df_hah_24_response', label: 'Treatment Response', importance: 88,
          value: 'Afebrile within 24h of ciprofloxacin 400 mg IV q12h; erythema regressing and MRI negative for osteomyelitis',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'IV antibiotic therapy with daily wound care is one of the best-studied Hospital at Home scenarios, and this patient is already responding.',
          contribution: 'positive' },
        { id: 'df_hah_24_caregiver', label: 'Caregiver Support', importance: 80,
          value: 'Spouse retired; full-time presence at home with diabetes management experience',
          dataSource: 'Social Work Assessment', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          explanation: 'Full-time caregiver with no gaps meets the highest standard for Hospital at Home safety.',
          contribution: 'positive' },
        { id: 'df_hah_24_home', label: 'Home Environment', importance: 76,
          value: 'Single-story home, accessible bathroom, reliable WiFi',
          dataSource: 'Home Assessment', timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Physical environment ideal for Hospital at Home delivery including remote monitoring.',
          contribution: 'positive' },
        { id: 'df_hah_24_insurance', label: 'Insurance Coverage', importance: 72,
          value: 'Medicare Advantage with confirmed Hospital at Home benefit and pre-auth',
          dataSource: 'Case Management', timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          explanation: 'Pre-authorization already in place removes the most common Hospital at Home delay.',
          contribution: 'positive' },
        { id: 'df_hah_24_readmission', label: 'Readmission Risk', importance: 70,
          value: 'No admissions or ED visits in 90 days despite longstanding diabetes',
          dataSource: 'Encounter History', timestamp: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
          explanation: 'A clean 90-day utilization history despite chronic disease points to low readmission risk.',
          contribution: 'positive' },
      ],
      [PredictProgram.HOME_HEALTH]: [
        { id: 'df_hh_24_1', label: 'Acute Phase Active', importance: 80,
          value: 'IV antibiotics requiring daily MD oversight',
          dataSource: 'Hospitalist Notes', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'HH cannot replace Hospital at Home daily oversight during IV antibiotic course.',
          contribution: 'negative' },
        { id: 'df_hh_24_2', label: 'Future Wound Care Fit', importance: 70,
          value: 'Wound care + diabetes education well-suited to HH',
          dataSource: 'Discharge Planning', timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Excellent HH step-down candidate after IV course completes.',
          contribution: 'positive' },
      ],
      [PredictProgram.SNF_AT_HOME]: [
        { id: 'df_snf_24_1', label: 'No Rehab Indication', importance: 82,
          value: 'Independent ambulation despite foot ulcer',
          dataSource: 'PT Eval', timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          explanation: 'SNF at Home eligibility requires skilled rehab needs which are not present.',
          contribution: 'negative' },
      ],
    },
    currentStage: WorkflowStage.TRIAGE,
    assignment: {
      patientId: '24',
      userId: 'user-city-coordinator',
      userName: 'Jennifer Martinez',
      userInitials: 'JM',
      assignedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    },
    lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    workflowHistory: [
      { id: 'wh_24_1', fromStage: null, toStage: WorkflowStage.TRIAGE,
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        userId: 'system', userName: 'System',
        note: 'Diabetic foot infection on IV antibiotics — strong Hospital at Home candidate; pre-auth confirmed.' },
    ],
    ninetyDaySummary: {
      narrativeSummary: `Mr. Reyes is a 67-year-old male with longstanding type 2 diabetes[[1]] admitted 2 days ago for a right foot infection requiring IV antibiotics. Over the past 90 days he has had one outpatient wound clinic visit 30 days ago and no inpatient admissions or ED visits[[2]].

Current admission was prompted by worsening cellulitis around a chronic plantar ulcer with mild streaking proximally. He was started on ciprofloxacin 400 mg IV q12h with rapid clinical response — afebrile within 24 hours and surrounding erythema regressing[[3]]. MRI ruled out osteomyelitis[[4]]. A PICC line was placed for the planned 10-day IV course.

Patient is well-supported at home: retired spouse provides full-time caregiver presence with prior experience managing his diabetes, the home is single-story with an accessible bathroom and reliable WiFi, and his Medicare Advantage plan's Hospital at Home pre-authorization is already approved. Clinical, social, and administrative factors all align — high-confidence Hospital at Home candidate.`,
      references: [
        { id: 1, source: 'Problem List / Chart Review', sourceType: 'ehr', detail: 'Chart review · current', snippet: 'Longstanding type 2 diabetes' },
        { id: 2, source: 'Encounter / Utilization History', sourceType: 'claims', detail: 'Utilization summary · last 90 days', snippet: '1 wound clinic visit; no admissions or ED visits' },
        { id: 3, source: 'Medication Administration Record', sourceType: 'medication', detail: 'Nursing · current', snippet: 'Ciprofloxacin 400 mg IV q12h; afebrile within 24 hours' },
        { id: 4, source: 'MRI Foot Report', sourceType: 'imaging', detail: 'Radiology · on admission', snippet: 'No evidence of osteomyelitis' },
      ],
      recentAdmissions: 0,
      erVisits: 0,
      totalInpatientDays: 2,
      currentLOS: 2,
      trend: 'improving',
      trendDescription: 'Acute infection on chronic ulcer responding well to therapy. No prior admissions despite chronic disease.',
      keyFindings: [
        'Diabetic foot infection without osteomyelitis (MRI negative)',
        'Rapid response to ciprofloxacin 400 mg IV q12h',
        'PICC placed for 10-day IV course',
      ],
      events: [
        { date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), type: 'admission',
          description: 'Admitted for diabetic foot infection; ciprofloxacin 400 mg IV q12h initiated',
          location: 'Metro General Hospital — Internal Medicine' },
        { date: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(), type: 'procedure',
          description: 'PICC line placement for home IV antibiotic therapy',
          location: 'Metro General Hospital — Interventional Radiology' },
        { date: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(), type: 'other',
          description: 'MRI right foot — no osteomyelitis',
          location: 'Metro General Hospital — Radiology' },
        { date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), type: 'other',
          description: 'Wound clinic visit for chronic plantar ulcer; offloading footwear adjusted',
          location: 'UW Medicine Wound Care Clinic' },
      ],
      lastUpdated: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    },
  },
];

HAH_ONLY_PATIENTS.push(...HAH_EXTRA_PATIENTS);

const HAH_ONLY_PATIENT_IDS = new Set(['16', '17', '18', '19', '20', '21', '22', '23', '24']);

// Utility function to determine if a patient should be flagged as URGENT
const shouldFlagAsUrgent = (patient: PredictPatient): boolean => {
  // Disabled for now - urgent criteria will be defined based on customer priorities
  // Infrastructure remains in place for future implementation
  return false;
};

const shouldFlagAsNew = (patient: PredictPatient): boolean => {
  // Once a user has opened the patient detail view, drop the NEW badge.
  if (patient.viewedAt) {
    return false;
  }

  // Only flag as NEW if patient is still in TRIAGE stage
  // Once they've progressed beyond TRIAGE, they've been reviewed
  if (patient.currentStage !== WorkflowStage.TRIAGE) {
    return false;
  }

  // Find the initial workflow transition (where patient was first added)
  const initialTransition = patient.workflowHistory.find(
    transition => transition.fromStage === null
  );

  if (!initialTransition) {
    return false;
  }

  // Check if patient was added within the last 24 hours
  const transitionTime = new Date(initialTransition.timestamp).getTime();
  const now = new Date().getTime();
  const twentyFourHours = 24 * 60 * 60 * 1000;

  return (now - transitionTime) < twentyFourHours;
};

// Capacity tab (demo): each patient gets ONE capacity calendar so results are
// consistent across templates — admit slots are the same regardless of template,
// and the same future day + time slot always resolves to the same availability.
//
// Three reusable calendar archetypes set the patient's general capacity lean:
// - GREEN:  several admit slots today + a mostly-available pool.
// - YELLOW: a single admit slot + a limited-availability-heavy pool.
// - RED:    no admit slots today + a mostly-unavailable pool.
const A = CapacityStatus.AVAILABLE;
const L = CapacityStatus.LIMITED;
const U = CapacityStatus.UNAVAILABLE;

const CALENDAR_GREEN: CapacityCalendar = {
  admitSlots: [
    '8:00 AM – 9:00 AM',
    '11:30 AM – 12:30 PM',
    '2:00 PM – 3:00 PM',
    '4:00 PM – 5:00 PM',
    '6:00 PM – 7:00 PM',
  ],
  statusPool: [A, A, A, L, A, A, A, L, A, A],
};

const CALENDAR_YELLOW: CapacityCalendar = {
  admitSlots: ['1:00 PM – 2:00 PM'],
  statusPool: [L, A, L, A, U, L, A, L, L, A],
};

const CALENDAR_RED: CapacityCalendar = {
  admitSlots: [],
  statusPool: [U, L, U, A, U, U, L, U, U, L],
};

const CALENDAR_BY_LEAN: Record<CapacityStatus, CapacityCalendar> = {
  [A]: CALENDAR_GREEN,
  [L]: CALENDAR_YELLOW,
  [U]: CALENDAR_RED,
};

// Per-patient capacity lean, picking which calendar archetype the patient uses.
const CAPACITY_LEAN_BY_PATIENT_ID: Record<string, CapacityStatus> = {
  '1': L, '2': A, '3': U, '4': A, '5': L, '6': A, '7': U, '8': L,
  '9': A, '10': L, '11': U, '12': A, '13': L, '14': A, '15': U, '16': A,
  '17': L, '18': A, '19': U, '20': L, '21': A, '22': U, '23': L, '24': A,
};

// Attach a predefined capacity calendar to each demo patient.
const applyCapacityCalendar = (patients: PredictPatient[]): PredictPatient[] => {
  return patients.map(patient => ({
    ...patient,
    capacityCalendar:
      patient.capacityCalendar ?? CALENDAR_BY_LEAN[CAPACITY_LEAN_BY_PATIENT_ID[patient.id] ?? A],
  }));
};

// MRNs are salted with a per-browser-session token so patients promoted into the
// real chart via the intake flow don't collide with MRNs from an earlier demo
// run (which would land you in a stale patient's chart). Fresh on each page load.
const MRN_SESSION_SUFFIX = `-${(Date.now().toString(36) + Math.random().toString(36).slice(2)).toUpperCase().slice(-5)}`;
const applyFreshMrns = (patients: PredictPatient[]): PredictPatient[] =>
  patients.map((patient) => ({ ...patient, mrn: `${patient.mrn}${MRN_SESSION_SUFFIX}` }));

// Demo: give every patient a made-up Seattle-area phone number, deterministic
// per patient. Format: (206|425)-555-NNNN where NNNN is 2000-9500.
const applyPhones = (patients: PredictPatient[]): PredictPatient[] => {
  return patients.map(patient => {
    if (patient.phone !== undefined) {
      return patient;
    }
    const seed = [...patient.id].reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
    const area = seed % 2 === 0 ? '206' : '425';
    const last4 = 2000 + ((seed * 7919) % 7501);
    return { ...patient, phone: `${area}-555-${last4}` };
  });
};

// Qualify stage (demo): EHR-driven verification statuses, completed progressively
// as the patient advances. Clinical + social stability are verified once the
// patient reaches Eligibility; consent is verified once they reach Acceptance.
const QUALIFY_STAGE_ORDER = [
  WorkflowStage.TRIAGE,
  WorkflowStage.ELIGIBILITY,
  WorkflowStage.ACCEPTANCE,
  WorkflowStage.TRANSITION,
];
const applyQualifyChecks = (patients: PredictPatient[]): PredictPatient[] => {
  return patients.map(patient => {
    if (patient.qualifyChecks !== undefined) {
      return patient;
    }
    const idx = QUALIFY_STAGE_ORDER.indexOf(patient.currentStage);
    const level = idx >= 2 ? 'all' : idx >= 1 ? 'clinical_social' : 'none';
    return {
      ...patient,
      qualifyChecks: buildQualifyChecks(patient, patient.lastUpdated, level),
    };
  });
};

// Transfer stage (demo): patients in the TRANSITION stage have a transfer order
// on file (read-only checkbox on the Transfer phase); everyone else does not.
const applyTransferOrders = (patients: PredictPatient[]): PredictPatient[] => {
  return patients.map(patient => {
    if (patient.transferOrder !== undefined) {
      return patient;
    }
    if (patient.currentStage === WorkflowStage.TRANSITION) {
      return {
        ...patient,
        transferOrder: {
          entered: true,
          physician: patient.attendingPhysician,
          enteredAt: patient.lastUpdated,
        },
      };
    }
    return patient;
  });
};

// Apply automatic change type flagging logic to patients
const applyUrgentFlags = (patients: PredictPatient[]): PredictPatient[] => {
  return patients.map(patient => {
    // If patient should be urgent, override changeType
    if (shouldFlagAsUrgent(patient)) {
      return {
        ...patient,
        changeType: ChangeType.URGENT,
      };
    }

    // If patient was added in last 24 hours and no other changeType is set, flag as NEW
    // Preserve existing changeTypes like UPGRADED, CLINICAL_DETERIORATION, etc.
    if (shouldFlagAsNew(patient) && !patient.changeType) {
      return {
        ...patient,
        changeType: ChangeType.NEW,
      };
    }

    // Otherwise keep existing changeType
    return patient;
  });
};

// Provider component
interface PredictProviderProps {
  children: ReactNode;
  onPatientChange?: (
    patientId: string,
    patientName: string,
    changeType: 'upgrade' | 'deterioration' | 'reevaluation' | 'urgent',
    message: string
  ) => void;
}

export const PredictProvider: React.FC<PredictProviderProps> = ({ children, onPatientChange }) => {
  const [allPatients, setAllPatients] = useState<PredictPatient[]>(applyFreshMrns(applyPhones(applyQualifyChecks(applyTransferOrders(applyCapacityCalendar(applyUrgentFlags([...MOCK_PATIENTS, ...HAH_ONLY_PATIENTS])))))));
  const [currentUser, setCurrentUser] = useState<PredictUser>(DEMO_USERS[0]); // Default to admin user
  const [demoMode, setDemoModeState] = useState<DemoMode>(() => {
    return (localStorage.getItem('discharge_demo_mode') as DemoMode) || 'no-hah';
  });

  const setDemoMode = (mode: DemoMode) => {
    setDemoModeState(mode);
    localStorage.setItem('discharge_demo_mode', mode);
  };

  const { userEmail } = useAuth();
  const { organizationName } = useOrganization();

  // Demo-only: when the selected top-level org name contains "hospital at home",
  // restrict the sample data set and program fit UI to Hospital at Home only.
  const hahOrgSelected = !!organizationName && organizationName.toLowerCase().includes('hospital at home');

  const visiblePrograms = hahOrgSelected
    ? [PredictProgram.HOSPITAL_AT_HOME]
    : demoMode === 'hah-plus'
    ? HAH_PLUS_PROGRAMS
    : VISIBLE_PROGRAMS;

  // Filter patients based on user's client organization access and demo mode
  const patients = useMemo(() => {
    // Get list of client organization names user has access to
    const accessibleClientNames = currentUser.clientOrganizations.map(client => client.name);

    // Filter patients to only those from accessible client organizations,
    // excluding Hospital at Home-only patients when not in Hospital at Home+ demo mode (unless the
    // selected org is a Hospital at Home org, in which case we surface Hospital at Home
    // patients regardless of demo mode and hide non-Hospital at Home ones).
    return allPatients.filter(patient => {
      if (!accessibleClientNames.includes(patient.facility)) return false;

      if (hahOrgSelected) {
        return (
          patient.recommendedProgram === PredictProgram.HOSPITAL_AT_HOME ||
          patient.selectedProgram === PredictProgram.HOSPITAL_AT_HOME
        );
      }

      return demoMode === 'hah-plus' || !HAH_ONLY_PATIENT_IDS.has(patient.id);
    });
  }, [allPatients, currentUser, demoMode, hahOrgSelected]);

  // Get patient by ID
  const getPatientById = (id: string): PredictPatient | undefined => {
    return patients.find(p => p.id === id);
  };

  // Take ownership of a patient
  const takeOwnership = (patientId: string): void => {
    if (!userEmail) return;

    const userInitials = getInitials(userEmail);
    const userName = userEmail.split('@')[0].replace('.', ' ').split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

    setAllPatients(prevPatients =>
      applyUrgentFlags(prevPatients.map(patient =>
        patient.id === patientId
          ? {
              ...patient,
              assignment: {
                patientId,
                userId: userEmail,
                userName,
                userInitials,
                assignedAt: new Date().toISOString()
              },
              lastUpdated: new Date().toISOString()
            }
          : patient
      ))
    );
  };

  // Release ownership of a patient
  const releaseOwnership = (patientId: string): void => {
    setAllPatients(prevPatients =>
      applyUrgentFlags(prevPatients.map(patient =>
        patient.id === patientId
          ? {
              ...patient,
              assignment: {
                patientId,
                userId: null,
                userName: null,
                userInitials: null,
                assignedAt: null
              },
              lastUpdated: new Date().toISOString()
            }
          : patient
      ))
    );
  };

  // Assign ownership of a patient to a specific user
  const assignOwnership = (patientId: string, userId: string): void => {
    const targetUser = DEMO_USERS.find(user => user.email === userId);
    if (!targetUser) return;

    const userInitials = getInitials(targetUser.email);
    const userName = targetUser.name;

    setAllPatients(prevPatients =>
      applyUrgentFlags(prevPatients.map(patient =>
        patient.id === patientId
          ? {
              ...patient,
              assignment: {
                patientId,
                userId: targetUser.email,
                userName,
                userInitials,
                assignedAt: new Date().toISOString()
              },
              lastUpdated: new Date().toISOString()
            }
          : patient
      ))
    );
  };

  // Transition patient workflow
  const transitionWorkflow = (
    patientId: string,
    toStage: WorkflowStage,
    note: string,
    reason?: string
  ): void => {
    if (!userEmail) return;

    const userName = userEmail.split('@')[0].replace('.', ' ').split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

    setAllPatients(prevPatients =>
      applyUrgentFlags(prevPatients.map(patient => {
        if (patient.id !== patientId) return patient;

        // Check if program was changed (compare to last transition's selectedProgram)
        const lastTransitionProgram = patient.workflowHistory[0]?.selectedProgram;
        const programChanged = lastTransitionProgram !== undefined && lastTransitionProgram !== patient.selectedProgram;

        const newTransition: WorkflowTransition = {
          id: `wh_${Date.now()}`,
          fromStage: patient.currentStage,
          toStage,
          timestamp: new Date().toISOString(),
          userId: userEmail,
          userName,
          note,
          reason,
          selectedProgram: patient.selectedProgram,
          programChanged: programChanged
        };

        return {
          ...patient,
          currentStage: toStage,
          workflowHistory: [newTransition, ...patient.workflowHistory],
          lastUpdated: new Date().toISOString(),
          changeType: undefined // Clear clinical indicator after user takes action
        };
      }))
    );
  };

  // Generic update patient function
  const updatePatient = (patientId: string, updates: Partial<PredictPatient>): void => {
    setAllPatients(prevPatients =>
      applyUrgentFlags(prevPatients.map(patient =>
        patient.id === patientId
          ? {
              ...patient,
              ...updates,
              lastUpdated: new Date().toISOString()
            }
          : patient
      ))
    );
  };

  // Trigger demo scenario
  const triggerDemoScenario = (scenarioId: string): void => {
    if (scenarioId === 'angela-martinez-complication') {
      // Find Angela Martinez (id: '2')
      const robertId = '2';

      // Updated decision factors reflecting PIV line complication (SNF_AT_HOME: 78)
      const deterioratedDecisionFactors: DecisionFactor[] = [
        {
          id: 'df_piv1',
          label: 'PICC Line Management Capability',
          importance: 90,
          value: 'SNF at Home skilled nurses trained in PICC line care',
          dataSource: 'Nursing Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'SNF at Home provides intensive skilled nursing for complex PICC line management',
          contribution: 'positive'
        },
        {
          id: 'df_piv2',
          label: 'Daily Nursing Visits',
          importance: 85,
          value: 'Daily RN visits for PICC assessment and IV therapy',
          dataSource: 'Care Planning',
          timestamp: new Date().toISOString(),
          explanation: 'Frequent skilled nursing oversight appropriate for PICC line without caregiver',
          contribution: 'positive'
        },
        {
          id: 'df_piv3',
          label: 'IV Therapy Completion',
          importance: 82,
          value: '7 days remaining on IV antibiotic course',
          dataSource: 'Infectious Disease',
          timestamp: new Date().toISOString(),
          explanation: 'SNF at Home enables safe completion of IV therapy at home',
          contribution: 'positive'
        },
        {
          id: 'df_piv4',
          label: 'No Caregiver',
          importance: 78,
          value: 'Patient lives alone, no family available',
          dataSource: 'Social Work Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'Lack of caregiver increases risk and monitoring requirements',
          contribution: 'negative'
        },
        {
          id: 'df_piv5',
          label: 'Vascular Access Complication',
          importance: 72,
          value: 'PIV failed due to phlebitis, required PICC placement',
          dataSource: 'Interventional Radiology',
          timestamp: new Date().toISOString(),
          explanation: 'Complication history requires heightened monitoring and care',
          contribution: 'negative'
        }
      ];

      // Create workflow transition
      const icuTransition: WorkflowTransition = {
        id: `wh_icu_transfer_${Date.now()}`,
        fromStage: WorkflowStage.ELIGIBILITY,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date().toISOString(),
        userId: 'system',
        userName: 'System',
        note: 'PIV line removed due to phlebitis. PICC line placed for continued IV therapy. Clinical update: needs continued IV therapy with PICC vascular access; no caregiver available. Discharge plan revised - no longer Infusion at Home candidate, will need SNF at Home for PICC line management.'
      };

      // Update Angela Martinez
      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== robertId) return patient;

          return {
            ...patient,
            locationType: LocationType.INPATIENT,
            department: 'Emergency Department',
            recommendedProgram: PredictProgram.SNF_AT_HOME,
            selectedProgram: undefined, // Back at Predict for re-evaluation — no committed selection
            programScores: {
              [PredictProgram.SNF_AT_HOME]: 78,
              [PredictProgram.INFUSION_AT_HOME]: 65,
              [PredictProgram.HOME_HEALTH]: 58
            },
            programDecisionFactors: {
              [PredictProgram.SNF_AT_HOME]: deterioratedDecisionFactors.map(df => ({
                ...df,
                id: `${df.id}_snf`
              })),
              [PredictProgram.INFUSION_AT_HOME]: [
                {
                  id: 'df_piv_inf1',
                  label: 'IV Medication Delivery',
                  importance: 78,
                  value: 'Infusion services can administer IV antibiotics via PICC',
                  dataSource: 'Pharmacy Orders',
                  timestamp: new Date().toISOString(),
                  explanation: 'Infusion at Home has capability to deliver IV medications through PICC line',
                  contribution: 'positive'
                },
                {
                  id: 'df_piv_inf2',
                  label: 'PICC Line Experience',
                  importance: 70,
                  value: 'Infusion nurses trained in PICC line access',
                  dataSource: 'Infusion Services',
                  timestamp: new Date().toISOString(),
                  explanation: 'Staff experienced with PICC line medication administration',
                  contribution: 'positive'
                },
                {
                  id: 'df_piv_inf3',
                  label: 'No Caregiver',
                  importance: 82,
                  value: 'Lives alone, no one to monitor between visits',
                  dataSource: 'Social Work Assessment',
                  timestamp: new Date().toISOString(),
                  explanation: 'Without caregiver, limited ability to monitor PICC complications between infusion visits',
                  contribution: 'negative'
                },
                {
                  id: 'df_piv_inf4',
                  label: 'Limited Visit Frequency',
                  importance: 75,
                  value: 'Infusion visits typically once daily for medication only',
                  dataSource: 'Care Coordination',
                  timestamp: new Date().toISOString(),
                  explanation: 'May not provide comprehensive PICC line assessment and wound care',
                  contribution: 'negative'
                }
              ],
              [PredictProgram.HOME_HEALTH]: [
                {
                  id: 'df_piv_hh1',
                  label: 'Wound Care Capability',
                  importance: 70,
                  value: 'Can provide cellulitis wound assessment',
                  dataSource: 'Nursing Assessment',
                  timestamp: new Date().toISOString(),
                  explanation: 'Home health appropriate for wound monitoring and dressing changes',
                  contribution: 'positive'
                },
                {
                  id: 'df_piv_hh2',
                  label: 'Insurance Coverage',
                  importance: 65,
                  value: 'Blue Cross covers home health services',
                  dataSource: 'Insurance Verification',
                  timestamp: new Date().toISOString(),
                  explanation: 'Patient has insurance coverage for home health nursing',
                  contribution: 'positive'
                },
                {
                  id: 'df_piv_hh3',
                  label: 'PICC Line Management Limitations',
                  importance: 88,
                  value: 'Standard HH visits insufficient for PICC complexity',
                  dataSource: 'IV Therapy Team',
                  timestamp: new Date().toISOString(),
                  explanation: 'PICC line requires more intensive monitoring than typical home health provides',
                  contribution: 'negative'
                },
                {
                  id: 'df_piv_hh4',
                  label: 'No Caregiver',
                  importance: 85,
                  value: 'Lives alone, no family to assist with care',
                  dataSource: 'Social Work Assessment',
                  timestamp: new Date().toISOString(),
                  explanation: 'Without caregiver, safety concerns for managing PICC at home health level',
                  contribution: 'negative'
                }
              ]
            },
            currentStage: WorkflowStage.TRIAGE,
            changeType: ChangeType.CLINICAL_DETERIORATION,
            primaryDiagnosis: 'Cellulitis with PIV line complication',
            workflowHistory: [icuTransition, ...patient.workflowHistory],
            lastUpdated: new Date().toISOString(),
            // Reset assignment since workflow is being re-triaged
            assignment: {
              patientId: robertId,
              userId: null,
              userName: null,
              userInitials: null,
              assignedAt: null
            }
          };
        }))
      );

      // Trigger notification
      if (onPatientChange) {
        onPatientChange(
          robertId,
          'Angela Martinez',
          'deterioration',
          'PIV line removed due to phlebitis. PICC line placed but no caregiver available. Score changed from 85 to 78. Program changed from Home Health to SNF at Home.'
        );
      }
    } else if (scenarioId === 'linda-martinez-upgrade') {
      // Find Linda Martinez (id: '1')
      const lindaId = '1';

      // Updated decision factors reflecting clinical improvements (positive factors for high score)
      const updatedDecisionFactors: DecisionFactor[] = [
        {
          id: 'df1_updated',
          label: 'Mobility and Rehabilitation',
          importance: 92,
          value: 'PT/OT cleared for home with walker, good rehab potential',
          dataSource: 'PT/OT Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'Physical and occupational therapy confirm patient safe for home discharge with intensive rehabilitation',
          contribution: 'positive'
        },
        {
          id: 'df2_updated',
          label: 'Respiratory Improvement',
          importance: 88,
          value: 'Successfully weaned from 4L to 2L oxygen',
          dataSource: 'Respiratory Therapy',
          timestamp: new Date().toISOString(),
          explanation: 'Significant respiratory improvement allows safe home transition with manageable oxygen needs',
          contribution: 'positive'
        },
        {
          id: 'df3_updated',
          label: 'Caregiver Support',
          importance: 85,
          value: 'Daughter documented as available full-time caregiver',
          dataSource: 'Social Work Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'Strong caregiver support system in place enables SNF at Home level of care',
          contribution: 'positive'
        },
        {
          id: 'df4_updated',
          label: 'Clinical Stability',
          importance: 80,
          value: 'Stable vital signs and cardiac status for 24 hours',
          dataSource: 'Nursing Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'Hemodynamic stability and improved cardiac function support home discharge',
          contribution: 'positive'
        },
        {
          id: 'df5_updated',
          label: 'Ongoing O2 Needs',
          importance: 60,
          value: 'Still requires 2L continuous oxygen',
          dataSource: 'Respiratory Therapy',
          timestamp: new Date().toISOString(),
          explanation: 'Continued oxygen dependency requires monitoring and equipment management',
          contribution: 'negative'
        }
      ];

      // Create workflow transition
      const newTransition: WorkflowTransition = {
        id: `wh_scenario_${Date.now()}`,
        fromStage: null,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date().toISOString(),
        userId: 'system',
        userName: 'System',
        note: 'Clinical updates received: PT/OT evaluation complete, oxygen requirements reduced, caregiver documented. Patient upgraded to high confidence for SNF at Home discharge.'
      };

      // Update Linda Martinez
      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== lindaId) return patient;

          return {
            ...patient,
            recommendedProgram: PredictProgram.SNF_AT_HOME,
            selectedProgram: undefined, // Back at Predict for re-evaluation — no committed selection
            programScores: {
              [PredictProgram.SNF_AT_HOME]: 82,
              [PredictProgram.HOME_HEALTH]: 75,
              [PredictProgram.HOSPITAL_AT_HOME]: 68
            },
            programDecisionFactors: {
              [PredictProgram.SNF_AT_HOME]: updatedDecisionFactors.map(df => ({
                ...df,
                id: `${df.id}_snf`
              })),
              [PredictProgram.HOME_HEALTH]: [
                {
                  id: 'df_upgrade_hh1',
                  label: 'Clinical Stability',
                  importance: 85,
                  value: 'Stable vitals and improved cardiac status',
                  dataSource: 'Nursing Assessment',
                  timestamp: new Date().toISOString(),
                  explanation: 'Clinical stability allows home health management',
                  contribution: 'positive'
                },
                {
                  id: 'df_upgrade_hh2',
                  label: 'Caregiver Present',
                  importance: 82,
                  value: 'Daughter available for support',
                  dataSource: 'Social Work Assessment',
                  timestamp: new Date().toISOString(),
                  explanation: 'Family caregiver enables home health services',
                  contribution: 'positive'
                },
                {
                  id: 'df_upgrade_hh3',
                  label: 'Rehabilitation Intensity',
                  importance: 78,
                  value: 'Needs intensive multi-disciplinary rehab',
                  dataSource: 'PT/OT Assessment',
                  timestamp: new Date().toISOString(),
                  explanation: 'Intensive therapy needs may exceed standard home health capability',
                  contribution: 'negative'
                }
              ],
              [PredictProgram.HOSPITAL_AT_HOME]: [
                {
                  id: 'df_upgrade_hah1',
                  label: 'Cardiac Monitoring',
                  importance: 80,
                  value: 'CHF requires ongoing monitoring',
                  dataSource: 'Cardiology Notes',
                  timestamp: new Date().toISOString(),
                  explanation: 'Hospital at Home can provide enhanced cardiac monitoring and management',
                  contribution: 'positive'
                },
                {
                  id: 'df_upgrade_hah2',
                  label: 'Caregiver Support',
                  importance: 75,
                  value: 'Daughter available for Hospital at Home supervision',
                  dataSource: 'Social Work Assessment',
                  timestamp: new Date().toISOString(),
                  explanation: 'Caregiver presence enables Hospital at Home services',
                  contribution: 'positive'
                },
                {
                  id: 'df_upgrade_hah3',
                  label: 'Rehabilitation Needs',
                  importance: 70,
                  value: 'Needs intensive rehab focus',
                  dataSource: 'PT/OT Assessment',
                  timestamp: new Date().toISOString(),
                  explanation: 'SNF at Home better suited for rehabilitation-focused care than Hospital at Home',
                  contribution: 'negative'
                }
              ]
            },
            currentStage: WorkflowStage.TRIAGE,
            changeType: ChangeType.CLINICAL_CHANGE,
            workflowHistory: [newTransition, ...patient.workflowHistory],
            lastUpdated: new Date().toISOString()
          };
        }))
      );

      // Trigger notification
      if (onPatientChange) {
        onPatientChange(
          lindaId,
          'Linda Martinez',
          'upgrade',
          'Clinical updates received. Score upgraded from 45 to 82 for SNF at Home. PT/OT evaluation complete, oxygen weaned, caregiver available.'
        );
      }
    } else if (scenarioId === 'william-johnson-disqualification') {
      // Find William Johnson (id: '4')
      const williamId = '4';

      // Updated decision factors reflecting caregiver availability (HOME_HEALTH: 75)
      const updatedDecisionFactorsHH: DecisionFactor[] = [
        {
          id: 'df_wj_updated_1',
          label: 'Caregiver Availability',
          importance: 92,
          value: 'Daughter arranged FMLA leave',
          dataSource: 'Social Work Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'Daughter will be available full-time for 6 weeks to provide daytime care and support',
          contribution: 'positive'
        },
        {
          id: 'df_wj_updated_2',
          label: 'Post-surgical Rehabilitation',
          importance: 88,
          value: 'Good progress with PT/OT',
          dataSource: 'PT Evaluation',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health PT appropriate for continued hip replacement rehabilitation',
          contribution: 'positive'
        },
        {
          id: 'df_wj_updated_3',
          label: 'Mobility Status',
          importance: 82,
          value: 'Ambulating with walker',
          dataSource: 'PT Notes',
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Good progress with mobility, safe for home with assistance',
          contribution: 'positive'
        },
        {
          id: 'df_wj_updated_4',
          label: 'Post-operative Care Needs',
          importance: 72,
          value: 'POD 4 from major orthopedic surgery',
          dataSource: 'Surgical Notes',
          timestamp: new Date().toISOString(),
          explanation: 'Early post-op period requires close monitoring for pain, wound healing, and DVT prevention',
          contribution: 'negative'
        },
        {
          id: 'df_wj_updated_5',
          label: 'Home Safety',
          importance: 70,
          value: 'Home modifications completed',
          dataSource: 'Home Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'Grab bars installed, raised toilet seat, shower chair in place',
          contribution: 'positive'
        }
      ];

      // SNF_AT_HOME factors (score: 68)
      const updatedDecisionFactorsSNF: DecisionFactor[] = [
        {
          id: 'df_wj_snf_updated_1',
          label: 'Intensive Rehabilitation Capability',
          importance: 78,
          value: 'SNF at Home provides intensive multi-disciplinary therapy',
          dataSource: 'Care Planning',
          timestamp: new Date().toISOString(),
          explanation: 'SNF at Home can deliver intensive PT/OT if needed for complex orthopedic recovery',
          contribution: 'positive'
        },
        {
          id: 'df_wj_snf_updated_2',
          label: 'Caregiver Support',
          importance: 75,
          value: 'Daughter available full-time',
          dataSource: 'Social Work Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'Caregiver presence supports SNF at Home level services',
          contribution: 'positive'
        },
        {
          id: 'df_wj_snf_updated_3',
          label: 'Care Intensity Needs',
          importance: 82,
          value: 'Standard post-op hip replacement recovery',
          dataSource: 'PT Evaluation',
          timestamp: new Date().toISOString(),
          explanation: 'Patient progressing well with standard PT protocol - does not require SNF-level intensity',
          contribution: 'negative'
        },
        {
          id: 'df_wj_snf_updated_4',
          label: 'Service Appropriateness',
          importance: 70,
          value: 'Home health appropriate for current needs',
          dataSource: 'Case Management',
          timestamp: new Date().toISOString(),
          explanation: 'Standard home health PT sufficient - SNF at Home would be higher level than needed',
          contribution: 'negative'
        }
      ];

      // Create re-evaluation transition
      const reEvaluationTransition: WorkflowTransition = {
        id: `wh_william_reeval_${Date.now()}`,
        fromStage: WorkflowStage.DISQUALIFIED,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date().toISOString(),
        userId: 'system',
        userName: 'System',
        note: 'Re-evaluation triggered: Caregiver situation resolved. Daughter arranged FMLA leave to provide full-time care. Patient now eligible for home health discharge.'
      };

      // Update William Johnson to eligible state
      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== williamId) return patient;

          return {
            ...patient,
            recommendedProgram: PredictProgram.HOME_HEALTH,
            selectedProgram: undefined, // Back at Predict for re-evaluation — no committed selection
            programScores: {
              [PredictProgram.HOME_HEALTH]: 75,
              [PredictProgram.SNF_AT_HOME]: 68
            },
            programDecisionFactors: {
              [PredictProgram.HOME_HEALTH]: updatedDecisionFactorsHH,
              [PredictProgram.SNF_AT_HOME]: updatedDecisionFactorsSNF
            },
            currentStage: WorkflowStage.TRIAGE,
            disqualificationReason: undefined,
            disqualificationNote: undefined,
            changeType: ChangeType.CLINICAL_CHANGE,
            workflowHistory: [reEvaluationTransition, ...patient.workflowHistory],
            lastUpdated: new Date().toISOString()
          };
        }))
      );

      // Trigger notification
      if (onPatientChange) {
        onPatientChange(
          williamId,
          'William Johnson',
          'reevaluation',
          'Patient re-evaluated after caregiver situation resolved. Daughter arranged FMLA leave. Score improved from 42 to 75 for Home Health.'
        );
      }
    } else if (scenarioId === 'thomas-anderson-icu-recovery') {
      // Find Thomas Anderson (id: '6')
      const thomasId = '6';

      // Updated decision factors reflecting ICU recovery and floor transfer
      const recoveryDecisionFactors: DecisionFactor[] = [
        {
          id: 'df_ta_recovery_1',
          label: 'Respiratory Status',
          importance: 92,
          value: 'Extubated, stable on 2L O2',
          dataSource: 'Pulmonology Notes',
          timestamp: new Date().toISOString(),
          explanation: 'Successfully extubated after 48h ventilation, good respiratory effort on low-flow oxygen',
          contribution: 'positive'
        },
        {
          id: 'df_ta_recovery_2',
          label: 'Vital Signs',
          importance: 88,
          value: 'Hemodynamically stable',
          dataSource: 'Nursing Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'Blood pressure stable without vasopressor support, heart rate controlled',
          contribution: 'positive'
        },
        {
          id: 'df_ta_recovery_3',
          label: 'Clinical Trajectory',
          importance: 85,
          value: 'Improving, transferred to floor',
          dataSource: 'ICU Discharge Summary',
          timestamp: new Date().toISOString(),
          explanation: 'Significant clinical improvement, no longer requires ICU-level monitoring',
          contribution: 'positive'
        },
        {
          id: 'df_ta_recovery_4',
          label: 'Rehabilitation Needs',
          importance: 82,
          value: 'Deconditioning from ICU stay',
          dataSource: 'PT Consult',
          timestamp: new Date().toISOString(),
          explanation: 'Will benefit from intensive home-based rehabilitation to regain functional capacity',
          contribution: 'positive'
        },
        {
          id: 'df_ta_recovery_5',
          label: 'Home Oxygen',
          importance: 78,
          value: 'Home O2 equipment arranged',
          dataSource: 'DME Order',
          timestamp: new Date().toISOString(),
          explanation: 'Oxygen concentrator and portable tanks ordered for home use',
          contribution: 'positive'
        },
        {
          id: 'df_ta_recovery_6',
          label: 'Family Support',
          importance: 75,
          value: 'Wife available as caregiver',
          dataSource: 'Social Work Notes',
          timestamp: new Date().toISOString(),
          explanation: 'Strong family support, wife able to manage care at home',
          contribution: 'positive'
        }
      ];

      // Create floor transfer transition
      const floorTransferTransition: WorkflowTransition = {
        id: `wh_thomas_floor_${Date.now()}`,
        fromStage: WorkflowStage.DISQUALIFIED,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date().toISOString(),
        userId: 'system',
        userName: 'System',
        note: 'Patient transferred from ICU to Medical Floor. Successfully extubated, hemodynamically stable on 2L O2. Clinical improvement makes patient eligible for SNF at Home with intensive home-based rehabilitation. Score improved from 35 to 82.'
      };

      // Update Thomas Anderson to eligible state
      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== thomasId) return patient;

          return {
            ...patient,
            department: 'Pulmonology',
            recommendedProgram: PredictProgram.SNF_AT_HOME,
            selectedProgram: undefined, // Back at Predict for re-evaluation — no committed selection
            programScores: {
              [PredictProgram.SNF_AT_HOME]: 82,
              [PredictProgram.HOME_HEALTH]: 68,
              [PredictProgram.HOSPITAL_AT_HOME]: 58
            },
            programDecisionFactors: {
              [PredictProgram.SNF_AT_HOME]: recoveryDecisionFactors,
              [PredictProgram.HOME_HEALTH]: [
                {
                  id: 'df_ta_hh_recovery_1',
                  label: 'Respiratory Improvement',
                  importance: 85,
                  value: 'Extubated, stable on 2L O2',
                  dataSource: 'Pulmonology Notes',
                  timestamp: new Date().toISOString(),
                  explanation: 'Significant respiratory improvement allows home-based care with oxygen',
                  contribution: 'positive'
                },
                {
                  id: 'df_ta_hh_recovery_2',
                  label: 'Caregiver Support',
                  importance: 78,
                  value: 'Wife available as primary caregiver',
                  dataSource: 'Social Work Notes',
                  timestamp: new Date().toISOString(),
                  explanation: 'Strong family support enables home health services',
                  contribution: 'positive'
                },
                {
                  id: 'df_ta_hh_recovery_3',
                  label: 'Oxygen Management',
                  importance: 72,
                  value: 'Home oxygen equipment arranged',
                  dataSource: 'DME Order',
                  timestamp: new Date().toISOString(),
                  explanation: 'Home health can manage oxygen therapy and monitoring',
                  contribution: 'positive'
                },
                {
                  id: 'df_ta_hh_recovery_4',
                  label: 'Severe ICU Deconditioning',
                  importance: 88,
                  value: 'Significant functional decline after prolonged ICU stay',
                  dataSource: 'PT Consult',
                  timestamp: new Date().toISOString(),
                  explanation: 'Requires intensive rehabilitation beyond standard home health PT capability',
                  contribution: 'negative'
                },
                {
                  id: 'df_ta_hh_recovery_5',
                  label: 'Rehabilitation Intensity',
                  importance: 82,
                  value: 'Needs multi-disciplinary intensive therapy',
                  dataSource: 'Therapy Evaluation',
                  timestamp: new Date().toISOString(),
                  explanation: 'Standard home health visits insufficient for intensive rehab needs',
                  contribution: 'negative'
                }
              ]
            },
            currentStage: WorkflowStage.TRIAGE,
            disqualificationReason: undefined,
            disqualificationNote: undefined,
            changeType: ChangeType.CLINICAL_CHANGE,
            workflowHistory: [floorTransferTransition, ...patient.workflowHistory],
            lastUpdated: new Date().toISOString()
          };
        }))
      );

      // Trigger notification
      if (onPatientChange) {
        onPatientChange(
          thomasId,
          'Thomas Anderson',
          'reevaluation',
          'Transferred from ICU to Medical Floor. Successfully extubated and stable. Score improved from 35 to 82 for SNF at Home.'
        );
      }
    } else if (scenarioId === 'maria-garcia-respiratory-distress') {
      // Find Maria Garcia (id: '3')
      const mariaId = '3';

      // Decision factors reflecting acute respiratory deterioration
      const deterioratedDecisionFactors: DecisionFactor[] = [
        {
          id: 'df_mg_resp_1',
          label: 'Respiratory Status',
          importance: 98,
          value: 'Acute respiratory distress requiring BiPAP support',
          dataSource: 'Respiratory Therapy',
          timestamp: new Date().toISOString(),
          explanation: 'Sudden onset respiratory failure requiring hospital-level respiratory support',
          contribution: 'negative'
        },
        {
          id: 'df_mg_resp_2',
          label: 'Oxygen Requirements',
          importance: 95,
          value: 'FiO2 60% on BiPAP, saturations 88-90%',
          dataSource: 'Nursing Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'High oxygen requirements beyond what can be managed safely at home',
          contribution: 'negative'
        },
        {
          id: 'df_mg_resp_3',
          label: 'Clinical Stability',
          importance: 92,
          value: 'Hemodynamically unstable, requiring continuous monitoring',
          dataSource: 'ICU Team',
          timestamp: new Date().toISOString(),
          explanation: 'Patient requires ICU-level monitoring and intervention not available in home setting',
          contribution: 'negative'
        },
        {
          id: 'df_mg_resp_4',
          label: 'Mental Status',
          importance: 88,
          value: 'Altered mental status, unable to protect airway',
          dataSource: 'Physician Assessment',
          timestamp: new Date().toISOString(),
          explanation: 'Altered level of consciousness increases aspiration risk and safety concerns',
          contribution: 'negative'
        },
        {
          id: 'df_mg_resp_5',
          label: 'Acuity Level',
          importance: 90,
          value: 'Likely malignant pleural effusion causing respiratory compromise',
          dataSource: 'Radiology Report',
          timestamp: new Date().toISOString(),
          explanation: 'Imaging suggests disease progression with significant pleural effusion requiring hospital intervention',
          contribution: 'negative'
        }
      ];

      // Create workflow transition to disqualified
      const disqualificationTransition: WorkflowTransition = {
        id: `wh_disqual_${Date.now()}`,
        fromStage: WorkflowStage.ACCEPTANCE,
        toStage: WorkflowStage.DISQUALIFIED,
        timestamp: new Date().toISOString(),
        userId: 'system',
        userName: 'System',
        note: 'Acute respiratory decompensation requiring BiPAP and ICU-level monitoring. Patient acuity too high for home-based palliative care. Requires hospital-level care.'
      };

      // Update Maria Garcia to disqualified state
      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== mariaId) return patient;

          return {
            ...patient,
            department: 'ICU',
            locationType: LocationType.INPATIENT,
            primaryDiagnosis: 'Metastatic ovarian cancer with acute respiratory failure',
            currentStage: WorkflowStage.DISQUALIFIED,
            disqualificationReason: DisqualificationReason.ACUITY_TOO_HIGH,
            disqualificationNote: 'Acute respiratory decompensation with hypoxemia requiring BiPAP at FiO2 60%. Altered mental status and hemodynamic instability require ICU-level monitoring. Patient too unstable for home-based care programs.',
            programScores: {
              [PredictProgram.PALLIATIVE]: 25,
              [PredictProgram.HOSPICE]: 20,
              [PredictProgram.HOME_HEALTH]: 15
            },
            programDecisionFactors: {
              [PredictProgram.PALLIATIVE]: deterioratedDecisionFactors.filter((_, i) => i < 4),
              [PredictProgram.HOSPICE]: deterioratedDecisionFactors.filter((_, i) => i < 3),
              [PredictProgram.HOME_HEALTH]: [deterioratedDecisionFactors[0], deterioratedDecisionFactors[1]]
            },
            changeType: ChangeType.CLINICAL_DETERIORATION,
            workflowHistory: [disqualificationTransition, ...patient.workflowHistory],
            lastUpdated: new Date().toISOString()
          };
        }))
      );

      // Trigger notification
      if (onPatientChange) {
        onPatientChange(
          mariaId,
          'Maria Garcia',
          'deterioration',
          'Acute respiratory failure requiring BiPAP support. Transferred to ICU. Patient disqualified from home programs due to high acuity.'
        );
      }
    } else if (scenarioId === 'david-park-hah-upgrade') {
      // David Park: low Hospital at Home score (48) upgraded after clinical improvement → Hospital at Home candidate
      const patientId = '17'; // David Park

      const upgradedHahFactors: DecisionFactor[] = [
        {
          id: 'df_hah_17_upg_stability',
          label: 'Clinical Stability',
          importance: 92,
          value: 'Weaned to 1.5L O2 nasal cannula after 90 min of ED treatment; SpO2 94% at rest, HR 82, RR 18, BP 132/78',
          dataSource: 'ED Nursing Re-assessment',
          timestamp: new Date().toISOString(),
          explanation: 'Response to initial nebulizer therapy and steroids brought the O2 requirement within the Hospital at Home threshold (2L or less at rest, SpO2 at least 92%) and normalized vitals. No ICU indication. A portable O2 concentrator can support the home acute episode.',
          contribution: 'positive'
        },
        {
          id: 'df_hah_17_upg_response',
          label: 'Treatment Response',
          importance: 88,
          value: 'Moderate COPD exacerbation responding within 90 minutes of nebulizers and steroids; no escalation required',
          dataSource: 'ED Physician / Pulmonology',
          timestamp: new Date().toISOString(),
          explanation: 'Attending physician confirms this presentation now meets Hospital at Home clinical eligibility criteria. Patient would otherwise be admitted to the pulmonology floor for 4-5 days; Hospital at Home offers equivalent acute oversight at home.',
          contribution: 'positive'
        },
        {
          id: 'df_hah_17_upg_caregiver',
          label: 'Caregiver Support',
          importance: 80,
          value: 'Hospital at Home eligibility screening triggered — son in home, coverage being confirmed',
          dataSource: 'Hospital at Home Care Coordinator',
          timestamp: new Date().toISOString(),
          explanation: 'The clinical candidacy upgrade triggers formal Hospital at Home eligibility screening. A care coordinator will confirm caregiver availability before enrollment.',
          contribution: 'positive'
        },
        {
          id: 'df_hah_17_upg_home',
          label: 'Home Environment',
          importance: 76,
          value: 'Home environment and geographic eligibility assessment now underway',
          dataSource: 'Hospital at Home Care Coordinator',
          timestamp: new Date().toISOString(),
          explanation: 'Home suitability and service-area eligibility are being assessed as part of the triggered screening workflow.',
          contribution: 'positive'
        },
        {
          id: 'df_hah_17_upg_insurance',
          label: 'Insurance Coverage',
          importance: 72,
          value: 'Medicare Advantage; Hospital at Home acute benefit confirmed, prior auth pending',
          dataSource: 'Case Management',
          timestamp: new Date().toISOString(),
          explanation: 'Benefit is confirmed, leaving prior authorization as the only remaining payer step before enrollment.',
          contribution: 'positive'
        },
        {
          id: 'df_hah_17_upg_readmission',
          label: 'Readmission Risk',
          importance: 70,
          value: 'Two COPD exacerbations in 45 days; escalating frequency on GOLD III disease',
          dataSource: 'Pulmonology Notes',
          timestamp: new Date().toISOString(),
          explanation: 'Escalating exacerbation frequency raises readmission risk; a Hospital at Home episode with daily oversight may interrupt the pattern.',
          contribution: 'negative'
        },
      ];

      const upgradeTransition: WorkflowTransition = {
        id: `wh_hah_dp_${Date.now()}`,
        fromStage: WorkflowStage.TRIAGE,
        toStage: WorkflowStage.TRIAGE,
        timestamp: new Date().toISOString(),
        userId: 'system',
        userName: 'System',
        note: 'Clinical update (90 min post-ED arrival): O2 weaned to 1.5L, vitals normalized, no ICU escalation. Hospital at Home clinical candidacy score upgraded from 48 to 81. Hospital at Home eligibility and social screening now triggered — patient would otherwise be admitted to floor for 4–5 days.'
      };

      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== patientId) return patient;
          return {
            ...patient,
            recommendedProgram: PredictProgram.HOSPITAL_AT_HOME,
            selectedProgram: undefined, // Back at Predict for re-evaluation — no committed selection
            currentStage: WorkflowStage.TRIAGE,
            programScores: {
              ...patient.programScores,
              [PredictProgram.HOSPITAL_AT_HOME]: 81,
              [PredictProgram.HOME_HEALTH]: 42,
              [PredictProgram.SNF_AT_HOME]: 22,
            },
            programDecisionFactors: {
              ...patient.programDecisionFactors,
              [PredictProgram.HOSPITAL_AT_HOME]: upgradedHahFactors,
            },
            changeType: ChangeType.UPGRADED,
            workflowHistory: [upgradeTransition, ...patient.workflowHistory],
            lastUpdated: new Date().toISOString(),
          };
        }))
      );

      if (onPatientChange) {
        onPatientChange(
          patientId,
          'David Park',
          'upgrade',
          'Hospital at Home clinical candidacy confirmed: O2 weaned to 1.5L and vitals normalized after 90 min of ED treatment. Score upgraded from 48 to 81 — Hospital at Home eligibility screening now triggered.'
        );
      }
    }
  };

  // Reset demo scenario to initial state
  const resetDemoScenario = (scenarioId: string): void => {
    if (scenarioId === 'angela-martinez-complication') {
      const robertId = '2';

      // Initial decision factors (good Home Health candidate state)
      const initialDecisionFactors: DecisionFactor[] = [
        {
          id: 'df_hh_1',
          label: 'IV Therapy Requirements',
          importance: 90,
          value: 'PIV line for IV antibiotics',
          dataSource: 'Pharmacy Orders',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health can manage PIV line and IV antibiotic administration',
          contribution: 'positive'
        },
        {
          id: 'df_hh_2',
          label: 'Wound Care',
          importance: 88,
          value: 'Requires daily wound assessment',
          dataSource: 'Nursing Assessment',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          explanation: 'Home health nursing can monitor cellulitis healing and wound care',
          contribution: 'positive'
        },
        {
          id: 'df_hh_3',
          label: 'Caregiver Support',
          importance: 82,
          value: 'Daughter available for assistance',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'Family support available to assist with care at home',
          contribution: 'positive'
        }
      ];

      // Initial workflow history
      const initialWorkflowHistory: WorkflowTransition[] = [
        {
          id: 'wh2',
          fromStage: null,
          toStage: WorkflowStage.TRIAGE,
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago (matches currentLOS: 0.5)
          userId: 'system',
          userName: 'System',
          note: 'Patient identified as Home Health candidate - cellulitis requiring IV antibiotics and wound care'
        }
      ];

      // Reset Angela Martinez to initial state (good Infusion at Home candidate)
      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== robertId) return patient;

          return {
            ...patient,
            locationType: LocationType.ED,
            department: 'Emergency Department',
            recommendedProgram: PredictProgram.HOME_HEALTH,
            selectedProgram: undefined, // Back at Predict for re-evaluation — no committed selection
            programScores: {
              [PredictProgram.HOME_HEALTH]: 85,
              [PredictProgram.INFUSION_AT_HOME]: 76,
              [PredictProgram.SNF_AT_HOME]: 68
            },
            programDecisionFactors: {
              [PredictProgram.HOME_HEALTH]: initialDecisionFactors,
              [PredictProgram.INFUSION_AT_HOME]: [
                {
                  id: 'df_reset_inf1',
                  label: 'IV Antibiotic Administration',
                  importance: 88,
                  value: 'Daily IV antibiotics via PIV line',
                  dataSource: 'Pharmacy Orders',
                  timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Infusion at Home specializes in IV medication delivery and line management',
                  contribution: 'positive'
                },
                {
                  id: 'df_reset_inf2',
                  label: 'Simple Protocol Appropriate',
                  importance: 75,
                  value: 'Straightforward once-daily antibiotic regimen',
                  dataSource: 'Pharmacy Review',
                  timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Uncomplicated IV protocol well-suited for infusion services',
                  contribution: 'positive'
                },
                {
                  id: 'df_reset_inf3',
                  label: 'Comprehensive Wound Care',
                  importance: 70,
                  value: 'Cellulitis requires daily wound assessment and monitoring',
                  dataSource: 'Nursing Assessment',
                  timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Home health provides more comprehensive wound management alongside IV therapy',
                  contribution: 'negative'
                }
              ],
              [PredictProgram.SNF_AT_HOME]: [
                {
                  id: 'df_reset_snf1',
                  label: 'Skilled Nursing Capability',
                  importance: 78,
                  value: 'SNF at Home provides intensive daily nursing care',
                  dataSource: 'Clinical Assessment',
                  timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
                  explanation: 'SNF at Home level of care can manage IV therapy and wound care effectively',
                  contribution: 'positive'
                },
                {
                  id: 'df_reset_snf2',
                  label: 'Rehabilitation Needs',
                  importance: 75,
                  value: 'No significant rehabilitation requirements',
                  dataSource: 'PT Evaluation',
                  timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Patient independently mobile, does not need intensive SNF-level therapy services',
                  contribution: 'negative'
                },
                {
                  id: 'df_reset_snf3',
                  label: 'Care Appropriateness',
                  importance: 72,
                  value: 'Uncomplicated cellulitis case',
                  dataSource: 'Case Management',
                  timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Straightforward IV and wound care can be managed by standard home health',
                  contribution: 'negative'
                }
              ]
            },
            currentStage: WorkflowStage.TRIAGE,
            primaryDiagnosis: 'Cellulitis requiring IV antibiotics',
            workflowHistory: initialWorkflowHistory,
            lastUpdated: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
            assignment: undefined,
            changeType: undefined // Clear changeType so applyUrgentFlags can recalculate (will set to NEW)
          };
        }))
      );
    } else if (scenarioId === 'linda-martinez-upgrade') {
      const lindaId = '1';

      // Initial decision factors (low confidence state)
      const initialDecisionFactors: DecisionFactor[] = [
        {
          id: 'df1_initial',
          label: 'Oxygen Requirements',
          importance: 85,
          value: 'Requiring supplemental O2 at 4L',
          dataSource: 'Respiratory Therapy',
          timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          explanation: 'High oxygen requirements indicate respiratory instability limiting home health',
          contribution: 'negative'
        },
        {
          id: 'df2_initial',
          label: 'Caregiver Support',
          importance: 78,
          value: 'No documented caregiver',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          explanation: 'Lack of caregiver support creates safety concerns for home-based care',
          contribution: 'negative'
        },
        {
          id: 'df3_initial',
          label: 'Medicare Eligibility',
          importance: 65,
          value: 'Age 65+, Medicare eligible',
          dataSource: 'Epic Demographics',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          explanation: 'Medicare covers home health services, making patient eligible for program',
          contribution: 'positive'
        },
        {
          id: 'df4_initial',
          label: 'Clinical Complexity',
          importance: 72,
          value: 'Declining cardiac function with recurrent admissions',
          dataSource: 'Cardiology Notes',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          explanation: 'Complex cardiac history and instability reduce home health appropriateness',
          contribution: 'negative'
        }
      ];

      // Initial workflow history
      const initialWorkflowHistory: WorkflowTransition[] = [
        {
          id: 'wh1',
          fromStage: null,
          toStage: WorkflowStage.TRIAGE,
          timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
          userId: 'system',
          userName: 'System',
          note: 'Patient identified for discharge planning - awaiting clinical updates'
        }
      ];

      // Reset Linda Martinez to initial state
      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== lindaId) return patient;

          return {
            ...patient,
            recommendedProgram: PredictProgram.HOME_HEALTH,
            selectedProgram: undefined, // Back at Predict for re-evaluation — no committed selection
            programScores: {
              [PredictProgram.HOME_HEALTH]: 45,
              [PredictProgram.SNF_AT_HOME]: 38,
              [PredictProgram.HOSPITAL_AT_HOME]: 32
            },
            programDecisionFactors: {
              [PredictProgram.HOME_HEALTH]: initialDecisionFactors.map(df => ({
                ...df,
                id: `${df.id}_hh`
              })),
              [PredictProgram.SNF_AT_HOME]: [
                {
                  id: 'df_reset_linda_snf1',
                  label: 'Clinical Instability',
                  importance: 92,
                  value: 'High O2 requirements (4L) and declining cardiac function',
                  dataSource: 'Nursing Assessment',
                  timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Medical instability precludes safe delivery of intensive home-based rehab services',
                  contribution: 'negative'
                },
                {
                  id: 'df_reset_linda_snf2',
                  label: 'Caregiver Availability',
                  importance: 88,
                  value: 'No caregiver available - lives alone',
                  dataSource: 'Social Work Assessment',
                  timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
                  explanation: 'SNF at Home requires 24/7 caregiver presence for intensive services - critical barrier',
                  contribution: 'negative'
                },
                {
                  id: 'df_reset_linda_snf3',
                  label: 'Rehabilitation Need Assessment',
                  importance: 75,
                  value: 'PT/OT evaluation pending - unclear rehab intensity needs',
                  dataSource: 'Therapy Consult',
                  timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Cannot confirm need for intensive rehab services without completed therapy evaluation',
                  contribution: 'negative'
                }
              ],
              [PredictProgram.HOSPITAL_AT_HOME]: [
                {
                  id: 'df_reset_linda_hah1',
                  label: 'Critical Clinical Instability',
                  importance: 92,
                  value: 'Active CHF exacerbation with O2 dependency and declining EF',
                  dataSource: 'Cardiology Assessment',
                  timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Acute decompensation requires hospital-level monitoring beyond Hospital at Home capability',
                  contribution: 'negative'
                },
                {
                  id: 'df_reset_linda_hah2',
                  label: 'Caregiver Support',
                  importance: 88,
                  value: 'No caregiver available - lives alone',
                  dataSource: 'Social Work Assessment',
                  timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Hospital at Home requires continuous caregiver presence for safety - critical barrier',
                  contribution: 'negative'
                },
                {
                  id: 'df_reset_linda_hah3',
                  label: 'Medical Complexity',
                  importance: 82,
                  value: 'Progressive CHF with declining EF (30%) and recurrent admissions',
                  dataSource: 'Cardiology Notes',
                  timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Disease trajectory and complexity exceed Hospital at Home management capabilities',
                  contribution: 'negative'
                }
              ]
            },
            currentStage: WorkflowStage.TRIAGE,
            changeType: undefined,
            workflowHistory: initialWorkflowHistory,
            lastUpdated: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
          };
        }))
      );
    } else if (scenarioId === 'william-johnson-disqualification') {
      // Reset William Johnson to disqualified state
      const williamId = '4';

      // Initial decision factors (no caregiver available - negative factors for low score)
      const initialDecisionFactors: DecisionFactor[] = [
        {
          id: 'df_hh_wj_1',
          label: 'Caregiver Availability',
          importance: 92,
          value: 'No caregiver during daytime work hours',
          dataSource: 'Social Work Assessment',
          timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          explanation: 'Unsafe for home health without daytime supervision post-surgery',
          contribution: 'negative'
        },
        {
          id: 'df_hh_wj_2',
          label: 'Fall Risk',
          importance: 88,
          value: 'High fall risk post-hip replacement without supervision',
          dataSource: 'PT Evaluation',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          explanation: 'Mobility limitations and fall risk require constant supervision unavailable at home',
          contribution: 'negative'
        }
      ];

      // Initial workflow history
      const initialWorkflowHistory: WorkflowTransition[] = [
        {
          id: 'wh7',
          fromStage: null,
          toStage: WorkflowStage.TRIAGE,
          timestamp: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), // 4 days ago (matches currentLOS: 4)
          userId: 'system',
          userName: 'System',
          note: 'Patient identified for home health'
        },
        {
          id: 'wh8',
          fromStage: WorkflowStage.TRIAGE,
          toStage: WorkflowStage.DISQUALIFIED,
          timestamp: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString(),
          userId: 'user2',
          userName: 'James Thompson',
          note: 'No caregiver available. Patient needs facility-based care.'
        }
      ];

      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== williamId) return patient;

          return {
            ...patient,
            recommendedProgram: PredictProgram.HOME_HEALTH,
            selectedProgram: PredictProgram.HOME_HEALTH,
            programScores: {
              [PredictProgram.HOME_HEALTH]: 42,
              [PredictProgram.SNF_AT_HOME]: 38
            },
            programDecisionFactors: {
              [PredictProgram.HOME_HEALTH]: initialDecisionFactors,
              [PredictProgram.SNF_AT_HOME]: [
                {
                  id: 'df_snf_wj_1',
                  label: 'Caregiver Support',
                  importance: 85,
                  value: 'No caregiver for SNF-level care supervision',
                  dataSource: 'Social Work Assessment',
                  timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
                  explanation: 'SNF at Home requires 24/7 caregiver presence which is not available',
                  contribution: 'negative'
                }
              ]
            },
            currentStage: WorkflowStage.DISQUALIFIED,
            disqualificationReason: DisqualificationReason.NO_CAREGIVER,
            disqualificationNote: 'Patient lives alone, wife works full-time. No caregiver available during daytime hours.',
            changeType: undefined,
            workflowHistory: initialWorkflowHistory,
            lastUpdated: new Date(Date.now() - 18 * 60 * 60 * 1000).toISOString()
          };
        }))
      );
    } else if (scenarioId === 'thomas-anderson-icu-recovery') {
      // Reset Thomas Anderson to ICU/disqualified state
      const thomasId = '6';

      // ICU decision factors (high acuity, not eligible)
      const icuDecisionFactors: DecisionFactor[] = [
        {
          id: 'df_ta_icu_1',
          label: 'Respiratory Status',
          importance: 95,
          value: 'Intubated on mechanical ventilation',
          dataSource: 'ICU Flowsheet',
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          explanation: 'COPD exacerbation progressed to respiratory failure requiring intubation',
          contribution: 'negative'
        },
        {
          id: 'df_ta_icu_2',
          label: 'Acuity Level',
          importance: 92,
          value: 'ICU-level care required',
          dataSource: 'ICU Team',
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          explanation: 'Requires continuous monitoring, unable to protect airway',
          contribution: 'negative'
        },
        {
          id: 'df_ta_icu_3',
          label: 'Hemodynamics',
          importance: 88,
          value: 'Requiring vasopressor support',
          dataSource: 'ICU Nursing',
          timestamp: new Date(Date.now() - 46 * 60 * 60 * 1000).toISOString(),
          explanation: 'Blood pressure support needed, hemodynamically unstable',
          contribution: 'negative'
        },
        {
          id: 'df_ta_icu_4',
          label: 'Prognosis',
          importance: 75,
          value: 'Expected prolonged ICU course',
          dataSource: 'Physician Notes',
          timestamp: new Date(Date.now() - 44 * 60 * 60 * 1000).toISOString(),
          explanation: 'Will likely require extended rehabilitation after ICU discharge',
          contribution: 'negative'
        }
      ];

      // ICU workflow history
      const icuWorkflowHistory: WorkflowTransition[] = [
        {
          id: 'wh_ta_initial',
          fromStage: null,
          toStage: WorkflowStage.TRIAGE,
          timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago (matches currentLOS: 3)
          userId: 'system',
          userName: 'System',
          note: 'Patient identified for home health - COPD exacerbation'
        },
        {
          id: 'wh_ta_icu',
          fromStage: WorkflowStage.TRIAGE,
          toStage: WorkflowStage.DISQUALIFIED,
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          userId: 'system',
          userName: 'System',
          note: 'Patient transferred to ICU due to respiratory failure. Intubated and mechanically ventilated. Too acute for home discharge planning at this time.'
        }
      ];

      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== thomasId) return patient;

          return {
            ...patient,
            department: 'Intensive Care Unit',
            recommendedProgram: PredictProgram.HOME_HEALTH,
            selectedProgram: PredictProgram.HOME_HEALTH,
            programScores: {
              [PredictProgram.HOME_HEALTH]: 35,
              [PredictProgram.SNF_AT_HOME]: 28
            },
            programDecisionFactors: {
              [PredictProgram.HOME_HEALTH]: icuDecisionFactors,
              [PredictProgram.SNF_AT_HOME]: [
                {
                  id: 'df_ta_snf_1',
                  label: 'Critical Care Status',
                  importance: 95,
                  value: 'Currently in ICU with mechanical ventilation',
                  dataSource: 'ICU Team',
                  timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
                  explanation: 'ICU-level care requirements far exceed SNF at Home capabilities',
                  contribution: 'negative'
                },
                {
                  id: 'df_ta_snf_2',
                  label: 'Rehabilitation Feasibility',
                  importance: 85,
                  value: 'Unable to participate in therapy at this time',
                  dataSource: 'PT Consult',
                  timestamp: new Date(Date.now() - 46 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Patient too critically ill for any home-based rehabilitation program',
                  contribution: 'negative'
                }
              ]
            },
            currentStage: WorkflowStage.DISQUALIFIED,
            disqualificationReason: DisqualificationReason.ACUITY_TOO_HIGH,
            disqualificationNote: 'Patient in ICU requiring mechanical ventilation and vasopressor support. ICU-level care required. Not appropriate for home discharge at this time.',
            changeType: undefined,
            workflowHistory: icuWorkflowHistory,
            lastUpdated: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString()
          };
        }))
      );
    } else if (scenarioId === 'maria-garcia-respiratory-distress') {
      // Reset Maria Garcia to original state (in Acceptance for Palliative)
      const mariaId = '3';

      // Original workflow history
      const originalWorkflowHistory: WorkflowTransition[] = [
        {
          id: 'wh4',
          fromStage: null,
          toStage: WorkflowStage.TRIAGE,
          timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          userId: 'system',
          userName: 'System',
          note: 'Patient identified for palliative home care'
        },
        {
          id: 'wh5',
          fromStage: WorkflowStage.TRIAGE,
          toStage: WorkflowStage.ELIGIBILITY,
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
          userId: 'user1',
          userName: 'Sarah Miller',
          note: 'Insurance and eligibility confirmed'
        },
        {
          id: 'wh6',
          fromStage: WorkflowStage.ELIGIBILITY,
          toStage: WorkflowStage.ACCEPTANCE,
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          userId: 'user1',
          userName: 'Sarah Miller',
          note: 'Patient and family education completed. Palliative agency contacted.'
        }
      ];

      // Original decision factors (stable palliative care candidate)
      const originalDecisionFactors: DecisionFactor[] = [
        {
          id: 'df_pal_1',
          label: 'Goals of Care Discussion',
          importance: 95,
          value: 'Comfort focused care',
          dataSource: 'Palliative Care Notes',
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          explanation: 'Family agrees with home-based palliative care plan',
          contribution: 'positive'
        },
        {
          id: 'df_pal_2',
          label: 'Symptom Management',
          importance: 88,
          value: 'Pain well controlled on current medication plan',
          dataSource: 'Epic Flowsheet',
          timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
          explanation: 'Symptoms manageable in home setting with palliative care support',
          contribution: 'positive'
        },
        {
          id: 'df_pal_3',
          label: 'Caregiver Support',
          importance: 82,
          value: 'Daughter lives in home as primary caregiver',
          dataSource: 'Social Work Notes',
          timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
          explanation: 'Strong caregiver presence enables home-based palliative care',
          contribution: 'positive'
        }
      ];

      // Reset Maria Garcia to original stable state
      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== mariaId) return patient;

          return {
            ...patient,
            department: 'Oncology',
            locationType: LocationType.INPATIENT,
            primaryDiagnosis: 'Stage IV metastatic ovarian cancer',
            currentStage: WorkflowStage.ACCEPTANCE,
            disqualificationReason: undefined,
            disqualificationNote: undefined,
            programScores: {
              [PredictProgram.PALLIATIVE]: 85,
              [PredictProgram.HOSPICE]: 68,
              [PredictProgram.HOME_HEALTH]: 55
            },
            programDecisionFactors: {
              [PredictProgram.PALLIATIVE]: originalDecisionFactors,
              [PredictProgram.HOSPICE]: [
                {
                  id: 'df_hos_1',
                  label: 'Prognosis',
                  importance: 85,
                  value: 'Life expectancy weeks to months',
                  dataSource: 'Oncology Notes',
                  timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Disease trajectory suggests hospice may be appropriate soon',
                  contribution: 'positive'
                },
                {
                  id: 'df_hos_2',
                  label: 'Patient/Family Preference',
                  importance: 78,
                  value: 'Patient choosing palliative over hospice at this time',
                  dataSource: 'Family Meeting Notes',
                  timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Family not ready for hospice transition - prefers palliative care currently',
                  contribution: 'negative'
                }
              ],
              [PredictProgram.HOME_HEALTH]: [
                {
                  id: 'df_hh_pal_1',
                  label: 'Care Complexity',
                  importance: 85,
                  value: 'Advanced cancer requiring specialized symptom management',
                  dataSource: 'Oncology Notes',
                  timestamp: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
                  explanation: 'Standard home health insufficient for complex palliative needs',
                  contribution: 'negative'
                }
              ]
            },
            changeType: undefined,
            workflowHistory: originalWorkflowHistory,
            lastUpdated: new Date(Date.now() - 30 * 60 * 1000).toISOString()
          };
        }))
      );
    } else if (scenarioId === 'david-park-hah-upgrade') {
      // Reset David Park to original state
      const patientId = '17';
      setAllPatients(prevPatients =>
        applyUrgentFlags(prevPatients.map(patient => {
          if (patient.id !== patientId) return patient;
          const original = HAH_ONLY_PATIENTS.find(p => p.id === patientId);
          return original ? { ...original } : patient;
        }))
      );
    }
  };

  // Initialize eligibility checklist for a patient
  const initializeChecklist = (patientId: string): void => {
    const patient = getPatientById(patientId);
    if (!patient || patient.eligibilityChecklist) return;

    if (!userEmail) return;

    const userName = userEmail.split('@')[0].replace('.', ' ').split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

    // Get the checklist template for the patient's selected program
    const template = PROGRAM_ELIGIBILITY_CHECKLISTS[patient.selectedProgram ?? patient.recommendedProgram];

    // Auto-populate answers based on patient data
    const sectionsWithAutoPopulated = template.sections.map(section => ({
      ...section,
      items: section.items.map(item => {
        let autoAnswer: string | undefined;
        let shouldAutoPopulate = false;

        // Auto-populate age requirement for SNF at Home
        if (item.id === 'snf-age-requirement') {
          autoAnswer = patient.age >= 18 ? 'YES' : 'NO';
          shouldAutoPopulate = true;
        }

        // Auto-populate LOS requirement for SNF at Home
        if (item.id === 'snf-los-requirement') {
          autoAnswer = patient.expectedLOS <= 30 ? 'YES' : 'NO';
          shouldAutoPopulate = true;
        }

        if (shouldAutoPopulate && autoAnswer) {
          return {
            ...item,
            answer: autoAnswer,
            autoPopulated: true,
            lastUpdated: new Date().toISOString(),
            updatedBy: 'System'
          };
        }

        return item;
      })
    }));

    const newChecklist: EligibilityChecklist = {
      ...template,
      sections: sectionsWithAutoPopulated,
      completionPercentage: 0,
      lastUpdated: new Date().toISOString(),
      updatedBy: userName
    };

    updatePatient(patientId, { eligibilityChecklist: newChecklist });
  };

  // Update a single checklist answer
  const updateChecklistAnswer = (
    patientId: string,
    itemId: string,
    answer: string | string[] | null
  ): void => {
    const patient = getPatientById(patientId);
    if (!patient || !patient.eligibilityChecklist) return;

    if (!userEmail) return;

    const userName = userEmail.split('@')[0].replace('.', ' ').split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

    // Update the answer for the specified item
    const updatedSections = patient.eligibilityChecklist.sections.map(section => ({
      ...section,
      items: section.items.map(item =>
        item.id === itemId
          ? {
              ...item,
              answer,
              autoPopulated: false, // Clear auto-populated flag when manually changed
              lastUpdated: new Date().toISOString(),
              updatedBy: userName
            }
          : item
      )
    }));

    const updatedChecklist: EligibilityChecklist = {
      ...patient.eligibilityChecklist,
      sections: updatedSections,
      lastUpdated: new Date().toISOString(),
      updatedBy: userName
    };

    // Recalculate completion percentage
    updatedChecklist.completionPercentage = calculateCompletionPercentage(updatedChecklist);

    updatePatient(patientId, { eligibilityChecklist: updatedChecklist });
  };

  // Switch demo user (for testing different client access levels)
  const switchDemoUser = (user: PredictUser): void => {
    setCurrentUser(user);
  };

  // Select or change the program for a patient (at Triage stage)
  const selectProgram = (patientId: string, program: PredictProgram): void => {
    const patient = getPatientById(patientId);
    if (!patient) return;

    updatePatient(patientId, {
      selectedProgram: program
    });
  };

  // Change program and map eligibility checklist if one exists
  const changeProgram = (patientId: string, newProgram: PredictProgram): void => {
    const patient = getPatientById(patientId);
    if (!patient) return;

    const updates: Partial<PredictPatient> = {
      selectedProgram: newProgram
    };

    // If patient has an eligibility checklist, map answers to new program's checklist
    if (patient.eligibilityChecklist && patient.eligibilityChecklist.program !== newProgram) {
      if (!userEmail) return;

      const userName = userEmail.split('@')[0].replace('.', ' ').split(' ')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

      // Get the old and new checklist templates
      const oldChecklist = patient.eligibilityChecklist;
      const newTemplate = PROGRAM_ELIGIBILITY_CHECKLISTS[newProgram];

      // Map answers from old checklist to new checklist where questions match
      const newSections = newTemplate.sections.map(newSection => {
        const newItems = newSection.items.map(newItem => {
          // Try to find a matching question in the old checklist
          for (const oldSection of oldChecklist.sections) {
            const matchingItem = oldSection.items.find(
              oldItem => oldItem.question === newItem.question && oldItem.questionType === newItem.questionType
            );
            if (matchingItem && matchingItem.answer !== undefined && matchingItem.answer !== null) {
              // Found a match - carry over the answer
              return {
                ...newItem,
                answer: matchingItem.answer,
                lastUpdated: matchingItem.lastUpdated,
                updatedBy: matchingItem.updatedBy
              };
            }
          }
          // No match found - return item without answer
          return newItem;
        });

        return {
          ...newSection,
          items: newItems
        };
      });

      const newChecklist: EligibilityChecklist = {
        ...newTemplate,
        sections: newSections,
        lastUpdated: new Date().toISOString(),
        updatedBy: userName,
        completionPercentage: 0
      };

      // Recalculate completion percentage
      newChecklist.completionPercentage = calculateCompletionPercentage(newChecklist);

      updates.eligibilityChecklist = newChecklist;
    }

    updatePatient(patientId, updates);
  };

  // Check if patient is ready to advance from current stage
  const isReadyToAdvance = (patientId: string): boolean => {
    const patient = getPatientById(patientId);
    if (!patient) return false;

    switch (patient.currentStage) {
      case WorkflowStage.TRIAGE:
        // Ready if a program has been selected and its score >= 75
        if (!patient.selectedProgram) return false;
        const score = patient.programScores[patient.selectedProgram];
        return score !== undefined && score >= 75;

      case WorkflowStage.ELIGIBILITY:
        // Ready if checklist is 100% complete
        return patient.eligibilityChecklist?.completionPercentage === 100;

      case WorkflowStage.ACCEPTANCE:
        // Ready if user has taken ownership AND provider approved AND patient accepted
        const hasOwnership = patient.assignment?.userId !== null && patient.assignment?.userId !== undefined;
        const hasProviderApproval = patient.providerApproval?.approved === true;
        const hasPatientAcceptance = patient.patientAcceptance?.accepted === true;
        return hasOwnership && hasProviderApproval && hasPatientAcceptance;

      case WorkflowStage.TRANSITION:
        // Ready only if patient has been discharged
        return patient.discharged?.completed ?? false;

      case WorkflowStage.DISQUALIFIED:
        // Not ready (terminal state)
        return false;

      default:
        return false;
    }
  };

  // Get detailed readiness information for current stage
  const getReadinessDetails = (patientId: string): StageReadinessDetails => {
    const patient = getPatientById(patientId);
    if (!patient) {
      return {
        stage: WorkflowStage.TRIAGE,
        isReady: false,
        overallPercentage: 0,
        requirements: [],
        nextStageLabel: 'Next Stage',
      };
    }

    switch (patient.currentStage) {
      case WorkflowStage.TRIAGE:
        return getTriageReadiness(patient);
      case WorkflowStage.ELIGIBILITY:
        return getEligibilityReadiness(patient);
      case WorkflowStage.ACCEPTANCE:
        return getAcceptanceReadiness(patient);
      case WorkflowStage.TRANSITION:
        return getTransitionReadiness(patient);
      case WorkflowStage.DISQUALIFIED:
        return getDisqualifiedReadiness(patient);
      default:
        return {
          stage: patient.currentStage,
          isReady: false,
          overallPercentage: 0,
          requirements: [],
          nextStageLabel: 'Next Stage',
        };
    }
  };

  // Helper function to get program label
  const getProgramLabel = (program: PredictProgram): string => {
    const labels: Record<PredictProgram, string> = {
      [PredictProgram.HOME_HEALTH]: 'Home Health',
      [PredictProgram.HOSPITAL_AT_HOME]: 'Hospital at Home', // Hidden in UI but needed for type completeness
      [PredictProgram.SNF_AT_HOME]: 'SNF at Home',
      [PredictProgram.HOSPICE]: 'Hospice',
      [PredictProgram.INFUSION_AT_HOME]: 'Infusion at Home',
      [PredictProgram.PALLIATIVE]: 'Palliative Care',
    };
    return labels[program];
  };

  // Stage-specific readiness helper functions
  const getTriageReadiness = (patient: PredictPatient): StageReadinessDetails => {
    const requirements: ReadinessRequirement[] = [];
    let metCount = 0;

    // Requirement 1: Program Selected
    const programSelected = patient.selectedProgram !== null && patient.selectedProgram !== undefined;
    requirements.push({
      id: 'program-selected',
      label: 'Program Selected',
      description: 'A discharge program must be selected',
      isMet: programSelected,
      value: patient.selectedProgram ? getProgramLabel(patient.selectedProgram) : 'Not selected',
      category: 'critical',
    });
    if (programSelected) metCount++;

    // Requirement 2: Disposition Score >= 75
    const score = patient.selectedProgram ? patient.programScores[patient.selectedProgram] : undefined;
    const scoreThreshold = 75;
    const scoreMet = score !== undefined && score >= scoreThreshold;
    requirements.push({
      id: 'program-score',
      label: 'Program Fit Score',
      description: 'Program fit score should be at least 75',
      isMet: scoreMet,
      value: score !== undefined ? score : 'N/A',
      targetValue: scoreThreshold,
      category: 'critical',
    });
    if (scoreMet) metCount++;

    const isReady = metCount === requirements.length;
    const overallPercentage = Math.round((metCount / requirements.length) * 100);

    return {
      stage: WorkflowStage.TRIAGE,
      isReady,
      overallPercentage,
      requirements,
      nextStageLabel: 'Eligibility',
      blockerMessage: isReady
        ? `Ready to advance to Eligibility stage`
        : !programSelected
        ? 'Select a discharge program to proceed'
        : 'Program fit score should be at least 75 to proceed',
    };
  };

  // Helper function to evaluate if an answer is satisfactory (not disqualifying)
  const evaluateAnswer = (item: EligibilityChecklistItem): { isSatisfactory: boolean; issue?: string } => {
    // If no answer, can't evaluate
    if (item.answer === undefined || item.answer === null || item.answer === '') {
      return { isSatisfactory: false, issue: 'No answer provided' };
    }

    // Check for empty array (multi-select with no selections)
    if (Array.isArray(item.answer) && item.answer.length === 0) {
      return { isSatisfactory: false, issue: 'No answer provided' };
    }

    // If no evaluation criteria, assume answer is satisfactory
    if (!item.evaluationCriteria) {
      return { isSatisfactory: true };
    }

    const { disqualifyingAnswers, acceptableAnswers, idealAnswers } = item.evaluationCriteria;
    const answer = item.answer;

    // Check if answer is disqualifying (case-insensitive)
    if (disqualifyingAnswers && disqualifyingAnswers.length > 0) {
      const disqualifyingLower = disqualifyingAnswers.map(a => a.toLowerCase());
      if (Array.isArray(answer)) {
        // For multi-select, check if any selected answer is disqualifying
        const hasDisqualifying = answer.some(a => disqualifyingLower.includes(a.toLowerCase()));
        if (hasDisqualifying) {
          return { isSatisfactory: false, issue: 'Answer disqualifies patient' };
        }
      } else {
        if (disqualifyingLower.includes((answer as string).toLowerCase())) {
          return { isSatisfactory: false, issue: 'Answer disqualifies patient' };
        }
      }
    }

    // If we have acceptable or ideal answers defined, check if answer matches (case-insensitive)
    const validAnswers = [...(acceptableAnswers || []), ...(idealAnswers || [])];
    if (validAnswers.length > 0) {
      const validAnswersLower = validAnswers.map(a => a.toLowerCase());
      if (Array.isArray(answer)) {
        // For multi-select, all answers must be in valid list
        const allValid = answer.every(a => validAnswersLower.includes(a.toLowerCase()));
        if (!allValid) {
          return { isSatisfactory: false, issue: 'Answer not in acceptable range' };
        }
      } else {
        if (!validAnswersLower.includes((answer as string).toLowerCase())) {
          return { isSatisfactory: false, issue: 'Answer not in acceptable range' };
        }
      }
    }

    return { isSatisfactory: true };
  };

  const getEligibilityReadiness = (patient: PredictPatient): StageReadinessDetails => {
    const requirements: ReadinessRequirement[] = [];
    let metCount = 0;

    // Requirement 1: Checklist Initialized
    const checklistInitialized = patient.eligibilityChecklist !== undefined;
    requirements.push({
      id: 'checklist-initialized',
      label: 'Checklist Initialized',
      description: 'Eligibility checklist must be initialized',
      isMet: checklistInitialized,
      value: checklistInitialized ? 'Initialized' : 'Not initialized',
      category: 'critical',
    });
    if (checklistInitialized) metCount++;

    // Requirement 2: Checklist Complete
    const completionPercentage = patient.eligibilityChecklist?.completionPercentage || 0;
    const checklistComplete = completionPercentage === 100;

    // Count total items and answered items
    let totalItems = 0;
    let answeredItems = 0;
    if (patient.eligibilityChecklist) {
      patient.eligibilityChecklist.sections.forEach(section => {
        section.items.forEach(item => {
          if (item.required) {
            totalItems++;
            if (item.answer !== undefined && item.answer !== null && item.answer !== '') {
              answeredItems++;
            }
          }
        });
      });
    }

    requirements.push({
      id: 'checklist-complete',
      label: 'All Questions Answered',
      description: 'All required checklist items must be completed',
      isMet: checklistComplete,
      value: checklistInitialized ? `${answeredItems} / ${totalItems}` : 'N/A',
      category: 'critical',
    });
    if (checklistComplete) metCount++;

    // Requirement 3: Answers Satisfactory (no disqualifying answers)
    let satisfactoryCount = 0;
    let unsatisfactoryCount = 0;
    let disqualifyingIssues: string[] = [];
    const problematicQuestions: Array<{ question: string; issue: string; answer: string | string[] }> = [];

    // Evaluate all answered questions, regardless of checklist completion
    if (patient.eligibilityChecklist) {
      patient.eligibilityChecklist.sections.forEach(section => {
        section.items.forEach(item => {
          if (item.required) {
            const evaluation = evaluateAnswer(item);
            if (evaluation.isSatisfactory) {
              satisfactoryCount++;
            } else {
              unsatisfactoryCount++;
              if (evaluation.issue === 'Answer disqualifies patient') {
                disqualifyingIssues.push(item.question);
              }
              // Track problematic questions for detailed requirements
              // Only add if question has been answered (not just "No answer provided")
              if (evaluation.issue !== 'No answer provided') {
                problematicQuestions.push({
                  question: item.question,
                  issue: evaluation.issue || 'Does not meet criteria',
                  answer: item.answer || 'No answer'
                });
              }
            }
          }
        });
      });
    }

    const allAnswersSatisfactory = checklistComplete && unsatisfactoryCount === 0;

    // Create subRequirements for problematic questions
    const subRequirements: ReadinessRequirement[] = [];
    if (problematicQuestions.length > 0) {
      problematicQuestions.forEach((pq, index) => {
        subRequirements.push({
          id: `issue-${index}`,
          label: pq.question,
          description: pq.issue,
          isMet: false,
          value: Array.isArray(pq.answer) ? pq.answer.join(', ') : String(pq.answer),
          category: pq.issue === 'Answer disqualifies patient' ? 'critical' : 'required',
        });
      });
    }

    requirements.push({
      id: 'answers-satisfactory',
      label: 'Answers Satisfactory',
      description: 'All answers must meet eligibility criteria (no disqualifying answers)',
      isMet: allAnswersSatisfactory,
      value: checklistInitialized ? `${satisfactoryCount} / ${totalItems} acceptable` : 'N/A',
      category: 'critical',
      subRequirements: subRequirements.length > 0 ? subRequirements : undefined,
    });
    if (allAnswersSatisfactory) metCount++;

    const isReady = metCount === requirements.length;

    // Calculate overall percentage based on completion and answer quality
    let overallPercentage = 0;
    if (checklistInitialized) {
      const completionWeight = 0.5;
      const qualityWeight = 0.5;
      const qualityPercentage = totalItems > 0 ? (satisfactoryCount / totalItems) * 100 : 0;
      overallPercentage = Math.round((completionPercentage * completionWeight) + (qualityPercentage * qualityWeight));
    }

    // Generate blocker message
    let blockerMessage = '';
    if (isReady) {
      blockerMessage = 'Ready to advance to Acceptance stage';
    } else if (!checklistInitialized) {
      blockerMessage = 'Checklist is being initialized';
    } else if (!checklistComplete) {
      blockerMessage = `Complete all required questions (${answeredItems}/${totalItems} answered)`;
    } else if (disqualifyingIssues.length > 0) {
      blockerMessage = `Patient has ${disqualifyingIssues.length} disqualifying answer${disqualifyingIssues.length > 1 ? 's' : ''} - see details below`;
    } else if (unsatisfactoryCount > 0) {
      blockerMessage = `${unsatisfactoryCount} answer${unsatisfactoryCount > 1 ? 's do' : ' does'} not meet criteria - see details below`;
    } else {
      blockerMessage = 'Unable to determine readiness';
    }

    return {
      stage: WorkflowStage.ELIGIBILITY,
      isReady,
      overallPercentage,
      requirements,
      nextStageLabel: 'Acceptance',
      blockerMessage,
    };
  };

  const getAcceptanceReadiness = (patient: PredictPatient): StageReadinessDetails => {
    const requirements: ReadinessRequirement[] = [];
    let metCount = 0;

    // Requirement 1: Ownership Assigned
    const ownershipAssigned = patient.assignment?.userId !== null && patient.assignment?.userId !== undefined;
    requirements.push({
      id: 'ownership-assigned',
      label: 'Ownership Assigned',
      description: 'Case must be assigned to a care coordinator',
      isMet: ownershipAssigned,
      value: ownershipAssigned ? patient.assignment?.userName || 'Assigned' : 'Unassigned',
      category: 'critical',
    });
    if (ownershipAssigned) metCount++;

    // Requirement 2: Provider/Hospitalist Approval
    const providerApproved = patient.providerApproval?.approved === true;
    requirements.push({
      id: 'provider-approval',
      label: 'Provider Approval',
      description: 'Hospitalist or attending physician must approve the discharge program',
      isMet: providerApproved,
      value: providerApproved
        ? `Approved by ${patient.providerApproval?.providerName || 'Provider'}`
        : 'Pending approval',
      category: 'critical',
    });
    if (providerApproved) metCount++;

    // Requirement 3: Patient Acceptance
    const patientAccepted = patient.patientAcceptance?.accepted === true;
    requirements.push({
      id: 'patient-acceptance',
      label: 'Patient Acceptance',
      description: 'Patient (or family/caregiver) must accept the discharge plan',
      isMet: patientAccepted,
      value: patientAccepted
        ? 'Patient/family accepted'
        : 'Patient acceptance pending',
      category: 'critical',
    });
    if (patientAccepted) metCount++;

    const isReady = metCount === requirements.length;
    const overallPercentage = Math.round((metCount / requirements.length) * 100);

    // Generate blocker message
    let blockerMessage = '';
    if (isReady) {
      blockerMessage = 'Ready to advance to Transition stage';
    } else if (!ownershipAssigned) {
      blockerMessage = 'Assign case to a care coordinator to proceed';
    } else if (!providerApproved) {
      blockerMessage = 'Awaiting provider approval of discharge program';
    } else if (!patientAccepted) {
      blockerMessage = 'Awaiting patient/family acceptance of discharge plan';
    } else {
      blockerMessage = 'Complete all requirements to proceed';
    }

    return {
      stage: WorkflowStage.ACCEPTANCE,
      isReady,
      overallPercentage,
      requirements,
      nextStageLabel: 'Transition',
      blockerMessage,
    };
  };

  const getTransitionReadiness = (patient: PredictPatient): StageReadinessDetails => {
    const requirements: ReadinessRequirement[] = [];

    // Requirement 1: Patient Discharged
    const isDischarged = patient.discharged?.completed ?? false;
    requirements.push({
      id: 'patient-discharged',
      label: 'Patient Discharged',
      description: 'Patient has been discharged from the facility',
      isMet: isDischarged,
      value: isDischarged ? 'Discharged' : 'Pending',
      category: 'critical',
    });

    const metCount = requirements.filter(r => r.isMet).length;
    const totalCount = requirements.length;
    const percentage = Math.round((metCount / totalCount) * 100);
    const isReady = metCount === totalCount;

    return {
      stage: WorkflowStage.TRANSITION,
      isReady,
      overallPercentage: percentage,
      requirements,
      nextStageLabel: 'Complete',
      blockerMessage: isReady ? 'Patient discharge is complete. Case will be closed.' : 'Patient must be discharged to complete the case',
    };
  };

  const getDisqualifiedReadiness = (patient: PredictPatient): StageReadinessDetails => {
    return {
      stage: WorkflowStage.DISQUALIFIED,
      isReady: false,
      overallPercentage: 0,
      requirements: [],
      nextStageLabel: 'N/A',
      blockerMessage: 'Patient has been disqualified from the discharge workflow',
    };
  };

  // Set provider approval for a patient
  const setProviderApproval = (patientId: string, approved: boolean, providerName?: string): void => {
    updatePatient(patientId, {
      providerApproval: {
        approved,
        providerName,
        approvedAt: approved ? new Date().toISOString() : undefined,
      },
    });
  };

  // Set patient acceptance for a patient
  const setPatientAcceptance = (patientId: string, accepted: boolean): void => {
    if (!userEmail) return;

    const userName = userEmail.split('@')[0].replace('.', ' ').split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

    updatePatient(patientId, {
      patientAcceptance: {
        accepted,
        acceptedAt: accepted ? new Date().toISOString() : undefined,
        acceptedBy: accepted ? userName : undefined,
      },
    });
  };

  // Set patient discharge status
  const setPatientDischarged = (patientId: string, discharged: boolean): void => {
    if (!userEmail) return;

    const userName = userEmail.split('@')[0].replace('.', ' ').split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

    updatePatient(patientId, {
      discharged: {
        completed: discharged,
        dischargedAt: discharged ? new Date().toISOString() : undefined,
        dischargedBy: discharged ? userName : undefined,
      },
    });
  };

  const markPatientViewed = (patientId: string): void => {
    setAllPatients(prevPatients =>
      applyUrgentFlags(prevPatients.map(patient =>
        patient.id === patientId && !patient.viewedAt
          ? { ...patient, viewedAt: new Date().toISOString(), changeType: patient.changeType === ChangeType.NEW ? undefined : patient.changeType }
          : patient
      ))
    );
  };

  const markPatientScheduled = (patientId: string): void => {
    setAllPatients(prevPatients =>
      applyUrgentFlags(prevPatients.map(patient =>
        patient.id === patientId && !patient.scheduledAt
          ? { ...patient, scheduledAt: new Date().toISOString() }
          : patient
      ))
    );
  };

  const contextValue: PredictContextType = {
    patients,
    loading: false,
    currentUser,
    getPatientById,
    takeOwnership,
    releaseOwnership,
    assignOwnership,
    transitionWorkflow,
    updatePatient,
    updateChecklistAnswer,
    initializeChecklist,
    switchDemoUser,
    triggerDemoScenario,
    resetDemoScenario,
    selectProgram,
    changeProgram,
    isReadyToAdvance,
    getReadinessDetails,
    setProviderApproval,
    setPatientAcceptance,
    setPatientDischarged,
    markPatientViewed,
    markPatientScheduled,
    demoMode,
    setDemoMode,
    visiblePrograms,
  };

  return (
    <PredictContext.Provider value={contextValue}>
      {children}
    </PredictContext.Provider>
  );
};
